import { gapi } from 'gapi-script';
import { toast } from 'react-toastify';
import { motion } from 'framer-motion';
import LogoFull from '../assets/logo.png';
import Gemini from '../assets/gemini.png';
import ReactMarkdown from 'react-markdown';
import Logo from '../assets/logo_no_title.png';
import { Doughnut, Line } from 'react-chartjs-2';
import { fetchGoogleCalendar } from './google/calendar';
import { fetchCalendarEvents } from './outlook/calendar';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { auth, firestore, realtime } from '../firebase-config';
import { useHistory } from 'react-router-dom/cjs/react-router-dom';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { handleGoogleLogin, initGoogleClient } from './google/config';
import { get, onValue, push, ref, set, update } from 'firebase/database';
import { handleLogin, microsoftAuthService, msalInstance } from './outlook/config';
import { deleteGmailEmail, fetchGmailEmails, sendGmailReply } from './google/gmail';
import React, { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import { geminiChat, getTaskExplanation, initiateRazorpayPayment } from './functions';
import { addTask, deleteTask, updateTaskDetails, updateTaskStatus, useTasks } from './tasks';
import { deleteEmail, fetchEmails, getMicrosoftAttachmentContent, sendMicrosoftReply } from './outlook/emails';
import { Chart as ChartJS, LineElement, CategoryScale, LinearScale, PointElement, ArcElement, Tooltip } from 'chart.js';
import { addDoc, collection, doc, getDoc, getDocs, limit, onSnapshot, orderBy, query, updateDoc, where } from 'firebase/firestore';
import { AppMode, authManagerInstance, AuthProvider, AuthStatus, availableWidgets, circleOptions, dark_mode_primary, dark_mode_secondary, data, getCircleData, light_mode_primary, light_mode_secondary, options, PaymentTiers, Priority, ProviderAuthStatus, TaskStatus } from './values';

ChartJS.register(LineElement, CategoryScale, LinearScale, PointElement, ArcElement, Tooltip);

export function DesktopOnlyMessage() {
    return (
        <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 px-4">
            <div className="bg-white rounded-xl shadow-md p-8 max-w-xs w-full">
                <div className="flex justify-center mb-4">
                    <svg className="w-12 h-12 text-blue-500" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3 5.75A2.75 2.75 0 015.75 3h12.5A2.75 2.75 0 0121 5.75v8.5A2.75 2.75 0 0118.25 17H5.75A2.75 2.75 0 013 14.25v-8.5z" />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M8 21h8m-4-4v4" />
                    </svg>
                </div>
                <h2 className="text-xl font-semibold text-gray-800 mb-2 text-center">Desktop Required</h2>
                <p className="text-gray-600 text-center">This dashboard is only available on desktop.<br />Please open this app on a PC to continue.</p>
            </div>
        </div>
    );
}

export const VerticalTabs = ({ tabs, appMode, activeTab, setActiveTab }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const handleClick = (index) => setActiveTab(index);
    const handleMouseEnter = () => setIsExpanded(true);
    const handleMouseLeave = () => setIsExpanded(false);
    return (
        <div className="flex h-screen">
            <div className={`h-full text-[#8E92BC] flex flex-col justify-between transition-all duration-500 ease-in-out ${appMode === AppMode.LIGHT ? 'border-r border-gray-200' : dark_mode_secondary + ' border-r border-gray-800'} ${isExpanded ? 'w-1/6' : 'w-16'}`} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
                <div className={`px-3.5 ${isExpanded ? '' : 'py-5'} items-center`}>
                    {isExpanded
                        ? <img src={LogoFull} className="p-5" alt='' />
                        : <img src={Logo} className="w-6/7" alt='' />
                    }
                </div>
                <div className={`flex-1 overflow-y-auto items-center ${isExpanded ? 'px-3' : 'px-1'}`}>
                    {tabs.map((tab, index) => (
                        <div key={index} className={`flex items-center p-3 cursor-pointer transition-colors rounded-lg ${index === activeTab ? appMode === AppMode.LIGHT ? `bg-[#EAEAEC] text-black` : `bg-[#2A2D3E] text-white` : appMode === AppMode.LIGHT ? 'text-[#8E92BC] hover:bg-[#F5F5F7] hover:text-black' : 'text-[#8E92BC] hover:bg-[#2A2D3E] hover:text-white'}`} onClick={() => handleClick(index)}>
                            <span className={`${index === activeTab ? 'text-current' : ''}`}>{tab.icon}</span>
                            {isExpanded && <span className="ml-3">{tab.label}</span>}
                        </div>
                    ))}
                </div>
            </div>
            <div className={`flex-grow ${activeTab === 6 ? 'overflow-y-auto' : 'overflow-y-hidden'}`}>{tabs[activeTab].content}</div>
        </div>
    );
};

export function HeroSection({ image, title, description, isButtonAvailable }) {
    const history = useHistory();
    const goToContact = () => {
        history.push('/contact');
    }
    return (
        <div className="relative w-full h-[20rem] sm:h-[25rem] md:h-[35rem] overflow-hidden">
            <motion.div initial={{ scale: 1.1, opacity: 0.8 }} whileInView={{ scale: 1, opacity: 1 }} transition={{ duration: 0.75, ease: "easeOut" }} className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${image})` }}></motion.div>
            <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} transition={{ duration: 0.8, delay: 0.2 }} className="absolute inset-0 bg-gradient-to-r from-[#155799]/65 to-[#159957]/65"></motion.div>
            <div className="relative z-10 flex items-center justify-center h-full px-4">
                <div className="w-4/5 text-center">
                    <motion.h1 initial={{ opacity: 0, y: -30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: "easeOut" }} className="mt-2 text-2xl sm:text-4xl md:text-6xl font-bold text-white">{title}</motion.h1>
                    <motion.p initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.2, ease: "easeOut" }} className="mt-2 sm:mt-3 text-lg sm:text-md font-medium text-gray-200">{description}</motion.p>
                    {isButtonAvailable
                        ? <motion.button onClick={goToContact} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.4 }} whileHover={{ scale: 1.05, boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)" }} whileTap={{ scale: 0.95 }} className="mt-4 sm:mt-7 text-teal-700 bg-white border-0 py-2 px-8 sm:px-12 focus:outline-none rounded text-base sm:text-lg">Reach Out to Us</motion.button>
                        : <></>
                    }
                </div>
            </div>
        </div>
    )
}

export const ProgressBar = ({ progress }) => {
    const safeProgress = Math.min(Math.max(0, Number(progress) || 0), 100);
    return (
        <div className="relative w-full h-2 bg-gray-200 rounded-full">
            <div className="absolute h-full bg-blue-500 rounded-full transition-all duration-500" style={{ width: `${safeProgress}%` }}></div>
            <div className="absolute -top-1.5 bg-blue-500 h-5 w-5 rounded-full transition-all duration-500" style={{ left: `${safeProgress}%`, transform: 'translateX(-75%)' }}></div>
        </div>
    );
};

export const ProfileOptionButton = ({ icon }) => {
    return (
        <button className='h-12 w-12 border border-gray-200 shadow rounded-full items-center justify-center'>
            <i className={`${icon} text-[#8E92BC]`}></i>
        </button>
    )
}

export function RunningTasks({ appMode }) {
    const [teams, setTeams] = useState([]);
    const [teamTasks, setTeamTasks] = useState([]);
    const [percentage, setPercentage] = useState(0);
    const [totalTasks, setTotalTasks] = useState(0);
    const [personalTasks, setPersonalTasks] = useState([]);
    const [runningTasksCount, setRunningTasksCount] = useState(0);
    const updateTaskStats = useCallback(() => {
        const allTasks = [...personalTasks, ...teamTasks];
        const runningTasks = allTasks.filter(task =>
            task.status === TaskStatus.TODO ||
            task.status === TaskStatus.IN_PROGRESS
        );
        setRunningTasksCount(runningTasks.length);
        setTotalTasks(allTasks.length);
        if (allTasks.length > 0) {
            const calculatedPercentage = Math.round(((allTasks.length - runningTasks.length) / allTasks.length) * 100);
            setPercentage(calculatedPercentage);
        } else {
            setPercentage(0);
        }
    }, [personalTasks, teamTasks]);

    useEffect(() => {
        updateTaskStats();
    }, [updateTaskStats]);

    useEffect(() => {
        if (!auth.currentUser) return;
        const userTeamsRef = ref(realtime, 'teams');
        const unsubscribe = onValue(userTeamsRef, (snapshot) => {
            const teamsData = snapshot.val() || {};
            const teamsList = Object.entries(teamsData).map(([id, data]) => {
                return { id, ...data };
            }).filter(team => {
                const isTeamMember = team.members && team.members.includes(auth.currentUser.uid);
                return isTeamMember;
            });
            setTeams(teamsList);
        });

        return () => unsubscribe();
    }, []);

    useEffect(() => {
        if (!auth.currentUser) return;
        const userTasksQuery = query(
            collection(firestore, 'tasks'),
            where("assignedTo", "==", auth.currentUser.uid)
        );
        const unsubscribe = onSnapshot(userTasksQuery, (snapshot) => {
            const tasksData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setPersonalTasks(tasksData);
        });
        return () => unsubscribe();
    }, []);

    useEffect(() => {
        if (!auth.currentUser || teams.length === 0) return;
        const unsubscribeArray = [];
        teams.forEach(team => {
            const tasksRef = ref(realtime, `teams/${team.id}/tasks`);
            const unsubscribe = onValue(tasksRef, (snapshot) => {
                const tasksData = snapshot.val() || {};
                const teamTasks = Object.entries(tasksData).map(([id, data]) => ({
                    id,
                    teamId: team.id,
                    ...data
                })).filter(task => task.assignedTo === auth.currentUser.uid);
                setTeamTasks(prevTasks => {
                    const filteredTasks = prevTasks.filter(task =>
                        task.teamId !== team.id
                    );
                    return [...filteredTasks, ...teamTasks];
                });
            });
            unsubscribeArray.push(unsubscribe);
        });
        return () => {
            unsubscribeArray.forEach(unsubscribe => unsubscribe());
        };
    }, [teams]);

    return (
        <div className="p-3 w-1/3 h-64">
            <div className={`h-full ${appMode === AppMode.LIGHT ? 'bg-[#141522]' : 'bg-black'} rounded-xl flex-col`}>
                <div className="p-3.5 flex justify-between items-center text-white">
                    <h1 className="font-medium">Running Tasks</h1>
                    <h1 className="text-3xl">{runningTasksCount}</h1>
                </div>
                <div className="pt-16 px-3 py-5 h-1/2 flex justify-between items-center relative">
                    <div className="w-1/2 2xl:w-full relative">
                        <Doughnut data={getCircleData(percentage)} options={circleOptions} />
                        <div className="absolute inset-0 flex items-center justify-center">
                            <p className="text-white text-lg font-bold">{percentage}%</p>
                        </div>
                    </div>
                    <p className="text-white text-lg font-bold">{totalTasks} Tasks</p>
                </div>
            </div>
        </div>
    );
}

export function GeminiChat({ appMode }) {
    const popupRef = useRef(null);
    const chatContainerRef = useRef(null);
    const [isOpen, setIsOpen] = useState(false);
    const [photoURL, setPhotoURL] = useState(null);
    const [userInput, setUserInput] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [chatHistory, setChatHistory] = useState([]);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            if (user) {
                setPhotoURL(user.photoURL);
            } else {
                setPhotoURL(null);
            }
        });
        return () => unsubscribe();
    }, []);

    useEffect(() => {
        if (chatContainerRef.current) {
            chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
        }
        function handleClickOutside(event) {
            if (popupRef.current && !popupRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [chatHistory, isOpen]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        await geminiChat(userInput, setUserInput, chatHistory, setChatHistory, setIsLoading);
    };

    const toggleChat = () => setIsOpen(!isOpen);
    const clearChat = () => setChatHistory([]);

    return (
        <div className="relative mt-2.5">
            <div onClick={toggleChat} className={`flex items-center p-3 rounded-lg shadow-md border ${appMode === AppMode.LIGHT ? 'bg-white border-gray-200' : dark_mode_primary + ' border-gray-800'} hover:shadow-lg transition-all duration-300 cursor-pointer`}>
                <img src={Gemini} className='w-9 h-9 object-cover rounded-full' alt='' />
                <span className={`ml-2.5 ${appMode === AppMode.LIGHT ? 'text-gray-700' : 'text-gray-300'} font-medium`}>Ask Me Anything...</span>
            </div>
            {isOpen && (
                <div className={`fixed inset-0 ${appMode === AppMode.LIGHT ? 'bg-black' : 'bg-gray-900'} bg-opacity-70 backdrop-blur-sm flex items-center justify-center z-50 transition-all duration-300`}>
                    <div ref={popupRef} className={`w-5/6 h-[95vh] rounded-2xl shadow-2xl border ${appMode === AppMode.LIGHT ? 'bg-white border-gray-200' : dark_mode_primary + ' border-gray-900'} overflow-y-auto flex flex-col transition-all duration-300 animate-fadeIn`} style={{ boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)' }}>
                        <div className={`flex justify-between items-center p-4 border-b ${appMode === AppMode.LIGHT ? 'bg-gradient-to-r from-blue-50 to-indigo-50 border-gray-200' : 'bg-gradient-to-r from-gray-800 to-gray-800 border-gray-900'}`}>
                            <div className="font-semibold text-gray-800 flex items-center">
                                <div className="w-8 h-8 rounded-full bg-gradient-to-r from-white to-gray-50 flex items-center justify-center mr-2">
                                    <img src={Gemini} className="w-5 h-5 object-cover" alt='' />
                                </div> <h1 className={`${appMode === AppMode.LIGHT ? 'text-gray-700' : 'text-gray-300'}`}>Corella AI</h1>
                                {/* <span className="ml-2 text-xs bg-blue-100 text-blue-600 px-2 py-0.5 rounded-full">AI</span> */}
                            </div>
                            <div className="flex">
                                <button onClick={clearChat} className="p-1.5 text-gray-500 hover:text-red-500 mr-2 rounded-full hover:bg-gray-100 transition-colors" title="Clear chat">
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                                    </svg>
                                </button>
                                <button onClick={toggleChat} className="p-1.5 text-gray-500 hover:text-gray-700 rounded-full hover:bg-gray-100 transition-colors" title="Close">
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                                    </svg>
                                </button>
                            </div>
                        </div>
                        <div ref={chatContainerRef} className="flex-grow overflow-y-auto p-5 custom-scrollbar" style={{ backgroundImage: appMode === AppMode.LIGHT ? 'radial-gradient(#BDBDBD 1px, transparent 0)' : 'radial-gradient(#374151 1px, transparent 0)', backgroundSize: '20px 20px' }}>
                            {chatHistory.length === 0
                                ? <div className="flex flex-col items-center justify-center h-full text-center p-6">
                                    <div className="w-20 h-20 bg-gradient-to-r from-white to-gray-50 rounded-full flex items-center justify-center mb-6 shadow-lg">
                                        <img src={Gemini} className="w-1/2 h-1/2 object-cover" alt='' />
                                    </div>
                                    <h3 className="text-xl font-bold mb-2">Welcome to Corella AI</h3>
                                    <p className="text-gray-600 font-medium mb-3">How can I help you today?</p>
                                    <p className="text-gray-500 text-sm max-w-md">Ask me anything. I'm here to assist with information and answers.</p>
                                </div>
                                : chatHistory.map((msg, index) => (
                                    <div key={index} className={`mb-5 flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                                        {msg.role !== 'user' && (
                                            <div className="w-10 h-10 rounded-full bg-gradient-to-r from-white to-gray-50 flex items-center justify-center mr-3 flex-shrink-0 shadow-md">
                                                <img src="https://images.seeklogo.com/logo-png/61/1/gemini-icon-logo-png_seeklogo-611605.png" className="w-6 h-6 object-cover" alt="Gemini" />
                                            </div>
                                        )}
                                        <div className={`max-w-[75%] p-4 rounded-2xl shadow-sm overflow-hidden ${msg.role === 'user'
                                            ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-br-none'
                                            : 'bg-white text-gray-800 border border-gray-100 rounded-bl-none'
                                            }`}
                                        >
                                            {msg.role === 'user'
                                                ? <p className="break-words overflow-wrap-normal">{msg.content}</p>
                                                : <div className="prose prose-sm max-w-none break-words overflow-hidden">
                                                    <ReactMarkdown components={{ root: ({ node, ...props }) => <div className="prose prose-sm max-w-none" {...props} /> }}>{msg.content}</ReactMarkdown>
                                                </div>
                                            }
                                        </div>
                                        {msg.role === 'user' && (
                                            <div className="w-10 h-10 rounded-full bg-gradient-to-r from-indigo-500 to-purple-600 flex items-center justify-center ml-3 flex-shrink-0 shadow-md overflow-hidden">
                                                {photoURL
                                                    ? <img src={photoURL} className="w-full h-full object-cover" onError={(e) => { e.target.src = 'https://static.vecteezy.com/system/resources/previews/026/196/789/non_2x/profile-icon-symbol-design-illustration-vector.jpg'; }} alt='' />
                                                    : <img src="https://static.vecteezy.com/system/resources/previews/026/196/789/non_2x/profile-icon-symbol-design-illustration-vector.jpg" className="w-full h-full object-cover" alt='' />
                                                }
                                            </div>
                                        )}
                                    </div>
                                )
                                )}
                            {isLoading && (
                                <div className="flex justify-start mb-5">
                                    <div className="w-10 h-10 rounded-full bg-gradient-to-r from-white to-gray-50 flex items-center justify-center mr-3 flex-shrink-0 shadow-md">
                                        <img src="https://images.seeklogo.com/logo-png/61/1/gemini-icon-logo-png_seeklogo-611605.png" className="w-6 h-6 object-cover" alt="Gemini" />
                                    </div>
                                    <div className="max-w-[75%] p-4 rounded-2xl bg-white text-gray-800 shadow-sm border border-gray-100 rounded-bl-none">
                                        <div className="flex space-x-2">
                                            <div className="w-2.5 h-2.5 rounded-full animate-pulse bg-blue-600"></div>
                                            <div className="w-2.5 h-2.5 rounded-full animate-pulse bg-blue-600" style={{ animationDelay: '0.2s' }}></div>
                                            <div className="w-2.5 h-2.5 rounded-full animate-pulse bg-blue-600" style={{ animationDelay: '0.4s' }}></div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                        <form onSubmit={handleSubmit} className="p-4 border-t border-gray-200">
                            <div className={`flex items-center bg-transparent rounded-xl shadow-sm overflow-hidden border ${appMode === AppMode.LIGHT ? 'border-gray-200' : 'border-gray-700'} focus-within:border-transparent transition-all`}>
                                <input type="text" value={userInput} onChange={(e) => setUserInput(e.target.value)} placeholder="Type your message..." className={`flex-grow p-3 bg-transparent border-none focus:outline-none ${appMode === AppMode.LIGHT ? 'text-gray-900' : 'text-gray-200'} placeholder-gray-500`} disabled={isLoading} />
                                <button type="submit" disabled={isLoading || !userInput.trim()} className={`p-3 m-1 rounded-lg flex items-center justify-center ${isLoading || !userInput.trim()
                                    ? `${appMode === AppMode.LIGHT ? 'bg-gray-300' : 'bg-gray-700'} cursor-not-allowed`
                                    : 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700'
                                    } text-white transition-all duration-300`}
                                >
                                    <i className="bi bi-chevron-right text-lg"></i>
                                </button>
                            </div>
                            <div className="mt-2 text-xs text-center text-gray-500">• Powered by Gemini •</div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}

export function ActivityGraph({ appMode }) {
    const dropdownRef = useRef(null);
    const [teams, setTeams] = useState([]);
    const [loading, setLoading] = useState(true);
    const [teamTasks, setTeamTasks] = useState([]);
    const [chartData, setChartData] = useState(null);
    const [timeRange, setTimeRange] = useState('week');
    const [personalTasks, setPersonalTasks] = useState([]);
    const [dropdownOpen, setDropdownOpen] = useState(false);

    useEffect(() => {
        if (!auth.currentUser) return;
        const userTeamsRef = ref(realtime, 'teams');
        const unsubscribe = onValue(userTeamsRef, (snapshot) => {
            const teamsData = snapshot.val() || {};
            const teamsList = Object.entries(teamsData).map(([id, data]) => ({
                id,
                ...data
            })).filter(team =>
                team.members && team.members.includes(auth.currentUser.uid)
            );
            setTeams(teamsList);
        });
        return () => unsubscribe();
    }, []);

    useEffect(() => {
        if (!auth.currentUser) return;
        const userTasksQuery = query(
            collection(firestore, 'tasks'),
            where("assignedTo", "==", auth.currentUser.uid)
        );
        const unsubscribe = onSnapshot(userTasksQuery, (snapshot) => {
            const tasksData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setPersonalTasks(tasksData);
            setLoading(false);
        });
        return () => unsubscribe();
    }, []);

    useEffect(() => {
        if (!auth.currentUser || teams.length === 0) return;
        const tasksRef = ref(realtime, 'tasks');
        const unsubscribe = onValue(tasksRef, (snapshot) => {
            const tasksData = snapshot.val() || {};
            const tasksList = Object.entries(tasksData).map(([id, data]) => ({
                id,
                ...data,
            })).filter(task =>
                teams.some(team => team.id === task.teamId) &&
                task.assignedTo === auth.currentUser.uid,
            );
            setTeamTasks(tasksList);
            setLoading(false);
        });
        return () => unsubscribe();
    }, [teams]);

    useEffect(() => {
        if (loading) return;
        const allTasks = [...personalTasks, ...teamTasks];
        const activityData = processTasksForTimeRange(allTasks, timeRange);
        setChartData({
            data: {
                labels: activityData.labels,
                datasets: [
                    {
                        ...data.datasets[0],
                        data: activityData.values,
                    },
                ]
            },
            options: {
                ...options,
                scales: {
                    ...options.scales,
                    y: {
                        ...options.scales.y,
                        min: 0,
                        max: activityData.yMax,
                        ticks: { stepSize: Math.ceil(activityData.yMax / 5) || 1 },
                    },
                    x: {
                        ...options.scales.x,
                        ticks: {
                            autoSkip: false,
                            maxRotation: 0,
                            font: { size: 10 },
                        }
                    },
                },
                plugins: {
                    tooltip: {
                        callbacks: {
                            title: function (context) {
                                const index = context[0].dataIndex;
                                const date = activityData.dates[index];
                                if (timeRange === 'year') {
                                    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
                                } else if (timeRange === 'month') {
                                    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
                                } else {
                                    return date.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
                                }
                            }
                        }
                    }
                },
            }
        });
    }, [personalTasks, teamTasks, timeRange, loading]);

    useEffect(() => {
        function handleClickOutside(event) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setDropdownOpen(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const processTasksForTimeRange = (tasks, range) => {
        const today = new Date();
        let days, labels = [];
        const dates = [];
        switch (range) {
            case 'month':
                days = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
                break;
            case 'year':
                days = 12;
                break;
            case 'week':
            default:
                days = 7;
        }
        if (range === 'year') {
            for (let i = 0; i < 12; i++) {
                const monthIndex = (today.getMonth() - 11 + i + 12) % 12;
                const year = today.getFullYear() - (today.getMonth() < monthIndex ? 1 : 0);
                const date = new Date(year, monthIndex, 1);
                dates.push(date);
                labels.push(date.toLocaleDateString('en-US', { month: 'short' }));
            }
        } else if (range === 'month') {
            const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
            for (let i = 0; i < days; i++) {
                const date = new Date(firstDay);
                date.setDate(firstDay.getDate() + i);
                dates.push(date);
                labels.push(date.getDate().toString());
            }
        } else {
            for (let i = 6; i >= 0; i--) {
                const date = new Date(today);
                date.setDate(today.getDate() - i);
                date.setHours(0, 0, 0, 0);
                dates.push(date);
                labels.push(date.toLocaleDateString('en-US', { weekday: 'short' }));
            }
        }

        const values = dates.map(date => {
            return tasks.filter(task => {
                if (!task.updatedAt) return false;
                let taskDate;
                if (typeof task.updatedAt === 'string') {
                    taskDate = new Date(task.updatedAt);
                } else if (task.updatedAt.toDate) {
                    taskDate = task.updatedAt.toDate();
                } else {
                    return false;
                }
                if (range === 'year') {
                    return taskDate.getMonth() === date.getMonth() &&
                        taskDate.getFullYear() === date.getFullYear() &&
                        task.status === TaskStatus.DONE;
                } else if (range === 'month') {
                    return taskDate.getDate() === date.getDate() &&
                        taskDate.getMonth() === date.getMonth() &&
                        taskDate.getFullYear() === date.getFullYear() &&
                        task.status === TaskStatus.DONE;
                } else {
                    return taskDate.toDateString() === date.toDateString() &&
                        task.status === TaskStatus.DONE;
                }
            }).length;
        });
        const maxValue = Math.max(...values, 1);
        const yMax = Math.max(Math.ceil(maxValue * 1.2), 3);
        return { labels, values, yMax, dates };
    };

    const toggleDropdown = () => {
        setDropdownOpen(!dropdownOpen);
    };

    const handleTimeRangeSelect = (range) => {
        setTimeRange(range);
        setDropdownOpen(false);
    };

    const getTimeRangeLabel = () => {
        switch (timeRange) {
            case 'month': return 'This month';
            case 'year': return 'This year';
            default: return 'This week';
        }
    };

    return (
        <div className="p-3 w-2/3 h-64">
            <div className={`h-full ${appMode === AppMode.LIGHT ? light_mode_primary : 'bg-gray-800'} rounded-xl`}>
                <div className="p-3.5 flex justify-between items-center">
                    <h1 className={`font-medium ${appMode === AppMode.LIGHT ? '' : 'text-white'}`}>Activity</h1>
                    <div className="relative" ref={dropdownRef}>
                        <button onClick={toggleDropdown} className={`flex items-center ${appMode === AppMode.LIGHT ? 'hover:bg-gray-100' : 'hover:bg-gray-700'} px-3 py-1 rounded-md transition-colors`}>
                            <span className={`${appMode === AppMode.LIGHT ? '' : 'text-white'}`}>{getTimeRangeLabel()}</span>
                            <i className={`ml-2 bi ${dropdownOpen ? 'bi-chevron-up' : 'bi-chevron-down'} ${appMode === AppMode.LIGHT ? '' : 'text-white'}`}></i>
                        </button>
                        {dropdownOpen && (
                            <div className={`absolute right-0 mt-1 w-36 rounded-md shadow-lg z-10 ${appMode === AppMode.LIGHT ? 'bg-white' : 'bg-gray-700'} ring-1 ring-black ring-opacity-5`}>
                                <div className="py-1" role="menu" aria-orientation="vertical">
                                    <button onClick={() => handleTimeRangeSelect('week')} className={`block w-full text-left px-4 py-2 text-sm ${appMode === AppMode.LIGHT ? 'text-gray-700 hover:bg-gray-100' : 'text-white hover:bg-gray-600'} ${timeRange === 'week' ? (appMode === AppMode.LIGHT ? 'bg-gray-100' : 'bg-gray-600') : ''}`} role="menuitem">This week</button>
                                    <button onClick={() => handleTimeRangeSelect('month')} className={`block w-full text-left px-4 py-2 text-sm ${appMode === AppMode.LIGHT ? 'text-gray-700 hover:bg-gray-100' : 'text-white hover:bg-gray-600'} ${timeRange === 'month' ? (appMode === AppMode.LIGHT ? 'bg-gray-100' : 'bg-gray-600') : ''}`} role="menuitem">This month</button>
                                    <button onClick={() => handleTimeRangeSelect('year')} className={`block w-full text-left px-4 py-2 text-sm ${appMode === AppMode.LIGHT ? 'text-gray-700 hover:bg-gray-100' : 'text-white hover:bg-gray-600'} ${timeRange === 'year' ? (appMode === AppMode.LIGHT ? 'bg-gray-100' : 'bg-gray-600') : ''}`} role="menuitem">This year</button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
                <div className="px-3 py-1">
                    <div className={`bg-white w-full h-40 rounded-lg`}>
                        <div className="w-full h-full">
                            {chartData
                                ? <Line data={chartData.data} options={{ ...chartData.options, maintainAspectRatio: false }} />
                                : <div className="w-full h-full flex items-center justify-center">
                                    <p className={`${appMode === AppMode.LIGHT ? 'text-gray-400' : 'text-gray-300'}`}>Loading activity data...</p>
                                </div>
                            }
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export const ProfileHeader = ({ appMode, title, subtitle, setAuthStatus, toggleAppMode, isReorderMode, toggleReorderMode }) => {
    const history = useHistory();
    const [isOpen, setIsOpen] = useState(false);
    const goto = () => { history.push(`/user`); }
    const [userName, setUserName] = useState(null);
    const [photoURL, setPhotoURL] = useState(null);
    const [notifications, setNotifications] = useState([]);
    const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
    const [notificationsLoading, setNotificationsLoading] = useState(true);
    const [showAllNotifications, setShowAllNotifications] = useState(false);

    const logout = async () => {
        try {
            await signOut(auth);
            setAuthStatus(AuthStatus.NOT_LOGGED_IN);
        }
        catch (err) { }
    };

    const handleToggleDropdown = () => {
        setIsOpen(!isOpen);
    };

    const getFirstName = (fullName) => {
        if (!fullName) return 'User';
        return fullName.split(' ')[0];
    };

    const handleToggleNotifications = () => {
        setIsNotificationsOpen(!isNotificationsOpen);
        if (!isNotificationsOpen) {
            setIsOpen(false);
            setShowAllNotifications(false);
        }
    };

    const handleViewAllNotifications = () => {
        setIsNotificationsOpen(false);
        setShowAllNotifications(true);
    };

    const handleCloseAllNotifications = () => {
        setShowAllNotifications(false);
    };

    const getUnreadCount = () => {
        return notifications.filter(notification => !notification.read).length;
    };

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (isOpen && !e.target.closest('.profile-dropdown') && !e.target.closest('.profile-button')) {
                setIsOpen(false);
            }
            if (isNotificationsOpen && !e.target.closest('.notifications-dropdown') && !e.target.closest('.notifications-button')) {
                setIsNotificationsOpen(false);
            }
        };
        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, [isOpen, isNotificationsOpen]);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            if (user) {
                setUserName(getFirstName(user.displayName) || 'User');
                setPhotoURL(user.photoURL);
            } else {
                setUserName('Guest');
                setPhotoURL(null);
            }
        });
        return () => unsubscribe();
    }, []);

    useEffect(() => {
        if (!auth.currentUser) return;
        setNotificationsLoading(true);
        const fetchUserTeamsAndNotifications = async () => {
            try {
                const userTeamsRef = ref(realtime, 'teams');
                const teamsSnapshot = await get(userTeamsRef);
                if (!teamsSnapshot.exists()) {
                    setNotificationsLoading(false);
                    return;
                }
                const teamsData = teamsSnapshot.val();
                const userTeamIds = Object.entries(teamsData)
                    .filter(([_, data]) => data.members && data.members.includes(auth.currentUser.uid))
                    .map(([id]) => id);
                userTeamIds.push(`personal_${auth.currentUser.uid}`);

                if (userTeamIds.length === 0) {
                    setNotificationsLoading(false);
                    return;
                }
                const activityLogsQuery = query(
                    collection(firestore, 'activityLogs'),
                    where('teamId', 'in', userTeamIds),
                    orderBy('timestamp', 'desc'),
                    limit(20)
                );
                const unsubscribeActivityLogs = onSnapshot(activityLogsQuery, (snapshot) => {
                    const activityNotifications = snapshot.docs.filter(doc => {
                        const data = doc.data();
                        return data.userId !== auth.currentUser.uid;
                    }).map(doc => {
                        const data = doc.data();
                        return {
                            id: doc.id,
                            message: formatActivityLogMessage(data),
                            time: formatTimestamp(data.timestamp),
                            read: false,
                            data: data,
                        };
                    });

                    const userNotificationsQuery = query(
                        collection(firestore, 'notifications'),
                        where('userId', '==', auth.currentUser.uid),
                        orderBy('timestamp', 'desc'),
                        limit(10),
                    );

                    getDocs(userNotificationsQuery).then(userNotificationsSnapshot => {
                        const userNotifications = userNotificationsSnapshot.docs.map(doc => {
                            const data = doc.data();
                            return {
                                id: doc.id,
                                message: formatNotificationMessage(data),
                                time: formatTimestamp(data.timestamp),
                                read: data.read || false,
                                data: data,
                                isDirectNotification: true
                            };
                        });
                        const allNotifications = [...activityNotifications, ...userNotifications]
                            .sort((a, b) => {
                                const dateA = new Date(a.data.timestamp?.toDate?.() || a.data.timestamp);
                                const dateB = new Date(b.data.timestamp?.toDate?.() || b.data.timestamp);
                                return dateB - dateA;
                            });

                        setNotifications(allNotifications);
                        setNotificationsLoading(false);
                    });
                });
                return () => {
                    unsubscribeActivityLogs();
                };
            } catch (error) {
                setNotificationsLoading(false);
            }
        };
        fetchUserTeamsAndNotifications();
    }, []);

    const formatNotificationMessage = (data) => {
        switch (data.type) {
            case 'team_invite':
                return `You were invited to join team ${data.teamName}`;
            default:
                return data.message || "New notification";
        }
    };

    const formatActivityLogMessage = (data) => {
        const userName = data.userName || "Someone";
        switch (data.action) {
            case 'created':
                return `${userName} created a new task`;
            case 'deleted':
                return `${userName} deleted a task`;
            case 'updated details':
                return `${userName} updated a task`;
            case 'created team':
                return `${userName} created a new team`;
            default:
                if (data.action.includes('moved from')) {
                    return `${userName} ${data.action} for a task`;
                } else if (data.action.includes('added')) {
                    return `${userName} ${data.action}`;
                }
                return `${userName} ${data.action}`;
        }
    };

    const formatTimestamp = (timestamp) => {
        if (!timestamp) return "Just now";
        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / (1000 * 60));
        const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
        if (diffMins < 1) return "Just now";
        if (diffMins < 60) return `${diffMins} min ago`;
        if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
        if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;

        return date.toLocaleDateString();
    };

    const markNotificationAsRead = async (id, isDirectNotification) => {
        try {
            setNotifications(notifications.map(notification =>
                notification.id === id ? { ...notification, read: true } : notification
            ));
            if (isDirectNotification) {
                await updateDoc(doc(firestore, 'notifications', id), {
                    read: true
                });
            }
        }
        catch (error) { }
    };

    const markAllAsRead = async () => {
        setNotifications(notifications.map(notification => ({ ...notification, read: true })));
        const directNotifications = notifications.filter(n => n.isDirectNotification && !n.read);
        for (const notification of directNotifications) {
            try {
                await updateDoc(doc(firestore, 'notifications', notification.id), { read: true });
            }
            catch (error) { }
        }
    };
    return (
        <div className="px-5 py-6 flex justify-between">
            <div className="flex-col">
                <h1 className="font-semibold text-2xl">{title ?? (userName ? `Hi, ${userName}` : 'Welcome')}</h1>
                <h2 className="text-lg">{subtitle && "Let's get your day started!"}</h2>
            </div>
            <div className="flex items-center">
                <div className="relative">
                    <button className={`h-10 w-10 notifications-button relative ${appMode === AppMode.LIGHT ? 'bg-white text-[#8E92BC]' : dark_mode_primary} rounded-full shadow-md flex items-center justify-center`} onClick={handleToggleNotifications}>
                        <i className="bi bi-bell font-semibold text-lg"></i>
                        {getUnreadCount() > 0 && <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">{getUnreadCount()}</span>}
                    </button>
                    {isNotificationsOpen && <Notifications appMode={appMode} notifications={notifications} onViewAll={handleViewAllNotifications} getUnreadCount={getUnreadCount} markAsRead={markNotificationAsRead} markAllAsRead={markAllAsRead} loading={notificationsLoading} />}
                </div>
                <div className="relative">
                    <button className="h-12 w-12 rounded-full ml-3 overflow-hidden bg-gray-200 profile-button" onClick={handleToggleDropdown}>
                        {photoURL
                            ? <img src={photoURL} className="w-full h-full object-cover" alt='' onError={(e) => { e.target.src = 'https://static.vecteezy.com/system/resources/previews/026/196/789/non_2x/profile-icon-symbol-design-illustration-vector.jpg'; }} />
                            : <img src="https://static.vecteezy.com/system/resources/previews/026/196/789/non_2x/profile-icon-symbol-design-illustration-vector.jpg" className="w-full h-full object-cover" alt='' />
                        }
                    </button>
                    {isOpen && (
                        <div className={`profile-dropdown absolute right-0 mt-2 w-56 rounded-lg shadow-lg z-50 overflow-hidden transition-all duration-200 transform origin-top-right ${appMode === AppMode.LIGHT
                            ? 'bg-white ring-1 ring-black ring-opacity-5'
                            : 'bg-gray-800 ring-1 ring-gray-700'}`}>
                            <div className="py-1">
                                <button onClick={goto} className={`group flex w-full items-center px-4 py-3 text-sm ${appMode === AppMode.LIGHT ? 'hover:bg-gray-100 text-gray-700' : 'hover:bg-gray-700 text-gray-200'}`}>
                                    <i className={`mr-3 text-lg bi bi-person ${appMode === AppMode.LIGHT ? 'text-gray-500' : 'text-gray-400'} group-hover:text-blue-500`}></i>
                                    <span>My Profile</span>
                                </button>
                                <button onClick={toggleAppMode} className={`group flex w-full items-center px-4 py-3 text-sm ${appMode === AppMode.LIGHT ? 'hover:bg-gray-100 text-gray-700' : 'hover:bg-gray-700 text-gray-200'}`}>
                                    <i className={`mr-3 text-lg bi ${appMode === AppMode.LIGHT ? 'bi-moon text-gray-500' : 'bi-sun text-gray-400'} group-hover:text-blue-500`}></i>
                                    <span>{appMode === AppMode.LIGHT ? 'Dark Mode' : 'Light Mode'}</span>
                                </button>
                                {toggleReorderMode && (
                                    <button onClick={toggleReorderMode} className={`group flex w-full items-center px-4 py-3 text-sm ${appMode === AppMode.LIGHT ? 'hover:bg-gray-100 text-gray-700' : 'hover:bg-gray-700 text-gray-200'}`}>
                                        <i className={`bi ${isReorderMode ? 'bi-layout-wtf' : 'bi-grid-3x3'} mr-3 ${appMode === AppMode.LIGHT ? 'text-gray-500' : 'text-gray-400'}`}></i> {isReorderMode ? "Done" : "Customize Dashboard"}
                                    </button>
                                )}
                                <div className={`my-1 h-px ${appMode === AppMode.LIGHT ? 'bg-gray-200' : 'bg-gray-700'}`}></div>
                                <button onClick={logout} className={`group flex w-full items-center px-4 py-3 text-sm ${appMode === AppMode.LIGHT ? 'hover:bg-gray-100 text-red-600' : 'hover:bg-gray-700 text-red-400'}`}>
                                    <i className="mr-3 text-lg bi bi-box-arrow-left group-hover:text-red-500"></i>
                                    <span>Logout</span>
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
            {showAllNotifications && <AllNotifications appMode={appMode} notifications={notifications} setNotifications={setNotifications} markAsRead={markNotificationAsRead} markAllAsRead={markAllAsRead} loading={notificationsLoading} onClose={handleCloseAllNotifications} />}
        </div>
    );
};

export const WidgetSelectionPopup = ({ isOpen, onClose, onAddWidget, appMode }) => {
    const popupRef = useRef(null);
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (popupRef.current && !popupRef.current.contains(event.target)) {
                onClose();
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };//eslint-disable-next-line
    }, []);

    if (!isOpen) return null;
    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div ref={popupRef} className={`${appMode === AppMode.LIGHT ? 'bg-white' : 'bg-gray-800'}  rounded-lg p-6 max-w-md w-full mx-4 max-h-96 overflow-y-auto`}>
                <div className="flex justify-between items-center mb-4">
                    <h2 className={`text-xl font-semibold ${appMode === AppMode.LIGHT ? 'text-gray-900' : 'text-white'}`}>Add New Widget</h2>
                    <button onClick={onClose} className={`${appMode === AppMode.LIGHT ? 'text-gray-500 hover:text-gray-700' : 'text-gray-400 hover:text-gray-200'}`}>
                        <i className="bi bi-x-lg"></i>
                    </button>
                </div>
                <div className="space-y-3">
                    {availableWidgets.map(widget => (
                        <div key={widget.id} className={`p-3 rounded-lg border cursor-pointer transition-colors
                                      ${appMode === AppMode.LIGHT
                                ? 'border-gray-200 hover:border-blue-300 hover:bg-blue-50'
                                : 'border-gray-600 hover:border-blue-500 hover:bg-gray-700'}`}
                            onClick={() => onAddWidget(widget)}
                        >
                            <h3 className={`font-medium ${appMode === AppMode.LIGHT ? 'text-gray-900' : 'text-white'}`}>{widget.name}</h3>
                            <p className={`text-sm ${appMode === AppMode.LIGHT ? 'text-gray-600' : 'text-gray-400'}`}>{widget.description}</p>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export const MediaPreviewModal = ({ media, onClose, appMode }) => {
    if (!media) return null;
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75 p-4" onClick={onClose}>
            <div className={`relative max-w-4xl max-h-[90vh] rounded-lg overflow-hidden ${appMode === AppMode.LIGHT ? 'bg-white' : 'bg-gray-900'}`} onClick={(e) => e.stopPropagation()}>
                <div className={`absolute top-2 right-2 z-10`}>
                    <button onClick={onClose} className="p-2 rounded-full bg-black bg-opacity-50 text-white hover:bg-opacity-70 transition-colors">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>
                <div className="p-4">
                    {media.type === 'image'
                        ? <img src={media.url} className="max-h-[80vh] max-w-full object-contain mx-auto" alt="" />
                        : media.type === 'video'
                            ? <video src={media.url} controls className="max-h-[80vh] max-w-full mx-auto" autoPlay />
                            : media.type === 'audio'
                                ? <div className={`p-8 rounded-lg ${appMode === AppMode.LIGHT ? 'bg-gray-100' : 'bg-gray-800'}`}>
                                    <audio src={media.url} controls className="w-full" autoPlay />
                                    <p className={`mt-4 text-center ${appMode === AppMode.LIGHT ? 'text-gray-700' : 'text-gray-300'}`}>
                                        {media.name || "Audio file"}
                                    </p>
                                </div>
                                : <div className="text-center p-8">
                                    <svg xmlns="http://www.w3.org/2000/svg" className={`h-16 w-16 mx-auto mb-4 ${appMode === AppMode.LIGHT ? 'text-gray-400' : 'text-gray-500'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                    </svg>
                                    <p className={`${appMode === AppMode.LIGHT ? 'text-gray-700' : 'text-gray-300'}`}>{media.name || "File"}</p>
                                    <a href={media.url} target="_blank" rel="noopener noreferrer" className={`mt-4 inline-block px-4 py-2 rounded-md ${appMode === AppMode.LIGHT ? 'bg-blue-100 text-blue-700 hover:bg-blue-200' : 'bg-blue-900 bg-opacity-50 text-blue-300 hover:bg-opacity-70'}`}>Open File</a>
                                </div>
                    }
                </div>
            </div>
        </div>
    );
};

export function Outlook({ appMode, authManager, setIsMicrosoftLoggedIn }) {
    const history = useHistory();
    const backdropRef = useRef(null);
    const [user, setUser] = useState(null);
    const [emails, setEmails] = useState([]);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [forwardTo, setForwardTo] = useState('');
    const [replyContent, setReplyContent] = useState('');
    const [isConnected, setIsConnected] = useState(false);
    const [updateTrigger, setUpdateTrigger] = useState(0);
    const [showDropdown, setShowDropdown] = useState(false);
    const [sendingReply, setSendingReply] = useState(false);
    const [isForwarding, setIsForwarding] = useState(false);//eslint-disable-next-line
    const [ignored, forceUpdate] = useReducer(x => x + 1, 0);
    const [selectedEmail, setSelectedEmail] = useState(null);
    const [forwardContent, setForwardContent] = useState('');//eslint-disable-next-line
    const [isInitialized, setIsInitialized] = useState(false);
    const [showReplyForm, setShowReplyForm] = useState(false);
    const [deletingEmail, setDeletingEmail] = useState(false);//eslint-disable-next-line
    const [attachmentData, setAttachmentData] = useState(null);
    const [sendingForward, setSendingForward] = useState(false);
    const [showEmailDetail, setShowEmailDetail] = useState(false);
    const [markingAsUnread, setMarkingAsUnread] = useState(false);
    const [subscriptionInfo, setSubscriptionInfo] = useState(null);
    const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
    const [showAccountOptions, setShowAccountOptions] = useState(false);
    const [downloadingAttachment, setDownloadingAttachment] = useState(false);

    useEffect(() => {
        if (authManager) {
            try {
                if (typeof authManager.getSubscriptionInfo === 'function') {
                    setSubscriptionInfo(authManager.getSubscriptionInfo());
                } else {
                    const accountsCount = authManager.getLoggedInAccounts().length;
                    setSubscriptionInfo({
                        tier: PaymentTiers.FREE,
                        name: 'Free',
                        accountLimit: 2,
                        accountsUsed: accountsCount,
                        canAddMore: accountsCount < 2,
                    });
                }
            } catch (error) {
                setSubscriptionInfo({
                    tier: PaymentTiers.FREE,
                    name: 'Free',
                    accountLimit: 2,
                    accountsUsed: 0,
                    canAddMore: true,
                });
            }
        }//eslint-disable-next-line
    }, []);

    const sanitizeHtml = (html) => {
        if (!html) return '';
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = html;
        const scripts = tempDiv.querySelectorAll('script');
        scripts.forEach(script => script.remove());
        const styles = tempDiv.querySelectorAll('style');
        styles.forEach(style => style.remove());

        const elements = tempDiv.querySelectorAll('*');
        elements.forEach(el => {
            const attrs = el.attributes;
            for (let i = attrs.length - 1; i >= 0; i--) {
                const attrName = attrs[i].name;
                if (attrName.startsWith('on')) {
                    el.removeAttribute(attrName);
                }
                if (attrName === 'href' || attrName === 'src') {
                    const value = attrs[i].value;//eslint-disable-next-line
                    if (value.toLowerCase().startsWith('javascript:')) {
                        el.removeAttribute(attrName);
                    }
                }
            }
            if (['iframe', 'object', 'embed', 'script', 'applet'].includes(el.tagName.toLowerCase())) {
                el.remove();
            }
        });
        return tempDiv.innerHTML;
    };

    useEffect(() => {
        if (showEmailDetail) {
            document.body.style.overflow = 'hidden';
            if (backdropRef.current) {
                backdropRef.current.style.display = 'flex';
                setTimeout(() => {
                    if (backdropRef.current) {
                        backdropRef.current.style.opacity = '1';
                    }
                }, 10);
            }
        } else {
            document.body.style.overflow = '';
            if (backdropRef.current) {
                backdropRef.current.style.opacity = '0';
                setTimeout(() => {
                    if (backdropRef.current) {
                        backdropRef.current.style.display = 'none';
                    }
                }, 300);
            }
        }
    }, [showEmailDetail]);

    const canAddMoreAccounts = () => {
        if (!authManager) return true;
        try {
            if (typeof authManager.canAddMoreAccounts === 'function') {
                return authManager.canAddMoreAccounts();
            } else {
                return authManager.getLoggedInAccounts().length < 2;
            }
        } catch (error) {
            return false;
        }
    };

    const handleUpgradeRedirect = () => {
        setShowUpgradePrompt(false);
        history.push('/pricing');
    };

    useEffect(() => {
        const initializeAuth = async () => {
            try {
                const result = await msalInstance.handleRedirectPromise();
                if (result) {
                    if (authManager && !canAddMoreAccounts()) {
                        setShowUpgradePrompt(true);
                        setIsInitialized(true);
                        setLoading(false);
                        return;
                    }
                    const newAccount = {
                        ...result.account,
                        provider: AuthProvider.MICROSOFT,
                        email: result.account.username,
                        username: result.account.username
                    };
                    setUser(result.account);
                    setIsConnected(true);
                    if (authManager) {
                        try {
                            authManager.addAccount(AuthProvider.MICROSOFT, newAccount);
                        } catch (error) {
                            if (error.message && error.message.includes("Account limit reached")) {
                                setShowUpgradePrompt(true);
                                setIsInitialized(true);
                                setLoading(false);
                                return;
                            }
                            throw error;
                        }
                    }
                    await fetchEmails(result.account, setLoading, setEmails, setError);
                } else {
                    const accounts = msalInstance.getAllAccounts();
                    if (accounts.length > 0) {
                        setUser(accounts[0]);
                        setIsConnected(true);
                        if (authManager) {
                            const msAccounts = authManager.getAccounts(AuthProvider.MICROSOFT);
                            if (msAccounts.length === 0) {
                                if (!canAddMoreAccounts()) {
                                    setShowUpgradePrompt(true);
                                    setIsInitialized(true);
                                    setLoading(false);
                                    return;
                                }
                                const newAccount = {
                                    ...accounts[0],
                                    provider: AuthProvider.MICROSOFT,
                                    email: accounts[0].username,
                                    username: accounts[0].username
                                };
                                try {
                                    authManager.addAccount(AuthProvider.MICROSOFT, newAccount);
                                } catch (error) {
                                    if (error.message && error.message.includes("Account limit reached")) {
                                        setShowUpgradePrompt(true);
                                        setIsInitialized(true);
                                        setLoading(false);
                                        return;
                                    }
                                    throw error;
                                }
                            }
                        }
                        await fetchEmails(accounts[0], setLoading, setEmails, setError);
                    } else {
                        setIsConnected(false);
                        setLoading(false);
                    }
                }
            } catch (err) {
                setError(err.message);
                setIsConnected(false);
            }
            finally {
                setIsInitialized(true);
                setLoading(false);
            }
        };
        initializeAuth();//eslint-disable-next-line
    }, [, updateTrigger]);

    const handleMicrosoftLogin = async () => {
        try {
            if (!canAddMoreAccounts()) {
                setShowUpgradePrompt(true);
                return;
            }
            setLoading(true);
            await microsoftAuthService.initialize();
            if (authManager) {
                const result = await handleLogin(setUser, setLoading, setError, setIsMicrosoftLoggedIn);
                if (result) {
                    const msAccounts = authManager.getAccounts(AuthProvider.MICROSOFT);
                    const newAccount = msAccounts.find(acc =>
                        acc.userData && acc.userData.email === result.email
                    );
                    if (newAccount) {
                        authManager.setActiveAccount(newAccount.id);
                    }
                    authManager.notifyStateChange();
                    setUpdateTrigger(prev => prev + 1);
                    forceUpdate();
                    setIsConnected(true);
                    await fetchEmails(result, setLoading, setEmails, setError);
                }
            } else {
                await handleLogin(setUser, setLoading, setError, setIsMicrosoftLoggedIn);
            }
        } catch (e) {
            if (e.message && e.message.includes("Account limit reached")) {
                setShowUpgradePrompt(true);
            } else {
                setError(e.message);
            }
            setLoading(false);
        }
    };

    const handleEmailClick = (email) => {
        setSelectedEmail(email);
        setShowEmailDetail(true);
    };

    const closeEmailDetail = () => {
        setShowEmailDetail(false);
        setSelectedEmail(null);
        setShowReplyForm(false);
        setIsForwarding(false);
        setReplyContent('');
        setForwardContent('');
        setForwardTo('');
    };

    const handleBackdropClick = (e) => {
        if (e.target === backdropRef.current) {
            closeEmailDetail();
        }
    };

    useEffect(() => {
        const handleEscKey = (e) => {
            if (e.key === 'Escape' && showEmailDetail) {
                closeEmailDetail();
            }
        };

        window.addEventListener('keydown', handleEscKey);
        return () => {
            window.removeEventListener('keydown', handleEscKey);
        };
    }, [showEmailDetail]);

    useEffect(() => {
        function handleClickOutside(event) {
            if (showDropdown && !event.target.closest('.dropdown-container')) {
                setShowDropdown(false);
            }
            if (showAccountOptions && !event.target.closest('.account-options-container')) {
                setShowAccountOptions(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showDropdown, showAccountOptions]);

    const handleDeleteEmail = async () => {
        if (!selectedEmail || !selectedEmail.fullEmail || !selectedEmail.fullEmail.id) {
            setError("Cannot delete email: Missing email information");
            return;
        }

        setDeletingEmail(true);
        try {
            const success = await deleteEmail(selectedEmail.account, selectedEmail.fullEmail.id, setLoading, setEmails, setError);
            if (success) {
                closeEmailDetail();
                await fetchEmails(selectedEmail.account, setLoading, setEmails, setError);
            }
        } catch (err) {
            setError(`Failed to delete email: ${err.message}`);
        } finally {
            setDeletingEmail(false);
        }
    };

    const handleSendReply = async () => {
        if (!selectedEmail || !selectedEmail.fullEmail || !selectedEmail.fullEmail.id || !replyContent.trim()) {
            setError("Cannot send reply: Missing email information or reply content");
            return;
        }
        setSendingReply(true);
        try {
            const success = await sendMicrosoftReply(selectedEmail.account, selectedEmail.fullEmail.id, replyContent, setLoading, setError);
            if (success) {
                setShowReplyForm(false);
                setReplyContent('');
                await fetchEmails(selectedEmail.account, setLoading, setEmails, setError);
            }
        } catch (err) {
            setError(`Failed to send reply: ${err.message}`);
        } finally {
            setSendingReply(false);
        }
    };

    const handleDownloadAttachment = async (attachmentId) => {
        if (!selectedEmail || !selectedEmail.fullEmail || !selectedEmail.fullEmail.id) {
            setError("Cannot download attachment: Missing email information");
            return;
        }
        setDownloadingAttachment(true);
        try {
            const data = await getMicrosoftAttachmentContent(
                selectedEmail.account,
                selectedEmail.fullEmail.id,
                attachmentId
            );
            setAttachmentData(data);
            const link = document.createElement('a');
            link.href = data.contentUrl;
            const attachment = selectedEmail.attachments.find(att => att.id === attachmentId);
            link.download = attachment ? attachment.name : 'attachment';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (err) {
            setError(`Failed to download attachment: ${err.message}`);
        } finally {
            setDownloadingAttachment(false);
        }
    };
    const handleForward = () => {
        setIsForwarding(true);
        setShowReplyForm(false);
        const sender = selectedEmail?.name || 'Unknown';
        const date = selectedEmail?.date || '';
        const time = selectedEmail?.time || '';
        const subject = selectedEmail?.subject || 'No Subject';
        const to = selectedEmail?.to || 'me';
        const body = selectedEmail?.body || selectedEmail?.message || '';
        const forwardHtml = `
            <div style="padding: 20px 0; font-family: Arial, sans-serif;">
                <div contenteditable="true" style="min-height: 120px; border-bottom: 1px solid #ddd; padding-bottom: 15px; margin-bottom: 15px;">
                    Type your message here...
                </div>
                <div style="padding: 10px; border-left: 2px solid #ccc; color: #666; margin-top: 10px;">
                    <p>---------- Forwarded message ----------</p>
                    <p>From: ${sender} ${selectedEmail?.email ? `<${selectedEmail.email}>` : ''}</p>
                    <p>Date: ${date} at ${time}</p>
                    <p>Subject: ${subject}</p>
                    <p>To: ${to}</p>
                    <div>${body}</div>
                </div>
            </div>
        `;
        setForwardContent(forwardHtml);
    };

    const handleSendForward = async () => {
        if (!selectedEmail || !forwardContent || !forwardTo.trim()) {
            setError("Cannot forward email: Missing recipient or content");
            return;
        }

        setSendingForward(true);
        try {
            let accessToken;
            try {
                if (typeof selectedEmail.account === 'string') {
                    accessToken = await microsoftAuthService.getAccessToken(selectedEmail.account, ['Mail.Send']);
                } else if (selectedEmail.account.id) {
                    accessToken = await microsoftAuthService.getAccessToken(selectedEmail.account.id, ['Mail.Send']);
                } else {
                    const tokenResponse = await msalInstance.acquireTokenSilent({
                        scopes: ['Mail.Send'],
                        account: selectedEmail.account
                    });
                    accessToken = tokenResponse.accessToken;
                }
            } catch (tokenError) {
                if (tokenError instanceof InteractionRequiredAuthError) {
                    if (typeof selectedEmail.account === 'string' || selectedEmail.account.id) {
                        accessToken = await microsoftAuthService.getAccessToken(
                            typeof selectedEmail.account === 'string' ? selectedEmail.account : selectedEmail.account.id,
                            ['Mail.Send']
                        );
                    } else {
                        const tokenResponse = await msalInstance.acquireTokenPopup({
                            scopes: ['Mail.Send'],
                            account: selectedEmail.account
                        });
                        accessToken = tokenResponse.accessToken;
                    }
                } else {
                    throw tokenError;
                }
            }
            const forwardPayload = {
                message: {
                    subject: `FW: ${selectedEmail.subject}`,
                    body: {
                        contentType: "HTML",
                        content: forwardContent
                    },
                    toRecipients: [
                        {
                            emailAddress: {
                                address: forwardTo
                            }
                        }
                    ]
                },
                saveToSentItems: true
            };
            const response = await fetch(`https://graph.microsoft.com/v1.0/me/messages/${selectedEmail.fullEmail.id}/forward`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${accessToken}`,
                },
                body: JSON.stringify(forwardPayload)
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(`Failed to forward email: ${response.status} - ${errorData.error?.message || response.statusText}`);
            }
            setIsForwarding(false);
            setForwardTo('');
            setForwardContent('');

        } catch (err) {
            setError(`Failed to forward email: ${err.message}`);
        } finally {
            setSendingForward(false);
        }
    };

    const handleMarkAsUnread = async () => {
        if (!selectedEmail || !selectedEmail.fullEmail || !selectedEmail.fullEmail.id) {
            setError("Cannot mark as unread: Missing email information");
            return;
        }

        setMarkingAsUnread(true);
        try {
            let accessToken;
            try {
                if (typeof selectedEmail.account === 'string') {
                    accessToken = await microsoftAuthService.getAccessToken(selectedEmail.account, ['Mail.ReadWrite']);
                } else if (selectedEmail.account.id) {
                    accessToken = await microsoftAuthService.getAccessToken(selectedEmail.account.id, ['Mail.ReadWrite']);
                } else {
                    const tokenResponse = await msalInstance.acquireTokenSilent({
                        scopes: ['Mail.ReadWrite'],
                        account: selectedEmail.account
                    });
                    accessToken = tokenResponse.accessToken;
                }
            } catch (tokenError) {
                if (tokenError instanceof InteractionRequiredAuthError) {
                    if (typeof selectedEmail.account === 'string' || selectedEmail.account.id) {
                        accessToken = await microsoftAuthService.getAccessToken(
                            typeof selectedEmail.account === 'string' ? selectedEmail.account : selectedEmail.account.id,
                            ['Mail.ReadWrite']
                        );
                    } else {
                        const tokenResponse = await msalInstance.acquireTokenPopup({
                            scopes: ['Mail.ReadWrite'],
                            account: selectedEmail.account
                        });
                        accessToken = tokenResponse.accessToken;
                    }
                } else {
                    throw tokenError;
                }
            }
            const response = await fetch(`https://graph.microsoft.com/v1.0/me/messages/${selectedEmail.fullEmail.id}`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${accessToken}`,
                },
                body: JSON.stringify({
                    isRead: false
                })
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(`Failed to mark as unread: ${response.status} - ${errorData.error?.message || response.statusText}`);
            }
            setEmails(prevEmails => prevEmails.map(email =>
                email.fullEmail.id === selectedEmail.fullEmail.id
                    ? { ...email, read: false }
                    : email
            ));
            closeEmailDetail();
        } catch (err) {
            setError(`Failed to mark as unread: ${err.message}`);
        } finally {
            setMarkingAsUnread(false);
        }
    };

    const OutlookFallback = () => (
        <div className="flex flex-col items-center justify-center h-full p-6 text-center">
            <div className="mb-4">
                <i className="bi bi-envelope-x text-5xl text-gray-400"></i>
            </div>
            <h2 className={`text-xl font-semibold mb-2 ${appMode === AppMode.LIGHT ? 'text-gray-800' : 'text-gray-200'}`}>No Outlook Account Connected</h2>
            <p className={`mb-6 ${appMode === AppMode.LIGHT ? 'text-gray-600' : 'text-gray-400'}`}>Connect your Outlook account to see your emails here.</p>
            <button onClick={handleMicrosoftLogin} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md flex items-center">
                <i className="bi bi-microsoft me-2"></i> Connect Outlook
            </button>
        </div>
    );

    const EmailDetailModal = () => {
        if (!selectedEmail) return null;
        const hasHtmlContent = selectedEmail.body && selectedEmail.body.includes('<html');

        return (
            <div ref={backdropRef} className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center opacity-0 transition-opacity duration-300" style={{ display: 'none' }} onClick={handleBackdropClick}>
                <div className={`relative w-full max-w-3xl max-h-[90vh] overflow-hidden rounded-lg shadow-2xl transform transition-transform duration-300 ${appMode === AppMode.LIGHT ? 'bg-white' : 'bg-gray-800'}`} onClick={(e) => e.stopPropagation()}>
                    {isForwarding
                        ? <div className={`p-4 border-b ${appMode === AppMode.LIGHT ? 'border-gray-200' : 'border-gray-700'} flex justify-between items-center sticky top-0 z-10 ${appMode === AppMode.LIGHT ? 'bg-white' : 'bg-gray-800'}`}>
                            <h2 className={`font-semibold ${appMode === AppMode.LIGHT ? 'text-gray-800' : 'text-white'}`}>Forward: {selectedEmail.subject}</h2>
                            <button onClick={() => setIsForwarding(false)} className="text-gray-500 hover:text-gray-700">
                                <i className="bi bi-x-lg"></i>
                            </button>
                        </div>
                        : <div className={`p-4 border-b ${appMode === AppMode.LIGHT ? 'border-gray-200' : 'border-gray-700'} flex justify-between items-center sticky top-0 z-10 ${appMode === AppMode.LIGHT ? 'bg-white' : 'bg-gray-800'}`}>
                            <div className="flex items-center space-x-2 overflow-hidden">
                                <div className="flex-shrink-0">
                                    <img src={selectedEmail.imageUrl} className="h-10 w-10 rounded-full object-cover" alt='' />
                                </div>
                                <div className="overflow-hidden">
                                    <h2 className={`font-semibold truncate ${appMode === AppMode.LIGHT ? 'text-gray-800' : 'text-white'}`}>{selectedEmail.subject}</h2>
                                    <p className={`text-sm truncate ${appMode === AppMode.LIGHT ? 'text-gray-600' : 'text-gray-300'}`}>{selectedEmail.name} {selectedEmail.email ? `<${selectedEmail.email}>` : ''}</p>
                                </div>
                            </div>
                            <div className="flex items-center space-x-2">
                                <button className={`p-2 rounded-full hover:${appMode === AppMode.LIGHT ? 'bg-gray-100' : 'bg-gray-700'} transition-colors`} title="Archive" disabled={deletingEmail}>
                                    <i className="bi bi-archive"></i>
                                </button>
                                <button onClick={closeEmailDetail} className={`p-2 rounded-full hover:${appMode === AppMode.LIGHT ? 'bg-gray-100' : 'bg-gray-700'} transition-colors`} title="Close">
                                    <i className="bi bi-x-lg"></i>
                                </button>
                            </div>
                        </div>
                    }
                    {isForwarding
                        ? <div className={`p-6 overflow-y-auto ${appMode === AppMode.LIGHT ? 'bg-white' : 'bg-gray-800'}`} style={{ maxHeight: 'calc(90vh - 130px)' }}>
                            <div className="mb-4">
                                <label className={`block text-sm font-medium mb-2 ${appMode === AppMode.LIGHT ? 'text-gray-700' : 'text-gray-300'}`}>To:</label>
                                <input type="email" value={forwardTo} onChange={(e) => setForwardTo(e.target.value)} className={`w-full px-3 py-2 border rounded-md ${appMode === AppMode.LIGHT ? 'border-gray-300' : 'border-gray-600'}`} placeholder="recipient@example.com" />
                            </div>
                            <div className={`border rounded-md p-4 min-h-[300px] ${appMode === AppMode.LIGHT ? 'border-gray-300' : 'border-gray-600'}`} dangerouslySetInnerHTML={{ __html: forwardContent }} contentEditable onInput={(e) => setForwardContent(e.currentTarget.innerHTML)}></div>
                            <div className="mt-4 flex justify-end space-x-3">
                                <button onClick={() => setIsForwarding(false)} className={`px-4 py-2 rounded-md ${appMode === AppMode.LIGHT ? 'bg-gray-200 hover:bg-gray-300 text-gray-700' : 'bg-gray-700 hover:bg-gray-600 text-white'}`}>Cancel</button>
                                <button onClick={handleSendForward} disabled={!forwardTo || sendingForward} className={`px-4 py-2 rounded-md ${forwardTo && !sendingForward ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-400 cursor-not-allowed'} text-white flex items-center`}>
                                    {sendingForward
                                        ? <>
                                            <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>Sending...
                                        </>
                                        : 'Send'
                                    }
                                </button>
                            </div>
                        </div>
                        : <>
                            <div className={`overflow-y-auto ${appMode === AppMode.LIGHT ? 'text-gray-700' : 'text-gray-200'}`} style={{ maxHeight: 'calc(90vh - 130px)' }}>
                                <div className={`px-6 py-4 ${appMode === AppMode.LIGHT ? 'bg-gray-50' : 'bg-gray-750'}`}>
                                    <div className="flex justify-between items-center">
                                        <div>
                                            <span className="text-sm font-medium">Date: </span>
                                            <span className="text-sm">{selectedEmail.date} {selectedEmail.time}</span>
                                        </div>
                                        <div className="flex space-x-2">
                                            <span className={`px-2 py-1 text-xs rounded-full ${appMode === AppMode.LIGHT ? 'bg-blue-100 text-blue-800' : 'bg-blue-900 text-blue-200'}`}>Inbox</span>
                                            {selectedEmail.fullEmail?.importance === 'high' && <span className={`px-2 py-1 text-xs rounded-full ${appMode === AppMode.LIGHT ? 'bg-red-100 text-red-800' : 'bg-red-900 text-red-200'}`}>Important</span>}
                                        </div>
                                    </div>
                                    {selectedEmail.to && (
                                        <div className="mt-2">
                                            <span className="text-sm font-medium">To: </span>
                                            <span className="text-sm">{selectedEmail.to}</span>
                                        </div>
                                    )}
                                </div>
                                <div className="p-6">
                                    {hasHtmlContent
                                        ? <div className="email-html-content" dangerouslySetInnerHTML={{ __html: sanitizeHtml(selectedEmail.body) }} />
                                        : <div className="whitespace-pre-wrap">{selectedEmail.message || 'No message content'}</div>
                                    }
                                    {selectedEmail.attachments && selectedEmail.attachments.length > 0 && (
                                        <div className={`mt-6 p-4 rounded-md ${appMode === AppMode.LIGHT ? 'bg-gray-50' : 'bg-gray-750'}`}>
                                            <h3 className="font-medium mb-3">Attachments</h3>
                                            <div className="flex flex-wrap gap-3">
                                                {selectedEmail.attachments.map((attachment, index) => (
                                                    <div key={index} className={`flex items-center p-2 rounded-md cursor-pointer ${appMode === AppMode.LIGHT ? 'bg-white border border-gray-200 hover:bg-gray-50' : 'bg-gray-700 border border-gray-600 hover:bg-gray-650'}`} onClick={() => handleDownloadAttachment(attachment.id)}>
                                                        <i className="bi bi-file-earmark text-2xl mr-2"></i>
                                                        <div>
                                                            <p className="text-sm font-medium">{attachment.name}</p>
                                                            <p className="text-xs text-gray-500">{Math.round(attachment.size / 1024)} KB</p>
                                                        </div>
                                                        {downloadingAttachment &&
                                                            <div className="ml-2">
                                                                <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-blue-500"></div>
                                                            </div>
                                                        }
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {showReplyForm && (
                                    <div className={`px-6 pb-6 ${appMode === AppMode.LIGHT ? 'bg-white' : 'bg-gray-800'}`}>
                                        <div className={`p-4 rounded-md border ${appMode === AppMode.LIGHT ? 'border-gray-200 bg-gray-50' : 'border-gray-700 bg-gray-750'}`}>
                                            <h3 className={`font-medium mb-3 ${appMode === AppMode.LIGHT ? 'text-gray-800' : 'text-gray-200'}`}>Reply</h3>
                                            <textarea className={`w-full p-3 rounded-md border ${appMode === AppMode.LIGHT ? 'border-gray-300 bg-white' : 'border-gray-600 bg-gray-700'} focus:outline-none focus:ring-2 focus:ring-blue-500`} rows="5" placeholder="Type your reply here..." value={replyContent} onChange={(e) => setReplyContent(e.target.value)}></textarea>
                                            <div className="flex justify-end mt-3 space-x-2">
                                                <button onClick={() => setShowReplyForm(false)} className={`px-3 py-1 rounded-md ${appMode === AppMode.LIGHT ? 'bg-gray-200 hover:bg-gray-300 text-gray-700' : 'bg-gray-700 hover:bg-gray-600 text-white'}`}>Cancel</button>
                                                <button onClick={handleSendReply} disabled={!replyContent.trim() || sendingReply} className={`px-3 py-1 rounded-md bg-blue-600 hover:bg-blue-700 text-white flex items-center ${!replyContent.trim() || sendingReply ? 'opacity-50 cursor-not-allowed' : ''}`}>
                                                    {sendingReply
                                                        ? <>
                                                            <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div> Sending...
                                                        </>
                                                        : 'Send'
                                                    }
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div className={`p-4 border-t ${appMode === AppMode.LIGHT ? 'border-gray-200 bg-white' : 'border-gray-700 bg-gray-800'} sticky bottom-0 flex justify-between items-center`}>
                                <div>
                                    <button className={`px-3 py-2 rounded-md ${appMode === AppMode.LIGHT ? 'hover:bg-gray-100' : 'hover:bg-gray-700'} transition-colors`} title="Delete" onClick={handleDeleteEmail} disabled={deletingEmail}>
                                        {deletingEmail
                                            ? <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-red-500"></div>
                                            : <i className="bi bi-trash text-red-500"></i>
                                        }
                                    </button>
                                    <button className={`px-3 py-2 rounded-md ${appMode === AppMode.LIGHT ? 'hover:bg-gray-100' : 'hover:bg-gray-700'} transition-colors`} title="Mark as unread" onClick={handleMarkAsUnread} disabled={markingAsUnread}>
                                        {markingAsUnread
                                            ? <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-blue-500"></div>
                                            : <i className="bi bi-envelope"></i>
                                        }
                                    </button>
                                </div>
                                <div className="flex space-x-2">
                                    <button className={`px-4 py-2 rounded-md ${appMode === AppMode.LIGHT ? 'bg-gray-200 hover:bg-gray-300 text-gray-700' : 'bg-gray-700 hover:bg-gray-600 text-white'} transition-colors`} onClick={handleForward}>Forward</button>
                                    <button className="px-4 py-2 rounded-md bg-blue-600 hover:bg-blue-700 text-white transition-colors flex items-center" onClick={() => {
                                        setShowReplyForm(!showReplyForm);
                                        setIsForwarding(false);
                                    }}
                                    >
                                        <i className="bi bi-reply me-2"></i> Reply
                                    </button>
                                </div>
                            </div>
                        </>
                    }
                </div>
            </div>
        );
    };

    const handleAddAccount = () => {
        if (!canAddMoreAccounts()) {
            setShowUpgradePrompt(true);
            setShowDropdown(false);
            return;
        }
        setShowAccountOptions(true);
        setShowDropdown(false);
    };

    const handleRemoveAccount = async () => {
        if (!user) return;
        try {
            setLoading(true);
            if (authManager) {
                const accounts = authManager.getAccounts(AuthProvider.MICROSOFT);
                const accountToRemove = accounts.find(acc =>
                    acc.userData && acc.userData.username === user.username
                );

                if (accountToRemove) {
                    authManager.removeAccount(accountToRemove.id);
                }
            }
            await msalInstance.logoutRedirect({
                account: msalInstance.getAccountByUsername(user.username)
            });
            setUser(null);
            setEmails([]);
            setIsConnected(false);
        } catch (error) {
            setError(`Failed to remove account: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };
    return (
        <div className="pl-2 pr-1 py-4 w-1/2 h-full">
            <div className={`h-full ${appMode === AppMode.LIGHT ? 'bg-white' : dark_mode_primary} rounded-xl overflow-y-auto`}>
                <div className="pr-3 w-full flex justify-between relative sticky top-0 z-10 py-3 px-3 bg-inherit">
                    <h1 className="pl-3 font-medium">Outlook</h1>
                    <button onClick={() => setShowDropdown(!showDropdown)} className="bi bi-three-dots-vertical"></button>
                    {showDropdown && (
                        <div className={`absolute right-0 top-8 w-48 rounded-md shadow-lg z-10 ${appMode === AppMode.LIGHT ? 'bg-white' : 'bg-gray-800'} ring-1 ring-black ring-opacity-5 dropdown-container`}>
                            <ul>
                                <li onClick={handleAddAccount} className={`p-4 text-sm cursor-pointer hover:${appMode === AppMode.LIGHT ? 'bg-gray-100' : 'bg-gray-700'}`}><i className="mr-2 bi bi-plus-lg"></i> Add Account</li>
                                <li className={`px-4 py-3 text-sm cursor-pointer hover:${appMode === AppMode.LIGHT ? 'bg-gray-100' : 'bg-gray-700'}`}><i className="mr-2 bi bi-send"></i> Sent</li>
                                <li className={`px-4 py-3 text-sm cursor-pointer hover:${appMode === AppMode.LIGHT ? 'bg-gray-100' : 'bg-gray-700'}`}><i className="mr-2 bi bi-archive"></i> Junk</li>
                                <li onClick={handleRemoveAccount} className={`px-4 py-3 text-sm cursor-pointer hover:${appMode === AppMode.LIGHT ? 'bg-gray-100' : 'bg-gray-700'} text-red-500`}><i className="mr-2 bi bi-trash3"></i> Remove account</li>
                            </ul>
                        </div>
                    )}
                </div>
                <div className="px-1 py-5 h-[calc(100%-40px)]">
                    {error && (
                        <div className={`mx-4 mb-4 p-3 rounded-md ${appMode === AppMode.LIGHT ? 'bg-red-100 text-red-800' : 'bg-red-900 text-red-200'}`}>
                            <div className="flex items-center">
                                <i className="bi bi-exclamation-triangle-fill mr-2"></i>
                                <span>{error}</span>
                            </div>
                            <button className={`mt-2 text-sm ${appMode === AppMode.LIGHT ? 'text-red-600' : 'text-red-300'}`} onClick={() => setError(null)}>Dismiss</button>
                        </div>
                    )}
                    {loading
                        ? <div className="flex justify-center items-center h-full">
                            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
                        </div>
                        : !isConnected
                            ? <OutlookFallback />
                            : emails.length > 0
                                ? emails.map((email, index) => (
                                    <React.Fragment key={index}>
                                        <div className={`p-3 flex cursor-pointer ${appMode === AppMode.LIGHT ? 'hover:bg-gray-100' : 'hover:bg-gray-900'} transition-colors ${email.read ? '' : 'font-semibold'}`} onClick={() => handleEmailClick(email)}>
                                            <div className="w-1/6 overflow-hidden">
                                                <img src={email.imageUrl} className="h-12 w-12 rounded-full object-cover" alt='' />
                                            </div>
                                            <div className="w-5/6 ml-0.5 overflow-hidden">
                                                <h1 className="truncate">{email.name}</h1>
                                                <h2 className="text-ellipsis whitespace-nowrap">{email.subject}</h2>
                                                <p className="text-xs text-gray-500 truncate">{email.bodyPreview}</p>
                                            </div>
                                        </div>
                                        <hr className="mx-4" />
                                    </React.Fragment>
                                ))
                                : <div className="flex flex-col items-center justify-center h-full">
                                    <i className="bi bi-inbox text-4xl text-gray-400 mb-2"></i>
                                    <p className={`${appMode === AppMode.LIGHT ? 'text-gray-600' : 'text-gray-400'}`}>No emails found</p>
                                </div>
                    }
                </div>
            </div>
            <EmailDetailModal />
            {showUpgradePrompt && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className={`w-96 rounded-lg shadow-lg ${appMode === AppMode.LIGHT ? 'bg-white' : 'bg-gray-800'}`}>
                        <div className="p-4 border-b flex justify-between items-center">
                            <h2 className={`font-semibold ${appMode === AppMode.LIGHT ? 'text-gray-800' : 'text-white'}`}>Account Limit Reached</h2>
                            <button onClick={() => setShowUpgradePrompt(false)} className="text-gray-500 hover:text-gray-700">
                                <i className="bi bi-x-lg"></i>
                            </button>
                        </div>
                        <div className="p-6">
                            <div className="text-center mb-4">
                                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-yellow-100 text-yellow-500 mb-4">
                                    <i className="bi bi-exclamation-triangle text-2xl"></i>
                                </div>
                                <h3 className={`text-lg font-medium ${appMode === AppMode.LIGHT ? 'text-gray-800' : 'text-white'}`}>Subscription Limit Reached</h3>
                                <p className={`mt-2 ${appMode === AppMode.LIGHT ? 'text-gray-600' : 'text-gray-300'}`}>You've reached the maximum number of accounts for your current {subscriptionInfo?.name || 'Free'} plan.</p>
                            </div>
                            <div className="flex justify-center space-x-3">
                                <button onClick={() => setShowUpgradePrompt(false)} className={`px-4 py-2 rounded-md ${appMode === AppMode.LIGHT ? 'bg-gray-200 hover:bg-gray-300 text-gray-700' : 'bg-gray-700 hover:bg-gray-600 text-white'}`}>Cancel</button>
                                <button onClick={handleUpgradeRedirect} className={`px-4 py-2 rounded-md bg-purple-600 hover:bg-purple-700 text-white`}>Upgrade Now</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {showAccountOptions && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className={`w-96 rounded-lg shadow-lg account-options-container ${appMode === AppMode.LIGHT ? 'bg-white' : 'bg-gray-800'}`}>
                        <div className="p-4 border-b flex justify-between items-center">
                            <h2 className={`font-semibold ${appMode === AppMode.LIGHT ? 'text-gray-800' : 'text-white'}`}>Connect Another Account</h2>
                            <button onClick={() => setShowAccountOptions(false)} className="text-gray-500 hover:text-gray-700">
                                <i className="bi bi-x-lg"></i>
                            </button>
                        </div>
                        <div className="p-4">
                            <div className="flex justify-center">
                                <button onClick={() => { setShowAccountOptions(false); handleMicrosoftLogin(); }} className={`p-3 rounded-md flex flex-col items-center w-full ${appMode === AppMode.LIGHT ? 'bg-blue-50 hover:bg-blue-100 text-blue-600' : 'bg-blue-900 hover:bg-blue-800 text-blue-300'}`}>
                                    <i className="bi bi-microsoft text-xl mb-2"></i>
                                    <span className="text-sm">Microsoft Account</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export function OutlookSchool({ appMode, authManager }) {//eslint-disable-next-line
    const [user, setUser] = useState(null);
    const [emails, setEmails] = useState([]);//eslint-disable-next-line
    const [error, setError] = useState(null);//eslint-disable-next-line
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const getSecondAccountEmails = async () => {
            try {
                if (authManager) {
                    const msAccounts = authManager.getAccounts(AuthProvider.MICROSOFT)
                        .filter(acc => acc.status === ProviderAuthStatus.LOGGED_IN);
                    if (msAccounts.length >= 2) {
                        const secondAccount = msAccounts[1];
                        setUser(secondAccount.userData);
                        const currentActiveAccount = authManager.getActiveAccount(AuthProvider.MICROSOFT);
                        authManager.setActiveAccount(secondAccount.id);
                        try {
                            await fetchEmails(secondAccount, setLoading, setEmails, setError);
                        } finally {
                            if (currentActiveAccount) {
                                authManager.setActiveAccount(currentActiveAccount.id);
                            }
                        }
                    } else {
                        setLoading(false);
                    }
                } else {
                    setLoading(false);
                }
            } catch (err) {
                setError(`Failed to fetch second account emails: ${err.message}`);
                setLoading(false);
            }
        };
        getSecondAccountEmails();//eslint-disable-next-line
    }, []);

    return (
        <div className="pb-1.5 h-1/2">
            <div className={`p-3 h-full ${appMode === AppMode.LIGHT ? 'bg-white' : dark_mode_primary} rounded-xl overflow-y-auto`}>
                <h1 className="font-medium">Outlook: School</h1>
                <div className="px-1 py-5">
                    {emails.map((email, index) => (
                        <React.Fragment key={index}>
                            <div className="p-3 flex">
                                <div className="w-1/6 overflow-hidden">
                                    <img src={email.imageUrl} className="h-12 w-12 rounded-full object-cover" alt='' />
                                </div>
                                <div className="w-5/6 ml-2 overflow-hidden truncate">
                                    <h1 className="">{email.name}</h1>
                                    <h2 className="text-ellipsis whitespace-nowrap">{email.subject}</h2>
                                </div>
                            </div>
                            <hr className="mt-1 mx-4" />
                        </React.Fragment>
                    ))}
                </div>
            </div>
        </div>
    );
}

export function Gmail({ appMode, authManager, setIsGoogleLoggedIn }) {
    const history = useHistory();
    const backdropRef = useRef(null);//eslint-disable-next-line
    const [user, setUser] = useState(null);
    const [emails, setEmails] = useState([]);
    const [error, setError] = useState(null);//eslint-disable-next-line
    const [accounts, setAccounts] = useState([]);//eslint-disable-next-line
    const [loading, setLoading] = useState(false);
    const [forwardTo, setForwardTo] = useState('');
    const [isReplying, setIsReplying] = useState(false);
    const [replyContent, setReplyContent] = useState('');
    const [isConnected, setIsConnected] = useState(false);
    const [localLoading, setLocalLoading] = useState(true);
    const [showDropdown, setShowDropdown] = useState(false);
    const [isForwarding, setIsForwarding] = useState(false);
    const [forwardContent, setForwardContent] = useState('');
    const [activeAccount, setActiveAccount] = useState(null);//eslint-disable-next-line
    const [ignored, forceUpdate] = useReducer(x => x + 1, 0);
    const [selectedEmail, setSelectedEmail] = useState(null);//eslint-disable-next-line
    const [isInitialized, setIsInitialized] = useState(false);
    const [showEmailDetail, setShowEmailDetail] = useState(false);
    const [authInitialized, setAuthInitialized] = useState(false);
    const [subscriptionInfo, setSubscriptionInfo] = useState(null);
    const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
    const [showAccountOptions, setShowAccountOptions] = useState(false);
    useEffect(() => {
        if (authManager) {
            try {
                if (typeof authManager.getSubscriptionInfo === 'function') {
                    setSubscriptionInfo(authManager.getSubscriptionInfo());
                } else {
                    const accountsCount = authManager.getLoggedInAccounts().length;
                    setSubscriptionInfo({
                        tier: PaymentTiers.FREE,
                        name: 'Free',
                        accountLimit: 2,
                        accountsUsed: accountsCount,
                        canAddMore: accountsCount < 2,
                    });
                }
            } catch (error) {
                setSubscriptionInfo({
                    tier: PaymentTiers.FREE,
                    name: 'Free',
                    accountLimit: 2,
                    accountsUsed: 0,
                    canAddMore: true,
                });
            }
        }//eslint-disable-next-line
    }, []);

    const sanitizeHtml = (html) => {
        if (!html) return '';
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = html;
        const scripts = tempDiv.querySelectorAll('script');
        scripts.forEach(script => script.remove());
        const styles = tempDiv.querySelectorAll('style');
        styles.forEach(style => style.remove());
        const elements = tempDiv.querySelectorAll('*');
        elements.forEach(el => {
            const attrs = el.attributes;
            for (let i = attrs.length - 1; i >= 0; i--) {
                const attrName = attrs[i].name;
                if (attrName.startsWith('on')) {
                    el.removeAttribute(attrName);
                }
                if (attrName === 'href' || attrName === 'src') {
                    const value = attrs[i].value;//eslint-disable-next-line
                    if (value.toLowerCase().startsWith('javascript:')) {
                        el.removeAttribute(attrName);
                    }
                }
            }
            if (['iframe', 'object', 'embed', 'script', 'applet'].includes(el.tagName.toLowerCase())) {
                el.remove();
            }
        });
        return tempDiv.innerHTML;
    };

    useEffect(() => {
        if (showEmailDetail) {
            document.body.style.overflow = 'hidden';
            if (backdropRef.current) {
                backdropRef.current.style.display = 'flex';
                setTimeout(() => {
                    if (backdropRef.current) {
                        backdropRef.current.style.opacity = '1';
                    }
                }, 10);
            }
        } else {
            document.body.style.overflow = '';
            if (backdropRef.current) {
                backdropRef.current.style.opacity = '0';
                setTimeout(() => {
                    if (backdropRef.current) {
                        backdropRef.current.style.display = 'none';
                    }
                }, 300);
            }
        }
    }, [showEmailDetail]);

    const canAddMoreAccounts = () => {
        if (!authManager) return true;
        try {
            if (typeof authManager.canAddMoreAccounts === 'function') {
                return authManager.canAddMoreAccounts();
            } else {
                return authManager.getLoggedInAccounts().length < 2;
            }
        } catch (error) {
            return false;
        }
    };

    const handleUpgradeRedirect = () => {
        setShowUpgradePrompt(false);
        history.push('/pricing');
    };

    const handleDeleteEmail = async () => {
        if (!selectedEmail) return;
        try {
            setLocalLoading(true);
            const success = await deleteGmailEmail(selectedEmail.id, activeAccount, setLoading, setError);
            if (success) {
                setEmails(prev => prev.filter(email => email.id !== selectedEmail.id));
                closeEmailDetail();
            }
        } catch (error) {
            setError(`Failed to delete email: ${error.message}`);
        } finally {
            setLocalLoading(false);
        }
    };

    const handleReply = () => {
        setIsReplying(true);
        const sender = selectedEmail?.name || 'Unknown';
        const date = selectedEmail?.date || '';
        const time = selectedEmail?.time || '';
        const quoteHtml = `
          <div style="padding: 20px 0; font-family: Arial, sans-serif;">
            <div contenteditable="true" style="min-height: 120px; border-bottom: 1px solid #ddd; padding-bottom: 15px; margin-bottom: 15px;">
              Type your reply here...
            </div>
            <div style="padding: 10px; border-left: 2px solid #ccc; color: #666; margin-top: 10px;">
              <p>On ${date} at ${time}, ${sender} wrote:</p>
              <div>${selectedEmail?.body || selectedEmail?.message || ''}</div>
            </div>
          </div>
        `;
        setReplyContent(quoteHtml);
    };

    const sendReply = async () => {
        if (!selectedEmail || !replyContent) return;
        try {
            setLocalLoading(true);
            const success = await sendGmailReply(activeAccount, selectedEmail.threadId, selectedEmail.id, replyContent, setLoading, setError);
            if (success) {
                setIsReplying(false);
                setReplyContent('');
                try {
                    await fetchGmailEmails(activeAccount, setLoading, setEmails, setError);
                }
                catch (fetchError) { }
            }
        } catch (error) {
            setError(`Failed to send reply: ${error.message}`);
        } finally {
            setLocalLoading(false);
        }
    };

    const cancelReply = () => {
        setIsReplying(false);
        setReplyContent('');
    };

    const handleForward = () => {
        setIsForwarding(true);
        const forwardHtml = `
    <div style="padding: 20px 0; font-family: Arial, sans-serif;">
      <div contenteditable="true" style="min-height: 120px; border-bottom: 1px solid #ddd; padding-bottom: 15px; margin-bottom: 15px;">
        Type your message here...
      </div>
      <div style="padding: 10px; border-left: 2px solid #ccc; color: #666; margin-top: 10px;">
        <p>---------- Forwarded message ----------</p>
        <p>From: ${selectedEmail?.name || 'Unknown'} ${selectedEmail?.email ? `<${selectedEmail.email}>` : ''}</p>
        <p>Date: ${selectedEmail?.date || ''} at ${selectedEmail?.time || ''}</p>
        <p>Subject: ${selectedEmail?.subject || 'No Subject'}</p>
        <p>To: ${selectedEmail?.to || 'me'}</p>
        <div>${selectedEmail?.body || selectedEmail?.message || ''}</div>
      </div>
    </div>
  `;
        setForwardContent(forwardHtml);
    };

    const sendForward = async () => {
        if (!selectedEmail || !forwardContent || !forwardTo) return;
        try {
            setLocalLoading(true);
            const boundary = `boundary_${Date.now().toString(16)}`;
            const authInstance = window.gapi.auth2.getAuthInstance();
            const currentUser = authInstance.currentUser.get();
            const email = [
                `From: ${currentUser.getBasicProfile().getEmail()}`,
                `To: ${forwardTo}`,
                `Subject: Fwd: ${selectedEmail.subject}`,
                'MIME-Version: 1.0',
                `Content-Type: multipart/alternative; boundary="${boundary}"`,
                '',
                `--${boundary}`,
                'Content-Type: text/plain; charset=UTF-8',
                '',
                forwardContent.replace(/<[^>]*>/g, ''),
                '',
                `--${boundary}`,
                'Content-Type: text/html; charset=UTF-8',
                '',
                forwardContent,
                '',
                `--${boundary}--`
            ].join('\r\n');

            const encodedEmail = btoa(email)
                .replace(/\+/g, '-')
                .replace(/\//g, '_')
                .replace(/=+$/, '');

            await window.gapi.client.gmail.users.messages.send({
                userId: 'me',
                resource: {
                    raw: encodedEmail
                }
            });
            setIsForwarding(false);
            setForwardTo('');
            setForwardContent('');
        } catch (error) {
            setError(`Failed to forward email: ${error.message}`);
        } finally {
            setLocalLoading(false);
        }
    };

    const handleMarkAsUnread = async () => {
        if (!selectedEmail) return;
        try {
            setLocalLoading(true);
            await window.gapi.client.gmail.users.messages.modify({
                userId: 'me',
                id: selectedEmail.id,
                resource: {
                    addLabelIds: ['UNREAD'],
                    removeLabelIds: []
                }
            });
            setEmails(prev => prev.map(email =>
                email.id === selectedEmail.id
                    ? { ...email, isRead: false }
                    : email
            ));
            closeEmailDetail();
        } catch (error) {
            setError(`Failed to mark as unread: ${error.message}`);
        } finally {
            setLocalLoading(false);
        }
    };


    const cancelForward = () => {
        setIsForwarding(false);
        setForwardTo('');
        setForwardContent('');
    };

    useEffect(() => {
        const initializeAuth = async () => {
            if (authInitialized) return;
            try {
                setLoading(true);
                setLocalLoading(true);
                await initGoogleClient();
                await new Promise(resolve => setTimeout(resolve, 500));

                if (!gapi.auth2) {
                    throw new Error("Google Auth not initialized properly");
                }

                const authInstance = gapi.auth2.getAuthInstance();
                if (!authInstance) {
                    throw new Error("Failed to get Google Auth instance");
                }

                const googleUser = authInstance.currentUser.get();
                if (googleUser && googleUser.isSignedIn()) {
                    const profile = googleUser.getBasicProfile();
                    const googleAccount = {
                        provider: AuthProvider.GOOGLE,
                        id: profile.getId(),
                        name: profile.getName(),
                        email: profile.getEmail(),
                        username: profile.getEmail(),
                        photoURL: profile.getImageUrl(),
                    };
                    setUser(googleAccount);
                    setIsConnected(true);
                    if (authManager) {
                        const existingAccounts = authManager.getAccounts(AuthProvider.GOOGLE);
                        const accountExists = existingAccounts.some(acc =>
                            acc.userData && acc.userData.email === googleAccount.email
                        );
                        if (!accountExists) {
                            if (!canAddMoreAccounts()) {
                                setShowUpgradePrompt(true);
                                setIsInitialized(true);
                                setLoading(false);
                                setLocalLoading(false);
                                setAuthInitialized(true);
                                return;
                            }
                            try {
                                authManager.addAccount(AuthProvider.GOOGLE, googleAccount);
                            } catch (error) {
                                if (error.message && error.message.includes("Account limit reached")) {
                                    setShowUpgradePrompt(true);
                                    setIsInitialized(true);
                                    setLoading(false);
                                    setLocalLoading(false);
                                    setAuthInitialized(true);
                                    return;
                                }
                                throw error;
                            }
                        }
                    }
                    setAccounts(prev => {
                        const exists = prev.some(
                            acc => acc.provider === AuthProvider.GOOGLE && acc.username === googleAccount.username
                        );
                        return exists ? prev : [...prev, googleAccount];
                    });

                    setActiveAccount(googleAccount);
                    try {
                        await fetchGmailEmails(googleAccount, setLoading, setEmails, setError, 'inbox');
                    } catch (fetchError) {
                        setError(`Failed to fetch emails: ${fetchError.message}`);
                    }
                } else {
                    setIsConnected(false);
                }
            } catch (error) {
                if (error.message && error.message.includes("Account limit reached")) {
                    setShowUpgradePrompt(true);
                } else {
                    setError(`Google account handling failed: ${error.message}`);
                }
                setIsConnected(false);
            } finally {
                setIsInitialized(true);
                setLoading(false);
                setLocalLoading(false);
                setAuthInitialized(true);
            }
        };
        initializeAuth();//eslint-disable-next-line
    }, [, authInitialized]);

    const handleGoogleLoginClick = async () => {
        try {
            if (!canAddMoreAccounts()) {
                setShowUpgradePrompt(true);
                return;
            }
            setLoading(true);
            setLocalLoading(true);
            await initGoogleClient();

            if (authManager) {
                const result = await handleGoogleLogin(authManager, setUser, setLoading, setError, setIsGoogleLoggedIn);
                if (result) {
                    authManager.setActiveAccount(result.id);
                    authManager.notifyStateChange();
                    setUser(result);
                    setIsConnected(true);
                    setEmails([]);
                    try {
                        await fetchGmailEmails(result, setLoading, setEmails, setError, 'inbox');
                    } catch (fetchError) {
                        setError(`Failed to fetch emails: ${fetchError.message}`);
                    }

                    forceUpdate();
                    setAccounts(prev => {
                        const exists = prev.some(acc =>
                            acc.provider === AuthProvider.GOOGLE && acc.username === result.username
                        );
                        return exists ? prev : [...prev, result];
                    });
                    setActiveAccount(result);
                }
            } else {
                const result = await handleGoogleLogin(null, setUser, setLoading, setError, setIsGoogleLoggedIn);
                if (result) {
                    setUser(result);
                    setIsConnected(true);
                    setEmails([]);

                    try {
                        await fetchGmailEmails(result, setLoading, setEmails, setError, 'inbox');
                    } catch (fetchError) {
                        setError(`Failed to fetch emails: ${fetchError.message}`);
                    }
                    forceUpdate();
                }
            }
        } catch (error) {
            if (error.message && error.message.includes("Account limit reached")) {
                setShowUpgradePrompt(true);
            } else {
                setError(`Google login failed: ${error.message}`);
            }
        } finally {
            setLoading(false);
            setLocalLoading(false);
        }
    };

    const handleAddAccount = () => {
        if (!canAddMoreAccounts()) {
            setShowUpgradePrompt(true);
            setShowDropdown(false);
            return;
        }
        setShowAccountOptions(true);
        setShowDropdown(false);
    };

    const handleEmailClick = (email) => {
        setSelectedEmail(email);
        setShowEmailDetail(true);
    };

    const closeEmailDetail = () => {
        setShowEmailDetail(false);
        setSelectedEmail(null);
    };

    const handleBackdropClick = (e) => {
        if (e.target === backdropRef.current) {
            closeEmailDetail();
        }
    };

    useEffect(() => {
        const handleEscKey = (e) => {
            if (e.key === 'Escape' && showEmailDetail) {
                closeEmailDetail();
            }
        };
        window.addEventListener('keydown', handleEscKey);
        return () => {
            window.removeEventListener('keydown', handleEscKey);
        };
    }, [showEmailDetail]);

    useEffect(() => {
        function handleClickOutside(event) {
            if (showDropdown && !event.target.closest('.dropdown-container')) {
                setShowDropdown(false);
            }
            if (showAccountOptions && !event.target.closest('.account-options-container')) {
                setShowAccountOptions(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showDropdown, showAccountOptions]);

    const ErrorMessage = () => {
        if (!error) return null;
        return (
            <div className={`p-4 mb-4 rounded-md ${appMode === AppMode.LIGHT ? 'bg-red-50 text-red-800' : 'bg-red-900 text-red-200'}`}>
                <div className="flex items-center">
                    <i className="bi bi-exclamation-triangle-fill mr-2"></i>
                    <span>{error}</span>
                </div>
                <button className={`mt-2 text-sm ${appMode === AppMode.LIGHT ? 'text-red-600' : 'text-red-300'}`} onClick={() => setError(null)}>Dismiss</button>
            </div>
        );
    };

    const GmailFallback = () => (
        <div className="flex flex-col items-center justify-center h-full p-6 text-center">
            <div className="mb-4">
                <i className="bi bi-envelope-x text-5xl text-gray-400"></i>
            </div>
            <h2 className={`text-xl font-semibold mb-2 ${appMode === AppMode.LIGHT ? 'text-gray-800' : 'text-gray-200'}`}>No Gmail Account Connected</h2>
            <p className={`mb-6 ${appMode === AppMode.LIGHT ? 'text-gray-600' : 'text-gray-400'}`}>Connect your Gmail account to see your emails here.</p>
            <button onClick={handleGoogleLoginClick} className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md flex items-center">
                <i className="bi bi-google me-2"></i>Connect Gmail
            </button>
        </div>
    );

    const EmailDetailModal = () => {
        if (!selectedEmail) return null;
        const hasHtmlContent = selectedEmail.body && (selectedEmail.body.includes('<html') || selectedEmail.body.includes('<div') || selectedEmail.body.includes('<p'));
        return (
            <div ref={backdropRef} className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center opacity-0 transition-opacity duration-300" style={{ display: 'none' }} onClick={handleBackdropClick}>
                <div className={`relative w-full max-w-3xl max-h-[90vh] overflow-hidden rounded-lg shadow-2xl transform transition-transform duration-300 ${appMode === AppMode.LIGHT ? 'bg-white' : 'bg-gray-800'}`} onClick={(e) => e.stopPropagation()}>
                    {isReplying
                        ? <div className={`p-4 border-b ${appMode === AppMode.LIGHT ? 'border-gray-200' : 'border-gray-700'} flex justify-between items-center sticky top-0 z-10 ${appMode === AppMode.LIGHT ? 'bg-white' : 'bg-gray-800'}`}>
                            <h2 className={`font-semibold ${appMode === AppMode.LIGHT ? 'text-gray-800' : 'text-white'}`}>Reply to: {selectedEmail.subject}</h2>
                            <button onClick={cancelReply} className="text-gray-500 hover:text-gray-700">
                                <i className="bi bi-x-lg"></i>
                            </button>
                        </div>
                        : isForwarding
                            ? <div className={`p-4 border-b ${appMode === AppMode.LIGHT ? 'border-gray-200' : 'border-gray-700'} flex justify-between items-center sticky top-0 z-10 ${appMode === AppMode.LIGHT ? 'bg-white' : 'bg-gray-800'}`}>
                                <h2 className={`font-semibold ${appMode === AppMode.LIGHT ? 'text-gray-800' : 'text-white'}`}>Forward: {selectedEmail.subject}</h2>
                                <button onClick={cancelForward} className="text-gray-500 hover:text-gray-700">
                                    <i className="bi bi-x-lg"></i>
                                </button>
                            </div>
                            : <div className={`p-4 border-b ${appMode === AppMode.LIGHT ? 'border-gray-200' : 'border-gray-700'} flex justify-between items-center sticky top-0 z-10 ${appMode === AppMode.LIGHT ? 'bg-white' : 'bg-gray-800'}`}>
                                <div className="flex items-center space-x-2 overflow-hidden">
                                    <div className="flex-shrink-0">
                                        <img src={selectedEmail.imageUrl} className="h-10 w-10 rounded-full object-cover" alt='' />
                                    </div>
                                    <div className="overflow-hidden">
                                        <h2 className={`font-semibold truncate ${appMode === AppMode.LIGHT ? 'text-gray-800' : 'text-white'}`}>{selectedEmail.subject}</h2>
                                        <p className={`text-sm truncate ${appMode === AppMode.LIGHT ? 'text-gray-600' : 'text-gray-300'}`}>{selectedEmail.name} {selectedEmail.email ? `<${selectedEmail.email}>` : ''}</p>
                                    </div>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <button className={`p-2 rounded-full hover:${appMode === AppMode.LIGHT ? 'bg-gray-100' : 'bg-gray-700'} transition-colors`} title="Archive" onClick={() => handleDeleteEmail()}>
                                        <i className="bi bi-archive"></i>
                                    </button>
                                    <button className={`p-2 rounded-full hover:${appMode === AppMode.LIGHT ? 'bg-gray-100' : 'bg-gray-700'} transition-colors`} title="Star">
                                        <i className="bi bi-star"></i>
                                    </button>
                                    <button onClick={closeEmailDetail} className={`p-2 rounded-full hover:${appMode === AppMode.LIGHT ? 'bg-gray-100' : 'bg-gray-700'} transition-colors`} title="Close">
                                        <i className="bi bi-x-lg"></i>
                                    </button>
                                </div>
                            </div>
                    }
                    {isReplying
                        ? <div className={`p-6 overflow-y-auto ${appMode === AppMode.LIGHT ? 'bg-white' : 'bg-gray-800'}`} style={{ maxHeight: 'calc(90vh - 130px)' }}>
                            <div className="mb-4">
                                <label className={`block text-sm font-medium mb-2 ${appMode === AppMode.LIGHT ? 'text-gray-700' : 'text-gray-300'}`}>
                                    To: {selectedEmail.name} {selectedEmail.email ? `<${selectedEmail.email}>` : ''}
                                </label>
                            </div>
                            <div className={`border rounded-md p-4 min-h-[300px] ${appMode === AppMode.LIGHT ? 'border-gray-300' : 'border-gray-600'}`} dangerouslySetInnerHTML={{ __html: replyContent }} contentEditable onInput={(e) => setReplyContent(e.currentTarget.innerHTML)}></div>
                            <div className="mt-4 flex justify-end space-x-3">
                                <button onClick={cancelReply} className={`px-4 py-2 rounded-md ${appMode === AppMode.LIGHT ? 'bg-gray-200 hover:bg-gray-300 text-gray-700' : 'bg-gray-700 hover:bg-gray-600 text-white'}`}>Cancel</button>
                                <button onClick={sendReply} className="px-4 py-2 rounded-md bg-red-600 hover:bg-red-700 text-white">Send</button>
                            </div>
                        </div>
                        : isForwarding
                            ? <div className={`p-6 overflow-y-auto ${appMode === AppMode.LIGHT ? 'bg-white' : 'bg-gray-800'}`} style={{ maxHeight: 'calc(90vh - 130px)' }}>
                                <div className="mb-4">
                                    <label className={`block text-sm font-medium mb-2 ${appMode === AppMode.LIGHT ? 'text-gray-700' : 'text-gray-300'}`}>To:</label>
                                    <input type="email" value={forwardTo} onChange={(e) => setForwardTo(e.target.value)} className={`w-full px-3 py-2 border rounded-md ${appMode === AppMode.LIGHT ? 'border-gray-300' : 'border-gray-600'}`} placeholder="recipient@example.com" />
                                </div>
                                <div className={`border rounded-md p-4 min-h-[300px] ${appMode === AppMode.LIGHT ? 'border-gray-300' : 'border-gray-600'}`} dangerouslySetInnerHTML={{ __html: forwardContent }} contentEditable onInput={(e) => setForwardContent(e.currentTarget.innerHTML)}></div>
                                <div className="mt-4 flex justify-end space-x-3">
                                    <button onClick={cancelForward} className={`px-4 py-2 rounded-md ${appMode === AppMode.LIGHT ? 'bg-gray-200 hover:bg-gray-300 text-gray-700' : 'bg-gray-700 hover:bg-gray-600 text-white'}`}>Cancel</button>
                                    <button onClick={sendForward} disabled={!forwardTo} className={`px-4 py-2 rounded-md ${forwardTo ? 'bg-red-600 hover:bg-red-700' : 'bg-red-400 cursor-not-allowed'} text-white`}>Send</button>
                                </div>
                            </div>
                            : <>
                                <div className={`overflow-y-auto ${appMode === AppMode.LIGHT ? 'text-gray-700' : 'text-gray-200'}`} style={{ maxHeight: 'calc(90vh - 130px)' }}>
                                    <div className={`px-6 py-4 ${appMode === AppMode.LIGHT ? 'bg-gray-50' : 'bg-gray-750'}`}>
                                        <div className="flex justify-between items-center">
                                            <div>
                                                <span className="text-sm font-medium">Date: </span>
                                                <span className="text-sm">{selectedEmail.date} {selectedEmail.time}</span>
                                            </div>
                                            <div className="flex space-x-2">
                                                <span className={`px-2 py-1 text-xs rounded-full ${appMode === AppMode.LIGHT ? 'bg-blue-100 text-blue-800' : 'bg-blue-900 text-blue-200'}`}>Inbox</span>
                                                {selectedEmail.fullEmail?.labelIds?.includes('IMPORTANT') && <span className={`px-2 py-1 text-xs rounded-full ${appMode === AppMode.LIGHT ? 'bg-red-100 text-red-800' : 'bg-red-900 text-red-200'}`}>Important</span>}
                                            </div>
                                        </div>
                                        {selectedEmail.to && (
                                            <div className="mt-2">
                                                <span className="text-sm font-medium">To: </span>
                                                <span className="text-sm">{selectedEmail.to}</span>
                                            </div>
                                        )}
                                        {selectedEmail.cc && (
                                            <div className="mt-1">
                                                <span className="text-sm font-medium">CC: </span>
                                                <span className="text-sm">{selectedEmail.cc}</span>
                                            </div>
                                        )}
                                    </div>
                                    <div className="p-6">
                                        {hasHtmlContent
                                            ? <div className="email-html-content" dangerouslySetInnerHTML={{ __html: sanitizeHtml(selectedEmail.body) }} />
                                            : <div className="whitespace-pre-wrap">{selectedEmail.message || 'No message content'}</div>
                                        }
                                        {selectedEmail.attachments && selectedEmail.attachments.length > 0 && (
                                            <div className={`mt-6 p-4 rounded-md ${appMode === AppMode.LIGHT ? 'bg-gray-50' : 'bg-gray-750'}`}>
                                                <h3 className="font-medium mb-3">Attachments</h3>
                                                <div className="flex flex-wrap gap-3">
                                                    {selectedEmail.attachments.map((attachment, index) => (
                                                        <div key={index} className={`flex items-center p-2 rounded-md ${appMode === AppMode.LIGHT ? 'bg-white border border-gray-200' : 'bg-gray-700 border border-gray-600'}`}>
                                                            <i className={`bi bi-file-earmark${getFileIconSuffix(attachment.name)} text-2xl mr-2`}></i>
                                                            <div>
                                                                <p className="text-sm font-medium">{attachment.name}</p>
                                                                <p className="text-xs text-gray-500">{formatFileSize(attachment.size)}</p>
                                                            </div>
                                                            <button className={`ml-3 p-1 rounded-full ${appMode === AppMode.LIGHT ? 'hover:bg-gray-100' : 'hover:bg-gray-600'}`} title="Download">
                                                                <i className="bi bi-download"></i>
                                                            </button>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className={`p-4 border-t ${appMode === AppMode.LIGHT ? 'border-gray-200 bg-white' : 'border-gray-700 bg-gray-800'} sticky bottom-0 flex justify-between items-center`}>
                                    <div>
                                        <button onClick={handleDeleteEmail} className={`px-3 py-2 rounded-md ${appMode === AppMode.LIGHT ? 'hover:bg-gray-100' : 'hover:bg-gray-700'} transition-colors`} title="Delete">
                                            <i className="bi bi-trash text-red-500"></i>
                                        </button>
                                        <button onClick={handleMarkAsUnread} className={`px-3 py-2 rounded-md ${appMode === AppMode.LIGHT ? 'hover:bg-gray-100' : 'hover:bg-gray-700'} transition-colors`} title="Mark as unread">
                                            <i className="bi bi-envelope"></i>
                                        </button>
                                        <button className={`px-3 py-2 rounded-md ${appMode === AppMode.LIGHT ? 'hover:bg-gray-100' : 'hover:bg-gray-700'} transition-colors`} title="Label">
                                            <i className="bi bi-tag"></i>
                                        </button>
                                    </div>
                                    <div className="flex space-x-2">
                                        <button onClick={handleForward} className={`px-4 py-2 rounded-md ${appMode === AppMode.LIGHT ? 'bg-gray-200 hover:bg-gray-300 text-gray-700' : 'bg-gray-700 hover:bg-gray-600 text-white'} transition-colors`}>Forward</button>
                                        <button onClick={handleReply} className="px-4 py-2 rounded-md bg-red-600 hover:bg-red-700 text-white transition-colors flex items-center">
                                            <i className="bi bi-reply me-2"></i> Reply
                                        </button>
                                    </div>
                                </div>
                            </>
                    }
                </div>
            </div>
        );
    };

    const getFileIconSuffix = (filename) => {
        if (!filename) return '';
        const ext = filename.split('.').pop().toLowerCase();
        const iconMap = {
            'pdf': '-pdf',
            'doc': '-word',
            'docx': '-word',
            'xls': '-excel',
            'xlsx': '-excel',
            'ppt': '-ppt',
            'pptx': '-ppt',
            'jpg': '-image',
            'jpeg': '-image',
            'png': '-image',
            'gif': '-image',
            'zip': '-zip',
            'rar': '-zip',
            'txt': '-text',
        };
        return iconMap[ext] || '';
    };

    const formatFileSize = (bytes) => {
        if (!bytes || isNaN(bytes)) return 'Unknown size';
        const units = ['B', 'KB', 'MB', 'GB'];
        let size = bytes;
        let unitIndex = 0;
        while (size >= 1024 && unitIndex < units.length - 1) {
            size /= 1024;
            unitIndex++;
        }

        return `${size.toFixed(1)} ${units[unitIndex]}`;
    };

    const handleRetry = () => {
        setError(null);
        setAuthInitialized(false);
    };

    return (
        <div className="pr-2 pl-1 py-4 w-1/2 h-full">
            <div className={`h-full ${appMode === AppMode.LIGHT ? 'bg-white' : dark_mode_primary} rounded-xl overflow-y-auto`}>
                <div className="pr-3 w-full flex justify-between relative sticky top-0 z-10 py-3 px-3 bg-inherit">
                    <h1 className="font-medium">Gmail</h1>
                    <button onClick={() => setShowDropdown(!showDropdown)} className="bi bi-three-dots-vertical"></button>
                    {showDropdown && (
                        <div className={`absolute right-0 top-8 w-48 rounded-md shadow-lg z-10 ${appMode === AppMode.LIGHT ? 'bg-white' : 'bg-gray-800'} ring-1 ring-black ring-opacity-5 dropdown-container`}>
                            <ul>
                                <li onClick={handleAddAccount} className={`p-4 text-sm cursor-pointer hover:${appMode === AppMode.LIGHT ? 'bg-gray-100' : 'bg-gray-700'}`}><i className="mr-2 bi bi-plus-lg"></i> Add Account</li>
                                <li className={`px-4 py-3 text-sm cursor-pointer hover:${appMode === AppMode.LIGHT ? 'bg-gray-100' : 'bg-gray-700'}`}><i className="mr-2 bi bi-send"></i> Sent</li>
                                <li className={`px-4 py-3 text-sm cursor-pointer hover:${appMode === AppMode.LIGHT ? 'bg-gray-100' : 'bg-gray-700'}`}><i className="mr-2 bi bi-archive"></i> Junk</li>
                                <li className={`px-4 py-3 text-sm cursor-pointer hover:${appMode === AppMode.LIGHT ? 'bg-gray-100' : 'bg-gray-700'} text-red-500`}><i className="mr-2 bi bi-trash3"></i> Remove account</li>
                            </ul>
                        </div>
                    )}
                </div>
                <div className="py-5 h-[calc(100%-40px)]">
                    {error && (
                        <div className="px-4 mb-4">
                            <ErrorMessage />
                            <button onClick={handleRetry} className="mt-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md">
                                <i className="bi bi-arrow-clockwise me-2"></i> Retry Connection
                            </button>
                        </div>
                    )}

                    {localLoading
                        ? <div className="flex justify-center items-center h-full">
                            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-red-500"></div>
                        </div>
                        : !isConnected
                            ? <GmailFallback />
                            : emails.length > 0
                                ? emails.map((email, index) => (
                                    <React.Fragment key={index}>
                                        <div className={`pl-2 py-3 flex cursor-pointer ${appMode === AppMode.LIGHT ? 'hover:bg-gray-100' : 'hover:bg-gray-900'} transition-colors ${email.isRead ? '' : 'font-semibold'}`} onClick={() => handleEmailClick(email)}>
                                            <div className="w-1/6 overflow-hidden">
                                                <img src={email.imageUrl} className="h-12 w-12 rounded-full object-cover" alt='' />
                                            </div>
                                            <div className="w-5/6 ml-2 truncate overflow-elipsis">
                                                <h1 className="truncate">{email.name}</h1>
                                                <h2 className="text-ellipsis whitespace-nowrap">{email.subject}</h2>
                                                <p className="text-xs text-gray-500 truncate">{email.bodyPreview}</p>
                                            </div>
                                        </div>
                                        <hr className="mx-4" />
                                    </React.Fragment>
                                ))
                                : <div className="flex flex-col items-center justify-center h-full">
                                    <i className="bi bi-inbox text-4xl text-gray-400 mb-2"></i>
                                    <p className={`${appMode === AppMode.LIGHT ? 'text-gray-600' : 'text-gray-400'}`}>No emails found</p>
                                </div>
                    }
                </div>
            </div>
            <EmailDetailModal />
            {showUpgradePrompt && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className={`w-96 rounded-lg shadow-lg ${appMode === AppMode.LIGHT ? 'bg-white' : 'bg-gray-800'}`}>
                        <div className="p-4 border-b flex justify-between items-center">
                            <h2 className={`font-semibold ${appMode === AppMode.LIGHT ? 'text-gray-800' : 'text-white'}`}>Account Limit Reached</h2>
                            <button onClick={() => setShowUpgradePrompt(false)} className="text-gray-500 hover:text-gray-700">
                                <i className="bi bi-x-lg"></i>
                            </button>
                        </div>
                        <div className="p-6">
                            <div className="text-center mb-4">
                                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-yellow-100 text-yellow-500 mb-4">
                                    <i className="bi bi-exclamation-triangle text-2xl"></i>
                                </div>
                                <h3 className={`text-lg font-medium ${appMode === AppMode.LIGHT ? 'text-gray-800' : 'text-white'}`}>Subscription Limit Reached</h3>
                                <p className={`mt-2 ${appMode === AppMode.LIGHT ? 'text-gray-600' : 'text-gray-300'}`}>You've reached the maximum number of accounts for your current {subscriptionInfo?.name || 'Free'} plan.</p>
                            </div>
                            <div className="flex justify-center space-x-3">
                                <button onClick={() => setShowUpgradePrompt(false)} className={`px-4 py-2 rounded-md ${appMode === AppMode.LIGHT ? 'bg-gray-200 hover:bg-gray-300 text-gray-700' : 'bg-gray-700 hover:bg-gray-600 text-white'}`}>Cancel</button>
                                <button onClick={handleUpgradeRedirect} className={`px-4 py-2 rounded-md bg-purple-600 hover:bg-purple-700 text-white`}>Upgrade Now</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {showAccountOptions && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className={`w-96 rounded-lg shadow-lg account-options-container ${appMode === AppMode.LIGHT ? 'bg-white' : 'bg-gray-800'}`}>
                        <div className="p-4 border-b flex justify-between items-center">
                            <h2 className={`font-semibold ${appMode === AppMode.LIGHT ? 'text-gray-800' : 'text-white'}`}>Connect Another Account</h2>
                            <button onClick={() => setShowAccountOptions(false)} className="text-gray-500 hover:text-gray-700">
                                <i className="bi bi-x-lg"></i>
                            </button>
                        </div>
                        <div className="p-4">
                            <div className="flex justify-center">
                                <button onClick={() => { setShowAccountOptions(false); handleGoogleLoginClick(); }} className={`p-3 rounded-md flex flex-col items-center w-full ${appMode === AppMode.LIGHT ? 'bg-red-50 hover:bg-red-100 text-red-600' : 'bg-red-900 hover:bg-red-800 text-red-300'}`}>
                                    <i className="bi bi-google text-xl mb-2"></i>
                                    <span className="text-sm">Google Account</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export function WeekCalendar({ appMode, events, setEvents, setError, setActiveTab }) {//eslint-disable-next-line
    const [loading, setLoading] = useState(false);
    const [selectedDate, setSelectedDate] = useState(null);

    useEffect(() => {
        const fetchAllCalendarEvents = async () => {
            try {
                setLoading && setLoading(true);
                const loggedInAccounts = authManagerInstance.getLoggedInAccounts();
                if (loggedInAccounts.length === 0) {
                    setEvents && setEvents([]);
                    return;
                }
                const allEvents = [];
                for (const acct of loggedInAccounts) {
                    try {
                        if (acct.provider === AuthProvider.GOOGLE) {
                            const googleEvents = await fetchGoogleCalendar(acct, null, setError);
                            if (googleEvents && googleEvents.length > 0) {
                                allEvents.push(...googleEvents);
                            }
                        } else if (acct.provider === AuthProvider.MICROSOFT) {
                            const msEvents = await fetchCalendarEvents(acct, null, null, setError);
                            if (msEvents && msEvents.length > 0) {
                                allEvents.push(...msEvents);
                            }
                        }
                    } catch (error) { }
                }
                allEvents.sort((a, b) => new Date(a.startDateTime) - new Date(b.startDateTime));
                setEvents && setEvents(allEvents);
            } catch (error) {
                setError && setError(`Failed to fetch calendar events: ${error.message}`);
            } finally {
                setLoading && setLoading(false);
            }
        };
        fetchAllCalendarEvents();
    }, [setLoading, setEvents, setError]);

    const handleDateClick = (dateString) => {
        if (selectedDate === dateString) {
            setSelectedDate(null);
        } else {
            setSelectedDate(dateString);
        }
    };

    const generateWeekDays = () => {
        const days = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
        const today = new Date();
        const currentDay = today.getDay();
        const sunday = new Date(today);
        sunday.setDate(today.getDate() - currentDay);
        return days.map((day, index) => {
            const date = new Date(sunday);
            date.setDate(sunday.getDate() + index);
            const dateNum = date.getDate();
            const dateString = date.toDateString();
            const isToday = dateString === today.toDateString();
            const isSelected = dateString === selectedDate;

            return (
                <div key={index} className={`p-2 flex flex-col items-center rounded-full cursor-pointer  ${isToday ? appMode === AppMode.LIGHT ? 'bg-black text-white' : 'bg-black' : ''} ${isSelected && !isToday ? 'bg-blue-500 bg-opacity-10' : ''}`} onClick={() => handleDateClick(dateString)}>
                    <div className="text-xs mb-1">{day}</div>
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center 
                        ${isToday ? 'bg-blue-600 text-white' : isSelected ? 'bg-blue-600 text-white' : appMode === AppMode.LIGHT ? 'bg-gray-200 text-black' : 'bg-black text-white'}`}> {dateNum}
                    </div>
                </div>
            );
        });
    };

    const getWeekEventsByDay = () => {
        if (!events || events.length === 0) return {};
        const today = new Date();
        const currentDay = today.getDay();
        const sunday = new Date(today);
        sunday.setDate(today.getDate() - currentDay);
        sunday.setHours(0, 0, 0, 0);
        const endOfWeek = new Date(sunday);
        endOfWeek.setDate(sunday.getDate() + 6);
        endOfWeek.setHours(23, 59, 59, 999);
        const weekEvents = events.filter(event => {
            const eventDate = new Date(event.startDateTime);
            return eventDate >= sunday && eventDate <= endOfWeek;
        }).sort((a, b) => new Date(a.startDateTime) - new Date(b.startDateTime));
        const groupedEvents = {};
        for (let i = 0; i < 7; i++) {
            const day = new Date(sunday);
            day.setDate(sunday.getDate() + i);
            const dayString = day.toDateString();
            groupedEvents[dayString] = [];
        }
        weekEvents.forEach(event => {
            const eventDay = new Date(event.startDateTime).toDateString();
            if (groupedEvents[eventDay]) {
                groupedEvents[eventDay].push(event);
            }
        });
        return groupedEvents;
    };

    const weekEventsByDay = getWeekEventsByDay();
    const filteredEvents = () => {
        if (!selectedDate) {
            return weekEventsByDay;
        }
        const filtered = {};
        Object.keys(weekEventsByDay).forEach(day => {
            if (day === selectedDate) {
                filtered[day] = weekEventsByDay[day];
            } else {
                filtered[day] = [];
            }
        });
        return filtered;
    };
    const displayEvents = filteredEvents();

    return (
        <div className={`p-1 h-1/3`}>
            <div className={`py-3.5 h-full ${appMode === AppMode.LIGHT ? light_mode_secondary : dark_mode_secondary} rounded-lg overflow-y-auto`}>
                <div className="px-3 flex justify-between items-center mb-4">
                    <h1 className='font-medium'>
                        {selectedDate
                            ? `Calendar (${new Date(selectedDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })})`
                            : 'Calendar (This Week)'}
                    </h1>
                    <div className="flex items-center">
                        {/* {selectedDate && <button className="mr-2 text-sm text-blue-500 hover:text-blue-700" onClick={() => setSelectedDate(null)}>Reset</button>} */}
                        <button className="text-sm bi bi-arrows-angle-expand" onClick={() => { setActiveTab(6) }}></button>
                    </div>
                </div>
                <div className="h-20 flex overflow-x-auto justify-between mb-5">{generateWeekDays()}</div>
                <div className="px-3" style={{ maxHeight: '200px' }}>
                    {Object.keys(displayEvents).some(day => displayEvents[day].length > 0)
                        ? Object.entries(displayEvents).map(([dayString, dayEvents]) => {
                            if (dayEvents.length === 0) return null;
                            const date = new Date(dayString);
                            const dayName = date.toLocaleDateString(undefined, { weekday: 'short' });
                            const monthName = date.toLocaleDateString(undefined, { month: 'short' });
                            const dayNum = date.getDate();
                            return (
                                <div key={dayString} className="mb-3">
                                    <div className="font-medium text-sm mb-1">{`${dayName}, ${monthName} ${dayNum}`}</div>
                                    <div className="space-y-2">
                                        {dayEvents.map(event => (
                                            <div key={event.id} className={`p-2 ${appMode === AppMode.LIGHT ? 'bg-gray-400' : 'bg-gray-800'} rounded`}>
                                                <div className="w-full flex justify-between items-center">
                                                    <div className="font-medium">{event.title || event.subject}</div>
                                                    <div className={`text-xs font-semibold ${appMode === AppMode.LIGHT ? 'text-gray-100' : 'text-gray-400'}`}>{event.startTime} - {event.endTime}</div>
                                                </div>
                                                {event.location && <div className={`text-xs ${appMode === AppMode.LIGHT ? 'text-gray-100' : 'text-gray-400'}`}>📍 {event.location}</div>}
                                                <div className={`text-xs ${appMode === AppMode.LIGHT ? 'text-gray-100' : 'text-gray-400'} mt-1`}>{event.provider === AuthProvider.GOOGLE ? 'Google Calendar' : 'Microsoft Calendar'}</div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            );
                        }).filter(Boolean)
                        : <div className="text-center text-gray-500">{selectedDate ? "No events on this day" : "No events this week"}</div>
                    }
                </div>
            </div>
        </div>
    );
}

export function TaskOverview({ appMode, setActiveTab }) {
    const [teams, setTeams] = useState([]);
    const [tasks, setTasks] = useState([]);
    const [newNote, setNewNote] = useState('');
    const [progress, setProgress] = useState(0);
    const [todayTask, setTodayTask] = useState(null);
    const [noteType, setNoteType] = useState('Note');
    const [showModal, setShowModal] = useState(false);
    const [isHovered, setIsHovered] = useState(false);//eslint-disable-next-line
    const [forceUpdate, setForceUpdate] = useState(0);
    const [justCompleted, setJustCompleted] = useState(false);
    const [totalTasksCount, setTotalTasksCount] = useState(0);
    const [showNoteEditor, setShowNoteEditor] = useState(false);
    const [completedTasksCount, setCompletedTasksCount] = useState(0);
    const [reminderDateTime, setReminderDateTime] = useState(new Date());

    useEffect(() => {
        if (totalTasksCount > 0) {
            const newProgress = Math.round((completedTasksCount / totalTasksCount) * 100);
            setProgress(newProgress);
        } else {
            setProgress(0);
        }
    }, [completedTasksCount, totalTasksCount, justCompleted]);

    useEffect(() => {
        const fetchTeams = async () => {
            try {
                if (!auth.currentUser) return;
                const userTeamsRef = ref(realtime, 'teams');
                const teamsSnapshot = await get(userTeamsRef);
                const teamsData = teamsSnapshot.val() || {};
                const teamsList = Object.entries(teamsData).map(([id, data]) => ({
                    id,
                    ...data
                })).filter(team => team.members && team.members.includes(auth.currentUser.uid));
                setTeams(teamsList);
            } catch (error) { }
        };
        fetchTeams();
    }, []);

    useEffect(() => {
        const fetchTodayTasks = async () => {
            try {
                const today = new Date(2025, 2, 12);
                today.setHours(0, 0, 0, 0);
                const tomorrow = new Date(today);
                tomorrow.setDate(tomorrow.getDate() + 1);
                const userTasksQuery = query(
                    collection(firestore, 'tasks'),
                    where("assignedTo", "==", auth.currentUser.uid),
                    where("deadline", ">=", today.toISOString().split('T')[0]),
                    where("deadline", "<", tomorrow.toISOString().split('T')[0]),
                    where("status", "!=", TaskStatus.DONE),
                    orderBy("deadline"),
                    orderBy("priority")
                );

                const taskSnapshot = await getDocs(userTasksQuery);
                const firestoreTasks = taskSnapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data(),
                    source: 'firestore',
                }));
                let realtimeTasks = [];
                if (teams.length > 0) {
                    const todayStr = today.toISOString().split('T')[0];
                    const tomorrowStr = tomorrow.toISOString().split('T')[0];
                    for (const team of teams) {
                        const teamTasksRef = ref(realtime, `teams/${team.id}/tasks`);
                        const teamTasksSnapshot = await get(teamTasksRef);
                        const teamTasksData = teamTasksSnapshot.val() || {};
                        const teamTasks = Object.entries(teamTasksData)
                            .map(([id, data]) => ({
                                id,
                                ...data,
                                teamId: team.id,
                                source: 'realtime',
                            }))
                            .filter(task =>
                                task.assignedTo === auth.currentUser.uid &&
                                task.status !== TaskStatus.DONE &&
                                task.deadline &&
                                task.deadline.substring(0, 10) >= todayStr &&
                                task.deadline.substring(0, 10) < tomorrowStr
                            );
                        realtimeTasks = [...realtimeTasks, ...teamTasks];
                    }
                }
                const allTasks = [...firestoreTasks, ...realtimeTasks].sort((a, b) => {
                    const aDeadline = typeof a.deadline === 'string'
                        ? a.deadline
                        : a.deadline.toDate ? a.deadline.toDate().toISOString() : "";
                    const bDeadline = typeof b.deadline === 'string'
                        ? b.deadline
                        : b.deadline.toDate ? b.deadline.toDate().toISOString() : "";
                    const deadlineComparison = aDeadline.localeCompare(bDeadline);
                    if (deadlineComparison !== 0) return deadlineComparison;
                    const priorityOrder = { 'HIGH': 0, 'MEDIUM': 1, 'LOW': 2 };
                    return (priorityOrder[a.priority || 'LOW'] || 2) - (priorityOrder[b.priority || 'LOW'] || 2);
                });
                if (allTasks.length > 0) {
                    const task = allTasks[0];
                    const teamName = task.source === 'realtime' && task.teamId
                        ? teams.find(team => team.id === task.teamId)?.name
                        : null;
                    setTodayTask({
                        ...task,
                        teamName: teamName || "Personal Task",
                    });
                    const totalFirestoreQuery = query(
                        collection(firestore, 'tasks'),
                        where("assignedTo", "==", auth.currentUser.uid),
                        where("deadline", ">=", today.toISOString().split('T')[0]),
                        where("deadline", "<", tomorrow.toISOString().split('T')[0])
                    );
                    const totalSnapshot = await getDocs(totalFirestoreQuery);
                    let totalTasks = totalSnapshot.docs.length;
                    const completedFirestoreQuery = query(
                        collection(firestore, 'tasks'),
                        where("assignedTo", "==", auth.currentUser.uid),
                        where("deadline", ">=", today.toISOString().split('T')[0]),
                        where("deadline", "<", tomorrow.toISOString().split('T')[0]),
                        where("status", "==", TaskStatus.DONE)
                    );
                    const completedSnapshot = await getDocs(completedFirestoreQuery);
                    let completedTasks = completedSnapshot.docs.length;
                    if (teams.length > 0) {
                        const todayStr = today.toISOString().split('T')[0];
                        const tomorrowStr = tomorrow.toISOString().split('T')[0];
                        for (const team of teams) {
                            const teamTasksRef = ref(realtime, `teams/${team.id}/tasks`);
                            const teamTasksSnapshot = await get(teamTasksRef);
                            const teamTasksData = teamTasksSnapshot.val() || {};//eslint-disable-next-line
                            Object.values(teamTasksData).forEach(task => {
                                if (
                                    task.assignedTo === auth.currentUser.uid &&
                                    task.deadline &&
                                    task.deadline.substring(0, 10) >= todayStr &&
                                    task.deadline.substring(0, 10) < tomorrowStr
                                ) {
                                    totalTasks++;
                                    if (task.status === TaskStatus.DONE) {
                                        completedTasks++;
                                    }
                                }
                            });
                        }
                    }
                    setTotalTasksCount(totalTasks);
                    setCompletedTasksCount(completedTasks);
                }
                const upcomingFirestoreQuery = query(
                    collection(firestore, 'tasks'),
                    where("assignedTo", "==", auth.currentUser.uid),
                    where("status", "!=", TaskStatus.DONE),
                    orderBy("deadline"),
                    limit(5)
                );
                const upcomingSnapshot = await getDocs(upcomingFirestoreQuery);
                const upcomingFirestoreTasks = upcomingSnapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data(),
                    source: 'firestore',
                }));
                let upcomingRealtimeTasks = [];
                if (teams.length > 0) {
                    for (const team of teams) {
                        const teamTasksRef = ref(realtime, `teams/${team.id}/tasks`);
                        const teamTasksSnapshot = await get(teamTasksRef);
                        const teamTasksData = teamTasksSnapshot.val() || {};
                        const teamTasks = Object.entries(teamTasksData)
                            .map(([id, data]) => ({
                                id,
                                ...data,
                                teamId: team.id,
                                source: 'realtime'
                            })).filter(task =>
                                task.assignedTo === auth.currentUser.uid &&
                                task.status !== TaskStatus.DONE
                            );

                        upcomingRealtimeTasks = [...upcomingRealtimeTasks, ...teamTasks];
                    }
                }
                const upcomingTasks = [...upcomingFirestoreTasks, ...upcomingRealtimeTasks]
                    .sort((a, b) => {
                        if (!a.deadline) return 1;
                        if (!b.deadline) return -1;
                        const aDeadline = typeof a.deadline === 'string'
                            ? a.deadline
                            : a.deadline.toDate ? a.deadline.toDate().toISOString() : "";
                        const bDeadline = typeof b.deadline === 'string'
                            ? b.deadline
                            : b.deadline.toDate ? b.deadline.toDate().toISOString() : "";
                        return aDeadline.localeCompare(bDeadline);
                    })
                    .slice(0, 5).map(task => {
                        if (task.source === 'realtime' && task.teamId) {
                            const team = teams.find(t => t.id === task.teamId);
                            return { ...task, teamName: team ? team.name : "Team Task" };
                        }
                        return { ...task, teamName: "Personal Task" };
                    });
                setTasks(upcomingTasks);
            }
            catch (error) { }
        };
        if (auth.currentUser) {
            fetchTodayTasks();
        }
    }, [teams]);

    const updateProgressDirectly = (completedCount, totalCount) => {
        if (totalCount > 0) {
            const newProgress = Math.round((completedCount / totalCount) * 100);
            setProgress(newProgress);
        } else {
            setProgress(0);
        }
    };

    const handleMarkAsDone = async () => {
        const headTask = tasks[0];
        if (!headTask) return;
        try {
            const result = await updateTaskStatus(
                headTask.id,
                TaskStatus.DONE,
                headTask.source === 'realtime' ? headTask.teamId : `personal_${auth.currentUser.uid}`
            );
            if (result.success) {
                setJustCompleted(true);
                const newCompletedCount = completedTasksCount + 1;
                setCompletedTasksCount(newCompletedCount);
                const newProgress = Math.round((newCompletedCount / totalTasksCount) * 100);
                setProgress(newProgress);
                updateProgressDirectly(newCompletedCount, totalTasksCount);
                setTimeout(() => {
                    setTasks(prevTasks => prevTasks.filter(task => task.id !== headTask.id));
                    setTodayTask(null);
                    setForceUpdate(prev => prev + 1);
                    setJustCompleted(false);
                }, 300);
            }
        }
        catch (error) { }
    };

    const goToTasks = () => { setActiveTab(1); };

    const handleAddNote = () => {
        setShowModal(true);
    };

    const handleNewItemAction = (type) => {
        setNoteType(type);
        setNewNote('');
        if (type === 'Reminder') {
            const defaultTime = new Date();
            defaultTime.setHours(defaultTime.getHours() + 1);
            setReminderDateTime(defaultTime);
        }
        setShowNoteEditor(true);
        setShowModal(false);
    };

    const saveNote = async () => {
        if (newNote.trim() === '') {
            setShowNoteEditor(false);
            return;
        }
        try {
            const noteData = {
                userId: auth.currentUser.uid,
                type: noteType,
                content: newNote,
                timestamp: new Date(),
                date: new Date().toLocaleDateString(),
            };
            if (noteType === 'Reminder') {
                noteData.reminderDateTime = reminderDateTime;
            }
            const docRef = await addDoc(collection(firestore, "notes"), noteData);
            if (noteType === 'Reminder' && Notification.permission === "granted") {
                scheduleNotification({
                    ...noteData,
                    id: docRef.id
                });
            }
            setNewNote('');
            setShowNoteEditor(false);
        } catch (error) { }
    };

    const scheduleNotification = (note) => {
        if (!note.reminderDateTime) return;
        const reminderTime = note.reminderDateTime.toDate
            ? note.reminderDateTime.toDate()
            : new Date(note.reminderDateTime);
        const currentTime = new Date();
        const timeUntilReminder = reminderTime - currentTime;

        if (timeUntilReminder > 0) {
            setTimeout(() => {
                if (Notification.permission === "granted") {
                    const title = note.content.split('\n')[0] || 'Reminder';
                    const body = note.content.split('\n').slice(1).join('\n') || 'Your reminder is due!';
                    const notification = new Notification(title, {
                        body: body,
                        icon: '/favicon.ico',
                        tag: note.id,
                    });
                    notification.onclick = function () {
                        window.focus();
                    };
                }
            }, timeUntilReminder);
        }
    };

    const formatInputDateTime = (date) => {
        const d = date instanceof Date ? date : new Date();
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        const hours = String(d.getHours()).padStart(2, '0');
        const minutes = String(d.getMinutes()).padStart(2, '0');
        return `${year}-${month}-${day}T${hours}:${minutes}`;
    };

    return (
        <div className="p-1 h-2/3 relative">
            <div className={`p-3.5 h-full rounded-lg flex-col overflow-y-auto ${appMode === AppMode.LIGHT ? light_mode_secondary : dark_mode_secondary}`}>
                <div className="flex justify-between items-center">
                    <h1 className=''>Task Board</h1>
                    <button className="text-sm bi bi-arrows-angle-expand" onClick={goToTasks}></button>
                </div>
                {tasks.length === 0
                    ? <></>
                    : <div className='py-5'>
                        {/* <h1 className='font-semibold'>{tasks != null ? tasks.length + ' Tasks Today' : "No Tasks Today"}</h1> */}
                        <h1 className='font-semibold'>{tasks != null ? tasks[0].title : "No Tasks Today"}</h1>
                        <h2 className='text-sm'>{todayTask ? todayTask.teamName || "Project Name" : ""}</h2>
                        <div className="py-2.5 flex items-center">
                            <i className="bi bi-clock"></i>
                            <h3 className='ml-2'>{tasks[0].timeEstimate ?? "Deadline"}</h3>
                        </div>
                        <div className='p-1'>
                            <button onClick={handleMarkAsDone} className='w-full h-12 rounded-lg bg-[#546FFF] hover:bg-blue-600 text-white'>Mark as Done</button>
                        </div>
                        <div className="py-3 flex justify-between items-center font-semibold">
                            <h1 className=''>Progress</h1>
                            <h1 className='text-[#546FFF]'>{progress}%</h1>
                        </div>
                        <div className="pl-3"><ProgressBar progress={progress} key={`progress-${progress}-${justCompleted}`} /></div>
                    </div>
                }
                {tasks.length === 0
                    ? <></>
                    : <div className="pb-3.5 flex justify-between items-center">
                        <h1 className='font-semibold'>Tasks List</h1>
                        <button className='text-[#546FFF] text-xs' onClick={goToTasks}>View All</button>
                    </div>
                }
                <TaskList tasks={tasks} setActiveTab={setActiveTab} />
            </div>
            <button className={`fixed bottom-6 right-6 z-10 flex transition-all duration-300 ease-in-out ${isHovered ? 'bg-[#546FFF] rounded-full pl-4 pr-5 py-3 items-center' : 'bg-[#546FFF] rounded-full w-12 h-12 items-center justify-center'}`} onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)} onClick={handleAddNote} style={{ boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)' }}>
                <i className="bi bi-plus text-white text-2xl"></i>
                {isHovered && <span className="text-white ml-2 whitespace-nowrap">Add a new note</span>}
            </button>
            {showModal && <AddNoteModal appMode={appMode} show={showModal} onClose={() => setShowModal(false)} onAddNote={handleNewItemAction} onAddReminder={handleNewItemAction} />}
            {showNoteEditor && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50" onClick={() => setShowNoteEditor(false)}>
                    <div className={`w-96 rounded-lg shadow-lg ${appMode === AppMode.LIGHT ? 'bg-white' : 'bg-gray-800'}`} onClick={(e) => e.stopPropagation()}>
                        <div className="flex justify-between items-center p-4 border-b border-gray-200">
                            <h2 className={`text-lg font-semibold ${appMode === AppMode.LIGHT ? 'text-gray-800' : 'text-white'}`}>{noteType === 'Note' ? 'Add New Note' : 'Add New Reminder'}</h2>
                            <button onClick={() => setShowNoteEditor(false)} className={`p-1 rounded-full hover:bg-gray-200 ${appMode === AppMode.LIGHT ? 'text-gray-600' : 'text-gray-400'}`}>
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                </svg>
                            </button>
                        </div>
                        <div className="p-4">
                            <textarea value={newNote} onChange={(e) => setNewNote(e.target.value)} placeholder={noteType === 'Note' ? "Write your note here..." : "Write your reminder here..."} className={`w-full h-40 p-3 rounded-lg border ${appMode === AppMode.LIGHT ? 'border-gray-300 text-gray-800' : 'border-gray-600 bg-gray-700 text-white'} focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none`}></textarea>
                            {noteType === 'Reminder' && (
                                <div className="mt-4">
                                    <label className={`block text-sm font-medium mb-2 ${appMode === AppMode.LIGHT ? 'text-gray-700' : 'text-gray-300'}`}>Reminder Time:</label>
                                    <input type="datetime-local" value={formatInputDateTime(reminderDateTime)} onChange={(e) => {
                                        if (e.target.value) {
                                            setReminderDateTime(new Date(e.target.value));
                                        }
                                    }} className={`w-full p-2 rounded-md border ${appMode === AppMode.LIGHT ? 'border-gray-300 bg-white text-gray-800' : 'border-gray-600 bg-gray-700 text-white'} focus:outline-none focus:ring-2 focus:ring-blue-500`}
                                    />
                                </div>
                            )}

                            <div className="mt-6 flex justify-end space-x-3">
                                <button onClick={() => setShowNoteEditor(false)} className={`px-4 py-2 rounded-md text-sm font-medium ${appMode === AppMode.LIGHT ? 'bg-gray-200 text-gray-800 hover:bg-gray-300' : 'bg-gray-700 text-gray-200 hover:bg-gray-600'}`}>Cancel</button>
                                <button onClick={saveNote} className={`px-4 py-2 rounded-md text-sm font-medium bg-blue-600 text-white hover:bg-blue-700`}>Save {noteType}</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export const TaskList = ({ tasks, setActiveTab }) => {
    if (tasks.length === 0) {
        return (
            <div className='h-full flex flex-col justify-center items-center'>
                <div className="py-2 text-center text-md">No upcoming tasks</div>
                <div className="mt-3 w-full text-center items-center">
                    <button className='w-1/2 h-10 rounded-lg bg-[#546FFF] hover:bg-blue-600 text-white' onClick={() => { setActiveTab(1) }}>
                        <i className="bi bi-plus-lg"></i> Create New Task
                    </button>
                </div>
            </div>
        );
    }
    return (
        <>
            {tasks.map((task, index) => (
                <div key={task.id} className="py-3">
                    <div className="flex justify-between items-center">
                        <div className="flex items-center">
                            <div className="w-8 h-8 bg-gray-300 rounded-lg flex justify-center items-center">
                                <h1 className='text-black font-semibold'>{index + 1}</h1>
                            </div>
                            <div className="ml-3 flex-col">
                                <h3 className="font-medium">{task.title}</h3>
                            </div>
                        </div>
                        <div></div>
                        {/* <div className={`w-6 h-6 rounded-full border flex justify-center items-center cursor-pointer ${task.status === TaskStatus.DONE
                            ? 'border-blue-600 bg-blue-600'
                            : selectedTasks.includes(task.id)
                                ? 'border-blue-600 bg-blue-200'
                                : 'border-gray-400'
                            }`}
                            onClick={() => onTaskStatusChange(task)}
                        >
                            {task.status === TaskStatus.DONE && <i className="bi bi-check text-white text-xs"></i>}
                            {selectedTasks.includes(task.id) && task.status !== TaskStatus.DONE && <i className="bi bi-check text-blue-600 text-xs"></i>}
                        </div> */}
                    </div>
                </div>
            ))}
        </>
    );
};

export function AddNoteModal({ appMode, show, onClose, onAddNote, onAddReminder }) {
    if (!show) return null;
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50" onClick={onClose}>
            <div className={`w-80 rounded-lg shadow-lg p-5 ${appMode === AppMode.LIGHT ? 'bg-white' : 'bg-gray-800'}`} onClick={(e) => e.stopPropagation()}>
                <div className="flex justify-between items-center mb-4">
                    <h2 className={`text-lg font-semibold ${appMode === AppMode.LIGHT ? 'text-gray-800' : 'text-white'}`}>Add New</h2>
                    <button onClick={onClose} className={`p-1 rounded-full hover:bg-gray-200 ${appMode === AppMode.LIGHT ? 'text-gray-600' : 'text-gray-400'}`}>
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                    </button>
                </div>
                <div className="space-y-3">
                    <button onClick={() => onAddNote('Note')} className={`w-full py-3 px-4 rounded-lg flex items-center ${appMode === AppMode.LIGHT ? 'hover:bg-gray-100 text-gray-800' : 'hover:bg-gray-700 text-white'}`}>
                        <svg xmlns="http://www.w3.org/2000/svg" className={`h-5 w-5 mr-3 ${appMode === AppMode.LIGHT ? 'text-blue-600' : 'text-blue-400'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                        <span>Add Note</span>
                    </button>
                    <button onClick={() => onAddReminder('Reminder')} className={`w-full py-3 px-4 rounded-lg flex items-center ${appMode === AppMode.LIGHT ? 'hover:bg-gray-100 text-gray-800' : 'hover:bg-gray-700 text-white'}`}>
                        <svg xmlns="http://www.w3.org/2000/svg" className={`h-5 w-5 mr-3 ${appMode === AppMode.LIGHT ? 'text-amber-600' : 'text-amber-400'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span>Add Reminder</span>
                    </button>
                </div>
            </div>
        </div>
    );
}

export const PricingTile = ({ tierName, price, feature1, feature2, feature3, feature4, feature5, delay, billingCycle }) => {
    const history = useHistory();
    const [isLoading, setIsLoading] = useState(false);//eslint-disable-next-line
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const features = [feature1, feature2, feature3, feature4, feature5];

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            setIsAuthenticated(!!user);
        });
        return () => unsubscribe();
    }, []);

    const handleSubscription = async () => {
        if (!auth.currentUser) {
            toast.error("Please login to subscribe");
            history.push('/login');
            return;
        }

        setIsLoading(true);
        try {
            if (!window.Razorpay) {
                await loadRazorpayScript();
            }
            const result = await initiateRazorpayPayment(price, tierName, billingCycle, auth.currentUser.uid);
            if (result && result.success) {
                toast.success(`Successfully subscribed to ${tierName} plan!`);
                history.push('/dashboard');
            }
        } catch (error) {
            console.error("Payment error:", error);
            toast.error(error.message || "Payment failed. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };

    const loadRazorpayScript = () => {
        return new Promise((resolve) => {
            const script = document.createElement('script');
            script.src = 'https://checkout.razorpay.com/v1/checkout.js';
            script.onload = () => {
                resolve(true);
            };
            script.onerror = () => {
                resolve(false);
                toast.error("Failed to load payment gateway. Please check your internet connection.");
            };
            document.body.appendChild(script);
        });
    };

    return (
        <motion.div initial={{ opacity: 0, y: 25 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: delay }} className="p-4 xl:w-1/4 md:w-1/2 w-full">
            <div className="h-full p-6 rounded-xl border border-gray-300 flex flex-col relative overflow-hidden">
                <h2 className={`text-sm tracking-widest title-font mb-1 font-medium text-black`}>{tierName}</h2>
                {typeof price === 'string'
                    ? <h1 className={`text-5xl text-black leading-none font-semibold flex items-center py-2 pb-4 mb-4 border-b border-gray-200`}><span>{price}</span></h1>
                    : <h1 className={`text-5xl text-black leading-none font-semibold flex items-center py-2 pb-4 mb-4 border-b border-gray-200`}>
                        <span>₹ {price}</span>
                        <span className={`text-lg ml-1 font-normal text-gray-800`}>
                            {billingCycle === 'Monthly' ? '/mo' : '/yr'}
                        </span>
                    </h1>
                }
                {features.map((feature, index) => (
                    <span key={index} className="flex items-center text-lg py-1 mb-2">
                        {feature
                            ? <>
                                <span className="p-1 w-4 h-4 mr-2 inline-flex items-center justify-center bg-[#546FFF] text-white rounded-full flex-shrink-0">
                                    <svg fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" className="w-3 h-3" viewBox="0 0 24 24"><path d="M20 6L9 17l-5-5"></path></svg>
                                </span>
                                <h1 className={`ml-1 text-gray-900`}>{feature}</h1>
                            </>
                            : <span className="w-4 h-4 mr-2 inline-flex items-center justify-center text-black rounded-full flex-shrink-0"></span>}
                    </span>
                ))}
                <div className="py-2 mt-auto">
                    {billingCycle === null
                        ? <button onClick={tierName === "FREE" ? null : () => { history.push('/contact') }} disabled={isLoading} className={`flex items-center mt-auto text-white bg-[#546FFF] border-0 py-2 px-4 w-full focus:outline-none hover:bg-[#546FFF] rounded ${isLoading ? 'opacity-70 cursor-not-allowed' : ''}`}>
                            {isLoading
                                ? <span className="flex items-center justify-center">
                                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg> Loading...
                                </span>
                                : <>
                                    Contact Sales
                                </>
                            }
                        </button>
                        : <button
                            onClick={tierName === "FREE" ? null : handleSubscription}
                            disabled={isLoading || tierName === "FREE"}
                            className={`flex items-center mt-auto text-white bg-[#546FFF] border-0 py-2 px-4 w-full focus:outline-none hover:bg-[#546FFF] rounded ${isLoading || tierName === "FREE" ? 'opacity-70 cursor-not-allowed' : ''}`}
                        >
                            {isLoading
                                ? <span className="flex items-center justify-center">
                                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg> Processing...
                                </span>
                                : <>
                                    {tierName === "FREE" ? "Current Plan" : tierName === "ENTERPRISE" ? "Contact Sales" : "Subscribe Now"}
                                    {tierName === "FREE" ? <></> : (
                                        <svg fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" className="w-4 h-4 ml-auto" viewBox="0 0 24 24">
                                            <path d="M5 12h14M12 5l7 7-7 7"></path>
                                        </svg>
                                    )}
                                </>
                            }
                        </button>
                    }
                </div>
            </div>
        </motion.div>
    );
};

export const OptionTile = ({ icon, title, desc, redirect }) => {
    const handleClick = () => { if (redirect) redirect(); };
    return (
        <button onClick={handleClick} className="p-2 lg:w-1/3 md:w-1/2 w-full">
            <div className="h-full flex items-center border-gray-200 border p-4 rounded-lg">{icon}
                <div className="flex-grow">
                    <h2 className="text-gray-900 title-font font-medium">{title}</h2>
                    <p className="text-gray-500">{desc}</p>
                </div>
            </div>
        </button>
    )
}

export function LoadingScreen({ appMode }) {
    return (
        <div className={`w-screen h-screen fixed top-0 left-0 ${appMode === AppMode.LIGHT ? light_mode_primary : dark_mode_primary} z-50 flex items-center justify-center`}>
            <div className="flex flex-col items-center justify-center">
                <div className="mb-8 relative">
                    <svg width="60" height="60" viewBox="0 0 60 60" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M30 10C18.954 10 10 18.954 10 30C10 41.046 18.954 50 30 50C41.046 50 50 41.046 50 30C50 18.954 41.046 10 30 10Z" className='stroke-blue-600' strokeWidth="3" strokeLinecap="round" strokeDasharray="126" strokeDashoffset="126" fill="none">
                            <animate attributeName="stroke-dashoffset" from="126" to="0" dur="1.5s" begin="0s" repeatCount="indefinite" />
                        </path>
                    </svg>
                </div>
                <h1 className={`text-2xl font-medium ${appMode === AppMode.LIGHT ? 'text-gray-800' : 'text-white'} mb-6`}>Corella</h1>
                <p className={`text-sm ${appMode === AppMode.LIGHT ? 'text-gray-500' : 'text-gray-400'}`}>Loading your workspace</p>
            </div>
        </div>
    );
}

export const ExplanationPopup = ({ appMode, explanation, onClose, title, setShowPopup }) => {
    const explanationPopupRef = useRef(null);
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (explanationPopupRef.current && !explanationPopupRef.current.contains(event.target)) {
                setShowPopup(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };//eslint-disable-next-line
    }, []);
    return (
        <div className={`fixed inset-0 ${appMode === AppMode.LIGHT ? 'bg-black' : 'bg-gray-500'} bg-opacity-50 flex items-center justify-center z-50`}>
            <div ref={explanationPopupRef} className={`${appMode === AppMode.LIGHT ? 'bg-white text-[#232360]' : dark_mode_primary} rounded-lg p-6 max-w-lg w-full max-h-[80vh] overflow-y-auto`}>
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-lg font-semibold">{title === 'Summary' ? 'Email Summary' : 'Task Suggestion'}</h2>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
                        <i className="bi bi-x-lg"></i>
                    </button>
                </div>
                <div className="prose">
                    {explanation
                        ? <div className="whitespace-pre-line">{explanation}</div>
                        : <div className="flex justify-center items-center h-32">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#6772FE]"></div>
                        </div>
                    }
                </div>
            </div>
        </div>
    );
};

export const SuggestionPopup = ({ appMode, explanation, onClose, setShowPopup }) => {
    const replyPopupRef = useRef(null);
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (replyPopupRef.current && !replyPopupRef.current.contains(event.target)) {
                setShowPopup(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };//eslint-disable-next-line
    }, []);
    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div ref={replyPopupRef} className={`${appMode === AppMode.LIGHT ? 'bg-white text-[#232360]' : dark_mode_primary} rounded-lg p-6 max-w-lg w-full max-h-[80vh] overflow-y-auto`}>
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-lg font-semibold">Email Reply Suggestion</h2>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
                        <i className="bi bi-x-lg"></i>
                    </button>
                </div>

                <div className="prose">
                    {explanation
                        ? <div className="whitespace-pre-line">{explanation}</div>
                        : <div className="flex justify-center items-center h-32">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#6772FE]"></div>
                        </div>
                    }
                </div>
            </div>
        </div>
    );
};

export const NewActivity = ({ appMode, onClose, onSave }) => {
    const activityRef = useRef(null);
    const [form, setForm] = useState({
        title: '',
        description: '',
        location: '',
        start: '',
        end: '',
        attendees: '',
    });

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (activityRef.current && !activityRef.current.contains(event.target)) {
                onClose();
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };//eslint-disable-next-line
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const attendeeEmails = form.attendees
            .split(',')
            .map((email) => email.trim())
            .filter(Boolean);
        const eventData = {
            title: form.title,
            description: form.description,
            location: form.location,
            start: new Date(form.start),
            end: new Date(form.end),
            attendees: attendeeEmails.map((email) => ({ email })),
        };
        await onSave(eventData);
        onClose();
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div ref={activityRef} className={`${appMode === AppMode.LIGHT ? 'bg-white' : 'bg-gray-900'} rounded-lg p-6 max-w-md w-full max-h-[80vh] overflow-y-auto`}>
                <div className="flex justify-between items-center mb-4">
                    <h2 className={`text-lg font-semibold ${appMode === AppMode.LIGHT ? 'text-[#232360]' : ''}`}>Schedule a New Event</h2>
                    <button onClick={onClose} className={appMode === AppMode.LIGHT ? 'text-gray-500 hover:text-gray-700' : 'text-gray-300 hover:text-gray-400'}>
                        <i className="bi bi-x-lg"></i>
                    </button>
                </div>
                <form onSubmit={handleSubmit}>
                    <div className="mb-4">
                        <label className={`block ${appMode === AppMode.LIGHT ? 'text-gray-700' : 'text-gray-100'} text-sm font-bold mb-2`}>Event Name</label>
                        <input type="text" name="title" value={form.title} onChange={handleChange} className={`shadow-sm bg-transparent appearance-none border rounded-lg w-full py-2 px-3 ${appMode === AppMode.LIGHT ? 'text-gray-700' : 'text-gray-100'} focus:outline-none focus:shadow-outline`} placeholder="Enter event name" required />
                    </div>
                    <div className="mb-4">
                        <label className={`block ${appMode === AppMode.LIGHT ? 'text-gray-700' : 'text-gray-100'} text-sm font-bold mb-2`}>Description</label>
                        <textarea name="description" value={form.description} onChange={handleChange} className={`shadow-sm bg-transparent appearance-none border rounded-lg w-full py-2 px-3 ${appMode === AppMode.LIGHT ? 'text-gray-700' : 'text-gray-100'} focus:outline-none focus:shadow-outline`} placeholder="Event description" />
                    </div>
                    <div className="mb-4">
                        <label className={`block ${appMode === AppMode.LIGHT ? 'text-gray-700' : 'text-gray-100'} text-sm font-bold mb-2`}>Location</label>
                        <input type="text" name="location" value={form.location} onChange={handleChange} className={`shadow-sm bg-transparent appearance-none border rounded-lg w-full py-2 px-3 ${appMode === AppMode.LIGHT ? 'text-gray-700' : 'text-gray-100'} focus:outline-none focus:shadow-outline`} placeholder="Event location" />
                    </div>
                    <div className="mb-4">
                        <label className={`block ${appMode === AppMode.LIGHT ? 'text-gray-700' : 'text-gray-100'} text-sm font-bold mb-2`}>Start Date & Time</label>
                        <input type="datetime-local" name="start" value={form.start} onChange={handleChange} className={`shadow-sm bg-transparent appearance-none border border-gray-300 rounded-lg w-full py-3 px-4 ${appMode === AppMode.LIGHT ? 'text-gray-700' : 'text-gray-100'} leading-tight focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 ease-in-out`} required />
                    </div>
                    <div className="mb-4">
                        <label className={`block ${appMode === AppMode.LIGHT ? 'text-gray-700' : 'text-gray-100'} text-sm font-bold mb-2`}>End Date & Time</label>
                        <input type="datetime-local" name="end" value={form.end} onChange={handleChange} className={`shadow-sm bg-transparent appearance-none border border-gray-300 rounded-lg w-full py-3 px-4 ${appMode === AppMode.LIGHT ? 'text-gray-700' : 'text-gray-100'} leading-tight focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 ease-in-out`} required />
                    </div>
                    <div className="mb-4">
                        <label className={`block ${appMode === AppMode.LIGHT ? 'text-gray-700' : 'text-gray-100'} text-sm font-bold mb-2`}>Recipient Emails</label>
                        <input type="text" name="attendees" value={form.attendees} onChange={handleChange} placeholder="Add emails separated by commas" className={`shadow-sm bg-transparent appearance-none border rounded-lg w-full py-3 px-4 ${appMode === AppMode.LIGHT ? 'text-gray-700' : 'text-gray-100'} focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 ease-in-out`} />
                    </div>
                    <div className="flex justify-between">
                        <button type="button" onClick={onClose} className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-opacity-50 transition-all duration-200 ease-in-out">Cancel</button>
                        <button type="submit" className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 transition-all duration-200 ease-in-out">Create Event</button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export function EventDetailsPopup({ event, appMode, onClose }) {
    const eventRef = useRef(null);
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (eventRef.current && !eventRef.current.contains(event.target)) {
                onClose();
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };//eslint-disable-next-line
    }, []);
    if (!event) return null;
    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('default', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
    };

    const providerColors = event.provider === AuthProvider.GOOGLE
        ? { primary: 'bg-red-500', secondary: appMode === AppMode.LIGHT ? 'bg-red-50' : 'bg-red-900/20', icon: appMode === AppMode.LIGHT ? 'text-red-600' : 'text-red-400' }
        : { primary: 'bg-blue-500', secondary: appMode === AppMode.LIGHT ? 'bg-blue-50' : 'bg-blue-900/20', icon: appMode === AppMode.LIGHT ? 'text-blue-600' : 'text-blue-400' };
    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 animate-fadeIn">
            <div ref={eventRef} className={`w-full max-w-lg rounded-xl shadow-2xl ${appMode === AppMode.LIGHT ? 'bg-white' : 'bg-gray-800'} overflow-hidden`}>
                <div className={`p-5 ${providerColors.primary} flex justify-between items-center`}>
                    <h2 className="font-bold text-xl text-white">Event Details</h2>
                    <button onClick={onClose} className="rounded-full p-2 transition-colors bg-white/20 hover:bg-white/30 text-white">
                        <i className="bi bi-x-lg"></i>
                    </button>
                </div>
                <div className="p-6">
                    <h3 className={`text-2xl font-bold mb-6 ${appMode === AppMode.LIGHT ? 'text-gray-800' : 'text-white'}`}>{event.subject}</h3>
                    <div className="space-y-5">
                        <div className="flex items-start">
                            <div className={`rounded-full p-2 mr-4 ${providerColors.secondary}`}>
                                <i className={`bi bi-calendar-event text-lg ${providerColors.icon}`}></i>
                            </div>
                            <div>
                                <p className={`font-medium ${appMode === AppMode.LIGHT ? 'text-gray-900' : 'text-white'}`}>{formatDate(event.startDateTime)}</p>
                                <p className={`${appMode === AppMode.LIGHT ? 'text-gray-600' : 'text-gray-300'}`}>{event.startTime} - {event.endTime}</p>
                            </div>
                        </div>
                        {event.location && event.location !== 'No location' && (
                            <div className="flex items-start">
                                <div className={`rounded-full p-2 mr-4 ${appMode === AppMode.LIGHT ? 'bg-green-50' : 'bg-green-900/20'}`}>
                                    <i className={`bi bi-geo-alt text-lg ${appMode === AppMode.LIGHT ? 'text-green-600' : 'text-green-400'}`}></i>
                                </div>
                                <div>
                                    <p className={`font-medium ${appMode === AppMode.LIGHT ? 'text-gray-900' : 'text-white'}`}>Location</p>
                                    <p className={`${appMode === AppMode.LIGHT ? 'text-gray-600' : 'text-gray-300'}`}>{event.location}</p>
                                </div>
                            </div>
                        )}
                        {event.description && (
                            <div className="flex items-start">
                                <div className={`rounded-full p-2 mr-4 ${appMode === AppMode.LIGHT ? 'bg-purple-50' : 'bg-purple-900/20'}`}>
                                    <i className={`bi bi-card-text text-lg ${appMode === AppMode.LIGHT ? 'text-purple-600' : 'text-purple-400'}`}></i>
                                </div>
                                <div className="flex-1">
                                    <p className={`font-medium mb-2 ${appMode === AppMode.LIGHT ? 'text-gray-900' : 'text-white'}`}>Description</p>
                                    <div className={`p-4 rounded-lg ${appMode === AppMode.LIGHT ? 'bg-gray-50' : 'bg-gray-700'}`}>
                                        <p className={`whitespace-pre-wrap ${appMode === AppMode.LIGHT ? 'text-gray-600' : 'text-gray-300'}`}>{event.description}</p>
                                    </div>
                                </div>
                            </div>
                        )}
                        {event.attendees && event.attendees.length > 0 && (
                            <div className="flex items-start">
                                <div className={`rounded-full p-2 mr-4 ${appMode === AppMode.LIGHT ? 'bg-amber-50' : 'bg-amber-900/20'}`}>
                                    <i className={`bi bi-people text-lg ${appMode === AppMode.LIGHT ? 'text-amber-600' : 'text-amber-400'}`}></i>
                                </div>
                                <div className="flex-1">
                                    <p className={`font-medium mb-2 ${appMode === AppMode.LIGHT ? 'text-gray-900' : 'text-white'}`}>Attendees</p>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                        {event.attendees.map((attendee, index) => (
                                            <div key={index} className={`flex items-center p-3 rounded-lg overflow-x-auto truncate ${appMode === AppMode.LIGHT ? 'bg-gray-50' : 'bg-gray-700'}`}>
                                                <div className={`w-1/6 h-6 rounded-full flex items-center justify-center mr-3 ${appMode === AppMode.LIGHT ? 'bg-gray-200' : 'bg-gray-600'}`}><i className="bi bi-person"></i></div>
                                                <div>
                                                    <p className={`w-5/6 text-sm font-medium ${appMode === AppMode.LIGHT ? 'text-gray-800' : 'text-gray-200'}`}>{attendee.username || attendee.email || 'Guest'}</p>
                                                    {attendee.email && attendee.username && <p className={`text-xs ${appMode === AppMode.LIGHT ? 'text-gray-500' : 'text-gray-400'}`}>{attendee.email}</p>}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}
                        <div className="flex items-start">
                            <div className={`rounded-full p-2 mr-4 ${providerColors.secondary}`}>
                                <i className={`bi ${event.provider === AuthProvider.GOOGLE ? 'bi-google' : 'bi-microsoft'} text-lg ${providerColors.icon}`}></i>
                            </div>
                            <div>
                                <p className={`font-medium ${appMode === AppMode.LIGHT ? 'text-gray-900' : 'text-white'}`}>Calendar</p>
                                <p className={`${appMode === AppMode.LIGHT ? 'text-gray-600' : 'text-gray-300'}`}>{event.provider === AuthProvider.GOOGLE ? 'Google Calendar' : 'Microsoft Calendar'}</p>
                            </div>
                        </div>
                    </div>
                </div>
                <div className={`p-4 ${appMode === AppMode.LIGHT ? 'bg-gray-50' : 'bg-gray-700'} flex justify-end space-x-3`}>
                    {event.htmlLink && (
                        <a href={event.htmlLink} target="_blank" rel="noopener noreferrer" className={`px-4 py-2 rounded-lg font-medium transition-colors ${appMode === AppMode.LIGHT
                            ? 'bg-blue-600 text-white hover:bg-blue-700'
                            : 'bg-blue-700 text-white hover:bg-blue-600'
                            }`}
                        >Open in Calendar
                        </a>
                    )}
                    <button onClick={onClose} className={`px-4 py-2 rounded-lg font-medium transition-colors ${appMode === AppMode.LIGHT
                        ? 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                        : 'bg-gray-600 text-white hover:bg-gray-500'
                        }`}>Close
                    </button>
                </div>
            </div>
        </div>
    );
}

export const ReplyPopup = ({ appMode, loading, handleSendReply, handleEmailReply, reply, replyText, setReplyText, setShowReplyPopup, selectedEmail, suggestionPopup, setSuggestionPopup }) => {
    const replyRef = useRef(null);
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (replyRef.current && !replyRef.current.contains(event.target)) {
                setShowReplyPopup(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };//eslint-disable-next-line
    }, []);
    return (
        <div className={`fixed inset-0 ${appMode === AppMode.LIGHT ? 'bg-black' : 'bg-gray-700'} bg-opacity-50 flex items-center justify-center z-50`}>
            <div ref={replyRef} className={`${appMode === AppMode.LIGHT ? 'bg-white' : dark_mode_primary} rounded-lg w-full max-w-xl p-6`}>
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-semibold">Reply to {selectedEmail.name}</h2>
                    <button onClick={() => setShowReplyPopup(false)} className="text-gray-500 hover:text-gray-700">
                        <i className="bi bi-x-lg"></i>
                    </button>
                </div>
                <div className="mb-4">
                    <div className={`${appMode === AppMode.LIGHT ? 'bg-gray-100' : dark_mode_secondary} p-3 rounded mb-2`}>
                        <p className="text-sm font-semibold">Subject: RE: {selectedEmail.subject}</p>
                        <p className={`text-sm ${appMode === AppMode.LIGHT ? 'text-gray-500' : 'text-white'}`}>To: {selectedEmail.name}</p>
                    </div>
                    <textarea className="w-full border border-gray-300 bg-transparent rounded p-3 min-h-[200px] focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Write your reply here..." value={replyText} onChange={(e) => setReplyText(e.target.value)} />
                </div>
                <div className="flex justify-between">
                    <button className="bg-gray-200 text-gray-700 px-4 py-2 rounded hover:bg-gray-300 transition-colors" onClick={() => setShowReplyPopup(false)}>Cancel</button>
                    <div className="flex gap-2">
                        <button className="bg-blue-100 text-blue-600 px-4 py-2 rounded hover:bg-blue-200 transition-colors flex items-center gap-2" onClick={handleEmailReply}>
                            <i className="bi bi-lightbulb"></i>AI Suggestion
                        </button>
                        <button className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 transition-colors" onClick={handleSendReply} disabled={loading}>
                            {loading
                                ? <span className="flex items-center gap-2"><i className="bi bi-arrow-repeat animate-spin"></i> Sending...</span>
                                : <span className="flex items-center gap-2"><i className="bi bi-send"></i> Send</span>}
                        </button>
                    </div>
                </div>
            </div>
            {suggestionPopup && <SuggestionPopup appMode={appMode} explanation={loading ? null : reply} onClose={() => setSuggestionPopup(false)} setShowPopup={setSuggestionPopup} />}
        </div>
    );
};

export const DeletePopup = ({ appMode, title, clickAction, deleteLoading, onCancelled }) => {
    return (
        <div className={`fixed inset-0 ${appMode === AppMode.LIGHT ? 'bg-black' : 'bg-gray-700'} bg-opacity-50 flex items-center justify-center z-50`}>
            <div className={`${appMode === AppMode.LIGHT ? 'bg-white' : dark_mode_primary} p-6 rounded-lg shadow-lg w-full max-w-md`}>
                <h3 className="text-lg font-semibold mb-4">Delete {title}</h3>
                <p className="text-gray-600 mb-6">Are you sure you want to delete this {title}?</p>
                <div className="flex justify-end gap-x-3">
                    <button className="px-4 py-2 rounded-lg bg-gray-200 hover:bg-gray-300 transition-colors text-gray-800" onClick={onCancelled}>Cancel</button>
                    <button className="px-4 py-2 rounded-lg bg-red-500 hover:bg-red-600 transition-colors text-white flex items-center" onClick={clickAction}>
                        {deleteLoading && <i className="bi bi-arrow-repeat animate-spin mr-2"></i>} Delete
                    </button>
                </div>
            </div>
        </div>
    )
}

export function AccountSelector({ appMode, accounts, onClose, onSelectAccount, onRemoveAccount, activeAccount, subscriptionInfo, onUpgrade, authManager, setIsGoogleLoggedIn, setIsMicrosoftLoggedIn, setUser, setLoading, setError }) {
    const [showAddOptions, setShowAddOptions] = useState(false);
    const [addingAccount, setAddingAccount] = useState(false);
    const [confirmingRemoval, setConfirmingRemoval] = useState(null);
    const getProviderIcon = (provider) => {
        return provider === AuthProvider.MICROSOFT ? 'bi bi-microsoft' : 'bi bi-google';
    };

    const getProviderName = (provider) => {
        return provider === AuthProvider.MICROSOFT ? 'Microsoft' : 'Google';
    };

    const getProviderColor = (provider) => {
        return provider === AuthProvider.MICROSOFT ? 'text-blue-500' : 'text-red-500';
    };

    const getProviderBgColor = (provider) => {
        return provider === AuthProvider.MICROSOFT
            ? appMode === AppMode.LIGHT ? 'bg-blue-50' : 'bg-blue-900'
            : appMode === AppMode.LIGHT ? 'bg-red-50' : 'bg-red-900';
    };

    const getProviderBorderColor = (provider) => {
        return provider === AuthProvider.MICROSOFT
            ? appMode === AppMode.LIGHT ? 'border-blue-100' : 'border-blue-800'
            : appMode === AppMode.LIGHT ? 'border-red-100' : 'border-red-800';
    };

    const getAccountInfo = (account) => {
        if (!account) return { email: 'No email', provider: AuthProvider.GOOGLE };
        const userData = account?.userData || account || {};
        const email = userData.email ||
            userData.username ||
            userData.userPrincipalName ||
            'No email';
        const provider = account?.provider ||
            (userData.provider) ||
            (email.includes('gmail') ? AuthProvider.GOOGLE : AuthProvider.MICROSOFT);

        return { email, provider };
    };

    const isAccountActive = (account) => {
        if (!activeAccount) return false;
        const accountInfo = getAccountInfo(account);
        const activeInfo = getAccountInfo(activeAccount);
        return accountInfo.provider === activeInfo.provider &&
            accountInfo.email === activeInfo.email;
    };

    const canAddMoreAccounts = () => {
        if (!subscriptionInfo) return true;
        return subscriptionInfo.accountsUsed < subscriptionInfo.accountLimit;
    };
    const handleAddGoogleAccount = async () => {
        try {
            if (!canAddMoreAccounts()) {
                onUpgrade && onUpgrade();
                return;
            }

            setAddingAccount(true);
            setLoading && setLoading(true);
            await initGoogleClient();
            if (authManager) {
                const result = await handleGoogleLogin(authManager, setUser, setLoading, setError, setIsGoogleLoggedIn);
                if (result) {
                    authManager.setActiveAccount(result.id);
                    authManager.notifyStateChange();
                    onClose();
                }
            } else {
                await handleGoogleLogin(null, setUser, setLoading, setError, setIsGoogleLoggedIn);
                onClose();
            }
        } catch (error) {
            if (error.message && error.message.includes("Account limit reached")) {
                onUpgrade && onUpgrade();
            } else {
                setError && setError(`Failed to add Google account: ${error.message}`);
            }
        } finally {
            setAddingAccount(false);
            setLoading && setLoading(false);
            setShowAddOptions(false);
        }
    };

    const handleAddMicrosoftAccount = async () => {
        try {
            if (!canAddMoreAccounts()) {
                onUpgrade && onUpgrade();
                return;
            }
            setAddingAccount(true);
            setLoading && setLoading(true);
            await microsoftAuthService.initialize();
            if (authManager) {
                const result = await handleLogin(setUser, setLoading, setError, setIsMicrosoftLoggedIn);
                if (result) {
                    const msAccounts = authManager.getAccounts(AuthProvider.MICROSOFT);
                    const newAccount = msAccounts.find(acc =>
                        acc.userData && acc.userData.email === result.email
                    );
                    if (newAccount) {
                        authManager.setActiveAccount(newAccount.id);
                    }
                    authManager.notifyStateChange();
                    onClose();
                }
            } else {
                await handleLogin(setUser, setLoading, setError, setIsMicrosoftLoggedIn);
                onClose();
            }
        } catch (error) {
            if (error.message && error.message.includes("Account limit reached")) {
                onUpgrade && onUpgrade();
            } else {
                setError && setError(`Failed to add Microsoft account: ${error.message}`);
            }
        } finally {
            setAddingAccount(false);
            setLoading && setLoading(false);
            setShowAddOptions(false);
        }
    };

    const handleSelectAccount = (account) => {
        if (isAccountActive(account)) return;
        onSelectAccount && onSelectAccount(account);
    };

    const handleConfirmRemoval = (account) => {
        if (confirmingRemoval && confirmingRemoval.id === account.id) {
            onRemoveAccount && onRemoveAccount(account);
            setConfirmingRemoval(null);
        } else {
            setConfirmingRemoval(account);
            setTimeout(() => {
                setConfirmingRemoval(prevAccount => prevAccount && prevAccount.id === account.id ? null : prevAccount);
            }, 5000);
        }
    };

    const handleCancelRemoval = () => {
        setConfirmingRemoval(null);
    };

    const groupedAccounts = accounts.reduce((result, account) => {
        const accountInfo = getAccountInfo(account);
        if (!result[accountInfo.provider]) {
            result[accountInfo.provider] = [];
        }
        result[accountInfo.provider].push(account);
        return result;
    }, {});

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className={`w-full max-w-md rounded-lg shadow-xl ${appMode === AppMode.LIGHT ? 'bg-white' : 'bg-gray-800'} max-h-[90vh] flex flex-col`}>
                <div className="p-4 border-b flex justify-between items-center">
                    <h2 className={`font-semibold text-lg ${appMode === AppMode.LIGHT ? 'text-gray-800' : 'text-white'}`}>Account Manager</h2>
                    <button onClick={onClose} className={`text-gray-500 hover:${appMode === AppMode.LIGHT ? 'text-gray-700' : 'text-gray-300'} p-1 rounded-full hover:bg-gray-100 transition-colors`} aria-label="Close">
                        <i className="bi bi-x-lg"></i>
                    </button>
                </div>

                <div className="p-4 overflow-y-auto flex-grow">
                    {subscriptionInfo && (
                        <div className={`mb-4 p-3 rounded-lg ${appMode === AppMode.LIGHT ? 'bg-blue-50' : 'bg-blue-900/30'} border ${appMode === AppMode.LIGHT ? 'border-blue-100' : 'border-blue-800'}`}>
                            <div className="flex justify-between items-center">
                                <p className={`text-sm ${appMode === AppMode.LIGHT ? 'text-gray-700' : 'text-gray-200'}`}>
                                    <span className={`font-medium ${appMode === AppMode.LIGHT ? 'text-blue-700' : 'text-blue-300'}`}>{subscriptionInfo.name} Plan:
                                    </span> {subscriptionInfo.accountsUsed} of {subscriptionInfo.accountLimit === 15 ? 'Unlimited' : subscriptionInfo.accountLimit} accounts
                                </p>
                                {!canAddMoreAccounts() && onUpgrade && subscriptionInfo.tier !== PaymentTiers.ENTERPRISE && <button onClick={onUpgrade} className="text-xs py-1 px-3 bg-purple-600 text-white rounded hover:bg-purple-700 transition-colors">Upgrade</button>}
                            </div>
                            {subscriptionInfo.accountLimit !== 15 && (
                                <div className="mt-2">
                                    <div className="w-full bg-gray-300 rounded-full h-1.5">
                                        <div className="bg-blue-600 h-1.5 rounded-full" style={{ width: `${Math.min(100, (subscriptionInfo.accountsUsed / subscriptionInfo.accountLimit) * 100)}%` }}></div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                    <div className="space-y-4">
                        {Object.entries(groupedAccounts).map(([provider, providerAccounts]) => (
                            <div key={provider} className="space-y-2">
                                <h3 className={`text-sm font-medium ${appMode === AppMode.LIGHT ? 'text-gray-600' : 'text-gray-300'} flex items-center`}>
                                    <i className={`${getProviderIcon(provider)} mr-2 ${getProviderColor(provider)}`}></i> {getProviderName(provider)} Accounts
                                </h3>
                                <div className="space-y-2">
                                    {providerAccounts.map((account, index) => {
                                        const userData = account.userData || account;
                                        const isActive = isAccountActive(account);
                                        const isConfirmingThis = confirmingRemoval && confirmingRemoval.id === account.id;
                                        return (
                                            <div key={account.id || index} className={`p-3 rounded-lg border ${isActive ? `${getProviderBorderColor(provider)} ${getProviderBgColor(provider)}` : appMode === AppMode.LIGHT ? 'border-gray-200 hover:border-gray-300' : 'border-gray-700 hover:border-gray-600'} ${isConfirmingThis ? appMode === AppMode.LIGHT ? 'bg-red-50 border-red-200' : 'bg-red-900/30 border-red-800' : ''} transition-colors`}>
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center gap-3 flex-grow cursor-pointer" onClick={() => handleSelectAccount(account)}>
                                                        <div className={`h-10 w-10 rounded-full ${appMode === AppMode.LIGHT ? 'bg-gray-200' : 'bg-gray-600'} flex items-center justify-center overflow-hidden flex-shrink-0`}>
                                                            {userData.photoURL
                                                                ? <img src={userData.photoURL} alt='' className="h-full w-full object-cover rounded-full" />
                                                                : <i className={`${getProviderIcon(provider)} ${getProviderColor(provider)}`}></i>
                                                            }
                                                        </div>
                                                        <div className="min-w-0">
                                                            <p className={`text-sm font-medium ${appMode === AppMode.LIGHT ? 'text-gray-800' : 'text-white'} truncate`}>{userData.name || userData.username || userData.email}</p>
                                                            <p className={`text-xs ${appMode === AppMode.LIGHT ? 'text-gray-500' : 'text-gray-300'} truncate`}>{userData.email || userData.username}</p>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-2 ml-2">
                                                        {isConfirmingThis
                                                            ? <div className="flex items-center gap-1">
                                                                <button onClick={() => handleConfirmRemoval(account)} className="text-white bg-red-600 p-1.5 rounded hover:bg-red-700 transition-colors text-xs" aria-label="Confirm removal">
                                                                    <i className="bi bi-check"></i> Confirm
                                                                </button>
                                                                <button onClick={handleCancelRemoval} className={`p-1.5 rounded text-xs ${appMode === AppMode.LIGHT
                                                                    ? 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                                                    : 'bg-gray-700 text-white hover:bg-gray-600'
                                                                    } transition-colors`} aria-label="Cancel removal">
                                                                    <i className="bi bi-x"></i> Cancel
                                                                </button>
                                                            </div>
                                                            : <button onClick={() => handleConfirmRemoval(account)} className={`p-1.5 rounded-full ${appMode === AppMode.LIGHT ? 'text-gray-500 hover:text-red-600 hover:bg-red-50' : 'text-gray-400 hover:text-red-400 hover:bg-red-900/30'} transition-colors`} aria-label="Remove account">
                                                                <i className="bi bi-trash"></i>
                                                            </button>
                                                        }
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </div>
                    {canAddMoreAccounts() && (
                        <div className="mt-6">
                            {showAddOptions
                                ? <div className={`p-4 rounded-lg ${appMode === AppMode.LIGHT ? 'bg-gray-50' : 'bg-gray-700'} border ${appMode === AppMode.LIGHT ? 'border-gray-200' : 'border-gray-600'}`}>
                                    <div className="flex justify-between items-center mb-3">
                                        <h3 className={`text-sm font-medium ${appMode === AppMode.LIGHT ? 'text-gray-700' : 'text-white'}`}>Add account from:</h3>
                                        <button onClick={() => setShowAddOptions(false)} className={`text-sm ${appMode === AppMode.LIGHT ? 'text-gray-500' : 'text-gray-300'} hover:text-gray-700`} aria-label="Cancel">
                                            <i className="bi bi-x"></i>
                                        </button>
                                    </div>
                                    <div className="grid grid-cols-2 gap-3">
                                        <button onClick={handleAddMicrosoftAccount} disabled={addingAccount} className={`p-3 rounded-lg flex flex-col items-center ${appMode === AppMode.LIGHT
                                            ? 'bg-blue-50 hover:bg-blue-100 text-blue-600'
                                            : 'bg-blue-900/30 hover:bg-blue-900/50 text-blue-300'
                                            } transition-colors ${addingAccount ? 'opacity-50 cursor-not-allowed' : ''
                                            }`}>
                                            <i className="bi bi-microsoft text-xl mb-2"></i>
                                            <span className="text-sm">Microsoft</span>
                                        </button>
                                        <button onClick={handleAddGoogleAccount} disabled={addingAccount} className={`p-3 rounded-lg flex flex-col items-center ${appMode === AppMode.LIGHT
                                            ? 'bg-red-50 hover:bg-red-100 text-red-600'
                                            : 'bg-red-900/30 hover:bg-red-900/50 text-red-300'
                                            } transition-colors ${addingAccount ? 'opacity-50 cursor-not-allowed' : ''}`}>
                                            <i className="bi bi-google text-xl mb-2"></i>
                                            <span className="text-sm">Google</span>
                                        </button>
                                    </div>
                                </div>
                                : <button onClick={() => setShowAddOptions(true)} className={`w-full p-3 rounded-lg flex items-center justify-center ${appMode === AppMode.LIGHT ? 'bg-green-50 text-green-600 hover:bg-green-100 border border-green-100' : 'bg-green-900/30 text-green-300 hover:bg-green-900/50 border border-green-800'} transition-colors`}>
                                    <i className="bi bi-plus-circle mr-2"></i>
                                    <span>Add Another Account</span>
                                </button>
                            }
                        </div>
                    )}
                </div>
                <div className="p-4 border-t flex justify-end">
                    <button onClick={onClose} className={`px-4 py-2 rounded-md ${appMode === AppMode.LIGHT ? 'bg-gray-100 hover:bg-gray-200 text-gray-700' : 'bg-gray-700 hover:bg-gray-600 text-white'} transition-colors`}>Close</button>
                </div>
            </div>
        </div>
    );
}

export const NoAuthScreen = ({ appMode, setActiveTab }) => {
    const toggleTab = () => setActiveTab(7);
    return (
        <div className={`flex flex-col gap-4 justify-center items-center w-screen h-screen ${appMode === AppMode.LIGHT ? 'bg-gray-50' : dark_mode_primary}`}>
            <div className={`${appMode === AppMode.LIGHT ? 'bg-white' : dark_mode_secondary} p-8 rounded-lg shadow-lg max-w-md w-full`}>
                <h2 className="text-2xl font-semibold text-center mb-4">Authentication Required</h2>
                <p className={`${appMode === AppMode.LIGHT ? 'text-gray-800' : 'text-gray-400'} mb-6 text-center`}>Please log in with either Google or Microsoft to access this section.</p>
                <button onClick={toggleTab} className="w-full group relative inline-flex items-center justify-center px-6 py-3 overflow-hidden font-medium transition-all bg-blue-600 rounded-md hover:bg-blue-700 text-white">
                    <span className="absolute right-0 w-8 h-32 -mt-12 transition-all duration-1000 transform translate-x-12 bg-white opacity-10 rotate-12 group-hover:-translate-x-20 ease"></span> Connect an Account
                </button>
            </div>
        </div>
    )
}

export const EmptyMessagesList = ({ appMode, toggleNewChatPopup }) => (
    <div className={`w-full h-full flex flex-col items-center justify-center ${appMode === AppMode.LIGHT ? 'bg-gray-50' : dark_mode_primary}`}>
        <div className="relative mb-8">
            <div className="absolute -inset-1 bg-gradient-to-r from-indigo-400 to-blue-500 rounded-full opacity-70 blur-md"></div>
            <div className={`relative w-24 h-24 flex items-center justify-center rounded-full ${appMode === AppMode.LIGHT ? 'bg-white' : 'bg-gray-800'} shadow-lg`}>
                <svg xmlns="http://www.w3.org/2000/svg" className="w-12 h-12 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z" />
                </svg>
            </div>
        </div>
        <h2 className={`text-xl font-semibold mb-3 ${appMode === AppMode.LIGHT ? 'text-gray-800' : 'text-gray-100'}`}>No conversations yet</h2>
        <p className={`text-sm max-w-xs text-center mb-8 ${appMode === AppMode.LIGHT ? 'text-gray-500' : 'text-gray-400'}`}>Connect with colleagues and friends to start messaging</p>
        <button onClick={toggleNewChatPopup} className="group relative inline-flex items-center justify-center px-6 py-3 overflow-hidden font-medium transition-all bg-[#546FFF] rounded-md hover:bg-blue-700 text-white">
            <span className="absolute right-0 w-8 h-32 -mt-12 transition-all duration-1000 transform translate-x-12 bg-white opacity-10 rotate-12 group-hover:-translate-x-40 ease"></span>
            <span className="relative flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg> Start a new chat
            </span>
        </button>
        <div className={`mt-12 flex items-center space-x-2 ${appMode === AppMode.LIGHT ? 'text-gray-400' : 'text-gray-500'}`}>
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-xs">Your conversations will appear here</span>
        </div>
    </div>
);

export const EmptyChat = ({ appMode, toggleNewChatPopup }) => (
    <section className={`flex items-center justify-center min-h-screen px-6 py-20 mx-auto ${appMode === AppMode.LIGHT ? 'bg-gradient-to-br from-white to-gray-50' : dark_mode_primary}`}>
        <div className="flex flex-col items-center max-w-md mx-auto text-center">
            <div className="relative">
                <div className="absolute -inset-1 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full opacity-75 blur"></div>
                <div className={`relative flex items-center justify-center w-20 h-20 rounded-full ${appMode === AppMode.LIGHT ? 'bg-white' : 'bg-gray-800'} shadow-lg`}>
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-10 h-10 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                </div>
            </div>
            <h1 className={`mt-8 text-3xl font-bold tracking-tight ${appMode === AppMode.LIGHT ? 'text-gray-900' : 'text-white'} md:text-4xl`}>Corella</h1>
            <p className={`mt-4 text-lg ${appMode === AppMode.LIGHT ? 'text-gray-600' : 'text-gray-300'}`}>Your conversation space is empty</p>
            <p className={`mt-2 mb-5 text-sm ${appMode === AppMode.LIGHT ? 'text-gray-500' : 'text-gray-400'}`}>Start a new chat to begin your conversation</p>
            <button onClick={toggleNewChatPopup} className="group relative inline-flex items-center justify-center px-6 py-3 overflow-hidden font-medium transition-all bg-[#546FFF] rounded-md hover:bg-blue-700 text-white">
                <span className="absolute right-0 w-8 h-32 -mt-12 transition-all duration-1000 transform translate-x-12 bg-white opacity-10 rotate-12 group-hover:-translate-x-40 ease"></span>
                <span className="relative flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg> Start a new chat
                </span>
            </button>
            <div className={`mt-12 pt-8 border-t ${appMode === AppMode.LIGHT ? 'border-gray-200' : 'border-gray-700'}`}></div>
        </div>
    </section>
);

export function Notifications({ appMode, notifications, getUnreadCount, markAsRead, markAllAsRead, loading, onViewAll }) {
    const recentNotifications = notifications.filter((notification) => {
        const date =
            notification.data.timestamp?.toDate?.() ||
            new Date(notification.data.timestamp);
        const now = new Date();
        const diffMs = now - date;
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
        return diffDays < 1;
    });
    return (
        <div className="relative">
            <div className={`absolute w-4 h-4 top-2 right-6 rotate-45 shadow-md ${appMode === AppMode.LIGHT ? "bg-white" : dark_mode_primary}`} style={{ zIndex: 11 }}></div>
            <div className={`absolute right-0 mt-3 w-96 rounded-2xl shadow-2xl z-20 border ${appMode === AppMode.LIGHT
                ? "bg-white border-gray-100"
                : dark_mode_primary + " border-gray-800"
                }`}>
                <div className={`px-6 py-4 border-b flex justify-between items-center ${appMode === AppMode.LIGHT ? "border-gray-200" : "border-gray-800"}`}>
                    <h3 className={`font-semibold text-lg tracking-tight ${appMode === AppMode.LIGHT ? "text-gray-900" : "text-gray-200"}`}>Notifications</h3>
                    {getUnreadCount() > 0 && <button onClick={markAllAsRead} className="text-xs font-medium text-indigo-600 hover:text-indigo-800 transition">Mark all as read</button>}
                </div>
                <div className="max-h-80 overflow-y-auto">
                    {loading ? (
                        <div className="py-10 text-center text-gray-400">
                            <span className="inline-block animate-spin mr-2">⟳</span> Loading notifications...
                        </div>
                    ) : recentNotifications.length > 0 ? (
                        recentNotifications.map((notification) => (
                            <div key={notification.id} className={`group px-6 py-4 border-b flex flex-col gap-1 cursor-pointer transition rounded-none ${!notification.read
                                ? appMode === AppMode.LIGHT
                                    ? "bg-indigo-50 hover:bg-gray-50 border-gray-100"
                                    : "bg-gray-800 hover:bg-gray-700 border-gray-800"
                                : appMode === AppMode.LIGHT
                                    ? "hover:bg-gray-50 border-gray-100"
                                    : "hover:bg-gray-800 border-gray-800"
                                }`}
                                onClick={() =>
                                    markAsRead(notification.id, notification.isDirectNotification)
                                }
                            >
                                <div className="flex justify-between items-start">
                                    <span className={`text-sm ${!notification.read
                                        ? appMode === AppMode.LIGHT ? "font-semibold text-gray-900" : "font-semibold text-gray-100"
                                        : appMode === AppMode.LIGHT ? "font-normal text-gray-700" : "font-normal text-gray-300"
                                        } leading-tight`}
                                    >{notification.message}
                                    </span>
                                    {!notification.read && (
                                        <span className="h-2 w-2 bg-indigo-500 rounded-full mt-1 ml-2"></span>
                                    )}
                                </div>
                                <p className="text-xs text-gray-400">{notification.time}</p>
                            </div>
                        ))
                    ) : <div className="py-10 text-center text-gray-400">No recent notifications</div>
                    }
                </div>
                <div className="px-6 py-3 border-t text-center border-gray-300">
                    <button className="text-sm font-medium text-indigo-600 hover:text-indigo-800 transition" onClick={onViewAll}>View all notifications</button>
                </div>
            </div>
        </div>
    );
}

export function AllNotifications({ appMode, notifications, markAsRead, markAllAsRead, loading, onClose }) {
    const notificationRef = useRef(null);
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (notificationRef.current && !notificationRef.current.contains(event.target)) {
                onClose();
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };//eslint-disable-next-line
    }, []);
    return (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
            <div ref={notificationRef} className={`relative w-full max-w-2xl rounded-2xl shadow-2xl border ${appMode === AppMode.LIGHT
                ? "bg-white border-gray-100"
                : dark_mode_primary + " border-gray-800"
                }`}
            >
                <div className={`px-8 py-5 border-b flex justify-between items-center ${appMode === AppMode.LIGHT ? "border-gray-100" : "border-gray-800"}`}>
                    <h2 className={`text-xl font-semibold tracking-tight ${appMode === AppMode.LIGHT ? 'text-gray-900' : 'text-gray-200'}`}>All Notifications</h2>
                    <div className="flex items-center gap-2">
                        {notifications.some((n) => !n.read) && (
                            <button onClick={markAllAsRead} className="text-xs font-medium text-indigo-600 hover:text-indigo-800 transition">Mark all as read</button>
                        )}
                        <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition text-xl">
                            <i className="bi bi-x-lg"></i>
                        </button>
                    </div>
                </div>
                <div className="max-h-[70vh] overflow-y-auto px-6 py-4">
                    {loading ? (
                        <div className="py-16 text-center text-gray-400">
                            <span className="inline-block animate-spin mr-2">⟳</span> Loading notifications...
                        </div>
                    ) : notifications.length > 0 ? (
                        notifications.map((notification) => (
                            <div key={notification.id} className={`group px-6 py-4 mb-3 rounded-xl cursor-pointer transition flex flex-col gap-1 ${!notification.read
                                ? appMode === AppMode.LIGHT ? "bg-indigo-50 hover:bg-gray-50" : "bg-gray-800 hover:bg-gray-700"
                                : appMode === AppMode.LIGHT ? "bg-gray-50 hover:bg-gray-100" : "bg-gray-900 hover:bg-gray-800"
                                }`}
                                onClick={() =>
                                    markAsRead(notification.id, notification.isDirectNotification)
                                }
                            >
                                <div className="flex justify-between items-start">
                                    <span className={`text-sm ${!notification.read
                                        ? appMode === AppMode.LIGHT ? "font-semibold text-gray-900" : "font-semibold text-gray-100"
                                        : appMode === AppMode.LIGHT ? "font-normal text-gray-700" : "font-normal text-gray-300"
                                        } leading-tight`}
                                    >{notification.message}
                                    </span>
                                    {!notification.read && (
                                        <span className="h-2 w-2 bg-indigo-500 rounded-full mt-1 ml-2"></span>
                                    )}
                                </div>
                                <p className="text-xs text-gray-400">{notification.time}</p>
                            </div>
                        ))
                    ) : <div className="py-16 text-center text-gray-400">No notifications</div>
                    }
                </div>
                <div className={`px-8 py-5 border-t text-right ${appMode === AppMode.LIGHT ? "border-gray-100" : "border-gray-800"}`}>
                    <button className={`px-6 py-2 rounded-lg font-medium transition ${appMode === AppMode.LIGHT
                        ? "bg-gray-100 hover:bg-gray-200 text-gray-800"
                        : "bg-gray-800 hover:bg-gray-700 text-white"
                        }`} onClick={onClose}
                    >Close
                    </button>
                </div>
            </div>
        </div>
    );
}

export const FilePreviewModal = ({ file, isOpen, onClose }) => {
    const modalRef = useRef(null);
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (modalRef.current && !modalRef.current.contains(event.target)) {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen, onClose]);
    if (!isOpen) return null;

    const renderFileContent = () => {
        const fileType = file.name.split('.').pop().toLowerCase();
        if (['jpg', 'jpeg', 'png', 'gif'].includes(fileType)) {
            return <img src={URL.createObjectURL(file)} className="max-w-full max-h-[80vh] object-contain" alt='' />;
        } else if (fileType === 'pdf') {
            return <embed src={URL.createObjectURL(file)} type="application/pdf" className="w-full h-[80vh]" />;
        } else if (['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'].includes(fileType)) {
            return (
                <div className="p-4 bg-gray-100 rounded text-center">
                    <p className="text-gray-700">Preview not available for {fileType.toUpperCase()} files</p>
                    <a href={URL.createObjectURL(file)} download={file.name} className="mt-2 inline-block px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600">Download File</a>
                </div>
            );
        } else if (['txt', 'csv', 'json'].includes(fileType)) {
            return (
                <div className="bg-white p-4 rounded shadow overflow-auto h-[80vh]">
                    <pre className="whitespace-pre-wrap">{file.text || 'Loading content...'}</pre>
                </div>
            );
        } else {
            return (
                <div className="p-4 bg-gray-100 rounded text-center">
                    <p className="text-gray-700">No preview available for this file type</p>
                    <a href={URL.createObjectURL(file)} download={file.name} className="mt-2 inline-block px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600">Download File</a>
                </div>
            );
        }
    };

    return (
        <div className="fixed inset-0 z-[1055] flex items-center justify-center bg-black bg-opacity-50">
            <div ref={modalRef} className="bg-white rounded-lg shadow-xl w-11/12 max-w-4xl overflow-hidden">
                <div className="flex items-center justify-between p-4 border-b">
                    <h5 className="text-xl font-medium">{file.name}</h5>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-700 focus:outline-none">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>
                <div className="p-4">{renderFileContent()}</div>
            </div>
        </div>
    );
};

export function DeleteConfirmation({ appMode, setShowDeleteConfirm, noteToDelete, handleDeleteNote }) {
    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
            <div className={`${appMode === AppMode.LIGHT ? 'bg-white' : dark_mode_secondary} rounded-xl p-6 w-full max-w-md shadow-xl`}>
                <div className="text-center mb-5">
                    <div className={`mx-auto w-12 h-12 rounded-full flex items-center justify-center mb-4 ${appMode === AppMode.LIGHT ? 'bg-red-100' : 'bg-red-900 bg-opacity-30'}`}>
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                    </div>
                    <h2 className={`text-xl font-bold mb-2 ${appMode === AppMode.LIGHT ? 'text-gray-800' : 'text-white'}`}>Delete {noteToDelete.type}</h2>
                    <p className={`${appMode === AppMode.LIGHT ? 'text-gray-600' : 'text-gray-300'} mb-2`}>Are you sure you want to delete this {noteToDelete.type.toLowerCase()}?</p>
                    <div className={`p-3 rounded-lg mb-4 text-sm text-left ${appMode === AppMode.LIGHT ? 'bg-gray-100 text-gray-700' : 'bg-gray-800 text-gray-300'}`}>
                        <p className="truncate">{noteToDelete.content}</p>
                    </div>
                    <p className={`text-xs ${appMode === AppMode.LIGHT ? 'text-gray-500' : 'text-gray-400'}`}>This action cannot be undone.</p>
                </div>
                <div className="flex gap-3">
                    <button onClick={() => setShowDeleteConfirm(false)} className={`flex-1 px-5 py-2.5 rounded-xl ${appMode === AppMode.LIGHT ? 'bg-gray-100 text-gray-700 hover:bg-gray-200' : 'bg-gray-700 text-gray-200 hover:bg-gray-600'} font-medium transition-colors`}>Cancel</button>
                    <button onClick={handleDeleteNote} className="flex-1 px-5 py-2.5 bg-red-600 text-white rounded-xl hover:bg-red-700 font-medium transition-colors">Delete</button>
                </div>
            </div>
        </div>
    );
};

export const NotificationPrompt = ({ setShowNotificationPrompt, requestNotificationPermission }) => {
    return (
        <div className="fixed bottom-6 right-6 max-w-sm p-4 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
            <div className="flex items-start">
                <div className="flex-shrink-0 pt-0.5">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-500" viewBox="0 0 20 20" fill="currentColor">
                        <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z" />
                    </svg>
                </div>
                <div className="ml-3 flex-1">
                    <h3 className="text-sm font-medium text-gray-900">Enable notifications</h3>
                    <p className="mt-1 text-sm text-gray-500">Allow notifications to receive reminders even when the app is closed.</p>
                    <div className="mt-3 flex space-x-2">
                        <button onClick={() => setShowNotificationPrompt(false)} className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50">Not now</button>
                        <button onClick={requestNotificationPermission} className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded shadow-sm text-white bg-blue-600 hover:bg-blue-700">Allow notifications</button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export const FilePreviewPopup = ({ appMode, file, isOpen, onClose, accessToken, initGoogleClient }) => {
    const popupRef = useRef(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [previewUrl, setPreviewUrl] = useState('');
    const [textContent, setTextContent] = useState('');
    const [isTextFile, setIsTextFile] = useState(false);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (popupRef.current && !popupRef.current.contains(event.target)) {
                onClose();
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };//eslint-disable-next-line
    }, []);

    const getFileExtension = (filename) => {
        if (!filename) return '';
        return filename.slice((filename.lastIndexOf(".") - 1 >>> 0) + 2).toLowerCase();
    };

    const getGoogleAccessToken = async () => {
        if (!window.gapi || !window.gapi.auth) {
            if (initGoogleClient) {
                await initGoogleClient();
            }
        }
        return window.gapi?.auth?.getToken()?.access_token || null;
    };

    useEffect(() => {
        if (!file || !isOpen) return;
        setError(null);
        setLoading(true);
        setTextContent('');
        setIsTextFile(false);
        setPreviewUrl('');

        const loadPreview = async () => {
            try {
                const extension = getFileExtension(file.name);
                if (['txt', 'htm', 'html', 'py', 'js', 'css', 'json', 'md', 'csv'].includes(extension)) {
                    setIsTextFile(true);
                    if (file.provider === AuthProvider.MICROSOFT) {
                        const response = await fetch(`https://graph.microsoft.com/v1.0/me/drive/items/${file.id}/content`, {
                            headers: { Authorization: `Bearer ${accessToken}` }
                        });
                        if (!response.ok) throw new Error(`HTTP error ${response.status}`);
                        const text = await response.text();
                        setTextContent(text);
                    }
                    else if (file.provider === AuthProvider.GOOGLE) {
                        const token = await getGoogleAccessToken();
                        try {
                            const response = await fetch(`https://www.googleapis.com/drive/v3/files/${file.id}?alt=media`, {
                                headers: { Authorization: `Bearer ${token}` }
                            });
                            if (!response.ok) throw new Error(`HTTP error ${response.status}`);
                            const text = await response.text();
                            setTextContent(text);
                        } catch (err) {
                            const response = await fetch(`https://drive.google.com/uc?export=download&id=${file.id}`);
                            const text = await response.text();
                            setTextContent(text);
                        }
                    }
                    return;
                }
                if (file.provider === AuthProvider.GOOGLE &&
                    (file.mimeType?.includes('application/vnd.google-apps.') ||
                        ['gdoc', 'gsheet', 'gslides'].includes(extension))) {
                    if (file.mimeType?.includes('document') || extension === 'gdoc') {
                        setPreviewUrl(`https://docs.google.com/document/d/${file.id}/preview`);
                    }
                    else if (file.mimeType?.includes('spreadsheet') || extension === 'gsheet') {
                        setPreviewUrl(`https://docs.google.com/spreadsheets/d/${file.id}/preview`);
                    }
                    else if (file.mimeType?.includes('presentation') || extension === 'gslides') {
                        setPreviewUrl(`https://docs.google.com/presentation/d/${file.id}/preview`);
                    }
                    else {
                        setPreviewUrl(`https://drive.google.com/file/d/${file.id}/preview`);
                    }
                    return;
                }
                if (file.provider === AuthProvider.MICROSOFT) {
                    if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(extension)) {
                        try {
                            const response = await fetch(`https://graph.microsoft.com/v1.0/me/drive/items/${file.id}/thumbnails/0/large`, {
                                headers: { Authorization: `Bearer ${accessToken}` }
                            });
                            const data = await response.json();
                            if (data.url) {
                                setPreviewUrl(data.url);
                            } else {
                                setPreviewUrl(`https://graph.microsoft.com/v1.0/me/drive/items/${file.id}/content`);
                            }
                        } catch (err) {
                            setPreviewUrl(`https://graph.microsoft.com/v1.0/me/drive/items/${file.id}/content`);
                        }
                    }
                    else if (extension === 'pdf') {
                        setPreviewUrl(`https://mozilla.github.io/pdf.js/web/viewer.html?file=${encodeURIComponent(`https://graph.microsoft.com/v1.0/me/drive/items/${file.id}/content`)}`);
                    }
                    else if (['docx', 'xlsx', 'pptx', 'doc', 'xls', 'ppt'].includes(extension)) {
                        try {
                            const response = await fetch(`https://graph.microsoft.com/v1.0/me/drive/items/${file.id}/preview`, {
                                method: 'POST',
                                headers: {
                                    Authorization: `Bearer ${accessToken}`,
                                    'Content-Type': 'application/json',
                                }
                            });
                            const data = await response.json();
                            if (data.getUrl) {
                                setPreviewUrl(data.getUrl);
                            } else {
                                setPreviewUrl(`https://view.officeapps.live.com/op/view.aspx?src=${encodeURIComponent(`https://graph.microsoft.com/v1.0/me/drive/items/${file.id}/content`)}`);
                            }
                        } catch (err) {
                            setPreviewUrl(`https://view.officeapps.live.com/op/view.aspx?src=${encodeURIComponent(`https://graph.microsoft.com/v1.0/me/drive/items/${file.id}/content`)}`);
                        }
                    }
                    else {
                        setPreviewUrl(`https://graph.microsoft.com/v1.0/me/drive/items/${file.id}/content`);
                    }
                }
                else if (file.provider === AuthProvider.GOOGLE) {
                    if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(extension)) {
                        setPreviewUrl(`https://drive.google.com/uc?export=view&id=${file.id}`);
                    }
                    else if (extension === 'pdf') {
                        setPreviewUrl(`https://drive.google.com/file/d/${file.id}/preview`);
                    }
                    else if (['docx', 'xlsx', 'pptx', 'doc', 'xls', 'ppt'].includes(extension)) {
                        setPreviewUrl(`https://docs.google.com/viewer?embedded=true&url=${encodeURIComponent(`https://drive.google.com/uc?export=download&id=${file.id}`)}`);
                    }
                    else {
                        setPreviewUrl(`https://drive.google.com/file/d/${file.id}/preview`);
                    }
                }
            } catch (err) {
                setError(`Failed to load preview: ${err.message}`);
            } finally {
                setLoading(false);
            }
        };

        loadPreview();//eslint-disable-next-line
    }, [file, isOpen, accessToken, initGoogleClient]);

    if (!isOpen) return null;

    return (
        <div className={`fixed inset-0 ${appMode === AppMode.LIGHT ? 'bg-black' : 'bg-gray-700'} bg-opacity-50 z-50 flex items-center justify-center p-4`}>
            <div ref={popupRef} className={`${appMode === AppMode.LIGHT ? 'bg-white' : dark_mode_primary} rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col`}>
                <div className="p-4 border-b flex justify-between items-center">
                    <h2 className="text-xl font-semibold truncate">{file?.name}</h2>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-700 focus:outline-none">
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                        </svg>
                    </button>
                </div>
                <div className="flex-1 overflow-auto p-4">
                    {loading
                        ? <div className="flex justify-center items-center h-64">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
                        </div>
                        : error
                            ? <div className="text-red-500 text-center p-4">{error}
                                <div className="mt-4">
                                    <a href={file.provider === AuthProvider.MICROSOFT
                                        ? `https://graph.microsoft.com/v1.0/me/drive/items/${file?.id}/content`
                                        : `https://drive.google.com/uc?export=download&id=${file?.id}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded"
                                    >
                                        Download File
                                    </a>
                                </div>
                            </div>
                            : isTextFile
                                ? <div className={`${appMode === AppMode.LIGHT ? 'bg-gray-50' : 'bg-gray-800'} p-4 rounded w-full h-[70vh] overflow-auto`}>
                                    <pre className="whitespace-pre-wrap font-mono text-sm">{textContent}</pre>
                                </div>
                                : <iframe src={previewUrl} className="w-full h-[70vh]" title={file.name} onError={() => setError("Failed to load preview. Try downloading instead.")} allow="autoplay" allowFullScreen></iframe>
                    }
                </div>
                <div className="p-4 border-t flex justify-between items-center">
                    <div>
                        <span className="text-sm text-gray-500">
                            Type: {file?.file?.mimeType || file?.mimeType || getFileExtension(file?.name) || "Unknown"} •
                            Size: {file?.size ? `${Math.round(file.size / 1024)} KB` : "Unknown"}
                        </span>
                    </div>
                    <button onClick={() => window.open(file?.provider === AuthProvider.MICROSOFT
                        ? `https://graph.microsoft.com/v1.0/me/drive/items/${file?.id}/content`
                        : `https://drive.google.com/uc?export=download&id=${file?.id}`, '_blank')}
                        className="text-blue-500 hover:text-blue-600"
                    > Download
                    </button>
                </div>
            </div>
        </div>
    );
};

export const AttachmentPreviewPopup = ({ appMode, attachment, onClose }) => {
    const [previewUrl, setPreviewUrl] = useState('');
    const [textContent, setTextContent] = useState('');
    const [isTextFile, setIsTextFile] = useState(false);
    const [errorDetails, setErrorDetails] = useState(null);//eslint-disable-next-line
    const [isPdfPreviewFailed, setIsPdfPreviewFailed] = useState(false);//eslint-disable-next-line
    const [isUsingFallbackViewer, setIsUsingFallbackViewer] = useState(false);

    useEffect(() => {
        setErrorDetails(null);
        setIsPdfPreviewFailed(false);
        setIsUsingFallbackViewer(false);
        setIsTextFile(false);
        setTextContent('');
        setPreviewUrl('');

        const loadPreview = async () => {
            try {
                const fileType = getFileType();
                const contentUrl = attachment.contentUrl || attachment.url;
                if (fileType === 'text') {
                    setIsTextFile(true);
                    try {
                        const response = await fetch(contentUrl);
                        if (!response.ok) throw new Error(`HTTP error ${response.status}`);
                        const text = await response.text();
                        setTextContent(text);
                    } catch (err) {
                        setErrorDetails(`Failed to load text content: ${err.message}`);
                    }
                    return;
                }
                if (attachment.contentType?.includes('application/vnd.google-apps.')) {
                    const fileId = attachment.id || extractFileIdFromUrl(contentUrl);
                    if (attachment.contentType.includes('document')) {
                        setPreviewUrl(`https://docs.google.com/document/d/${fileId}/preview`);
                    } else if (attachment.contentType.includes('spreadsheet')) {
                        setPreviewUrl(`https://docs.google.com/spreadsheets/d/${fileId}/preview`);
                    } else if (attachment.contentType.includes('presentation')) {
                        setPreviewUrl(`https://docs.google.com/presentation/d/${fileId}/preview`);
                    } else {
                        setPreviewUrl(`https://drive.google.com/file/d/${fileId}/preview`);
                    }
                    return;
                }
                if (fileType === 'pdf') {
                    setPreviewUrl(`https://mozilla.github.io/pdf.js/web/viewer.html?file=${encodeURIComponent(contentUrl)}`);
                    return;
                }
                if (fileType === 'image') {
                    setPreviewUrl(contentUrl);
                } else if (['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'].includes(fileType)) {
                    setPreviewUrl(`https://view.officeapps.live.com/op/view.aspx?src=${encodeURIComponent(contentUrl)}`);
                } else {
                    setPreviewUrl(`https://docs.google.com/viewer?embedded=true&url=${encodeURIComponent(contentUrl)}`);
                }
            } catch (err) {
                setErrorDetails(`Failed to prepare preview: ${err.message}`);
            }
        };

        if (attachment) {
            loadPreview();
        }//eslint-disable-next-line
    }, [attachment]);

    const extractFileIdFromUrl = (url) => {
        if (!url) return '';//eslint-disable-next-line
        const match = url.match(/\/d\/([^\/]+)/);
        return match ? match[1] : '';
    };

    const useFallbackViewer = () => {
        const contentUrl = attachment.contentUrl || attachment.url;
        setPreviewUrl(`https://docs.google.com/viewer?embedded=true&url=${encodeURIComponent(contentUrl)}`);
        setIsUsingFallbackViewer(true);
        setIsPdfPreviewFailed(false);
    };

    const tryAlternateMethod = () => {
        window.open(attachment.contentUrl || attachment.url, '_blank');
    };

    const getFileType = () => {
        if (attachment.contentType) {
            if (attachment.contentType.startsWith('image/')) return 'image';
            if (attachment.contentType === 'application/pdf') return 'pdf';
            if (attachment.contentType.startsWith('video/')) return 'video';
            if (attachment.contentType.startsWith('audio/')) return 'audio';
            if (attachment.contentType.includes('spreadsheet')) return 'xlsx';
            if (attachment.contentType.includes('document')) return 'docx';
            if (attachment.contentType.includes('presentation')) return 'pptx';
            if (attachment.contentType.includes('text/')) return 'text';
        }

        const name = attachment.name || '';
        const extension = name.split('.').pop().toLowerCase();

        if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'].includes(extension)) return 'image';
        if (['pdf'].includes(extension)) return 'pdf';
        if (['doc', 'docx'].includes(extension)) return 'doc';
        if (['xls', 'xlsx'].includes(extension)) return 'xls';
        if (['ppt', 'pptx'].includes(extension)) return 'ppt';
        if (['txt', 'rtf', 'csv', 'md', 'json', 'js', 'html', 'htm', 'css', 'py'].includes(extension)) return 'text';
        if (['mp4', 'webm', 'ogg', 'mov', 'avi', 'mkv'].includes(extension)) return 'video';
        if (['mp3', 'wav', 'ogg', 'aac', 'flac'].includes(extension)) return 'audio';

        return 'unknown';
    };

    const getFileIcon = (filename) => {
        if (!filename) return '';
        const extension = filename.split('.').pop().toLowerCase();
        const iconMap = {
            pdf: '-pdf',
            doc: '-word',
            docx: '-word',
            xls: '-excel',
            xlsx: '-excel',
            ppt: '-ppt',
            pptx: '-ppt',
            jpg: '-image',
            jpeg: '-image',
            png: '-image',
            gif: '-image',
            mp4: '-play',
            webm: '-play',
            mov: '-play',
            mp3: '-music',
            wav: '-music',
            ogg: '-music',
            zip: '-zip',
            rar: '-zip',
            txt: '-text',
            csv: '-text'
        };
        return iconMap[extension] || '';
    };

    if (attachment.isLoading) {
        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className={`w-5/6 md:w-3/4 lg:w-2/3 h-5/6 rounded-lg shadow-lg ${appMode === AppMode.LIGHT ? 'bg-white' : dark_mode_secondary} flex flex-col`}>
                    <div className="p-4 border-b flex justify-between items-center">
                        <h2 className={`font-semibold ${appMode === AppMode.LIGHT ? 'text-gray-800' : 'text-white'}`}>Loading {attachment.name}...</h2>
                        <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
                            <i className="bi bi-x-lg"></i>
                        </button>
                    </div>
                    <div className="flex-1 flex items-center justify-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
                    </div>
                    <div className="p-4 border-t flex justify-end">
                        <button onClick={onClose} className={`px-4 py-2 rounded ${appMode === AppMode.LIGHT ? 'bg-blue-500 hover:bg-blue-600 text-white' : 'bg-blue-600 hover:bg-blue-700 text-white'}`}>Cancel</button>
                    </div>
                </div>
            </div>
        );
    }

    if (attachment.error) {
        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className={`w-5/6 md:w-3/4 lg:w-2/3 h-5/6 rounded-lg shadow-lg ${appMode === AppMode.LIGHT ? 'bg-white' : 'bg-gray-800'} flex flex-col`}>
                    <div className="p-4 border-b flex justify-between items-center">
                        <h2 className={`font-semibold ${appMode === AppMode.LIGHT ? 'text-gray-800' : 'text-white'}`}>{attachment.name}</h2>
                        <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
                            <i className="bi bi-x-lg"></i>
                        </button>
                    </div>
                    <div className="flex-1 flex flex-col items-center justify-center">
                        <i className="bi bi-exclamation-triangle text-red-500 text-4xl mb-4"></i>
                        <p className={`text-center ${appMode === AppMode.LIGHT ? 'text-gray-800' : 'text-white'}`}>{attachment.error}</p>
                        <button className={`mt-6 px-4 py-2 rounded ${appMode === AppMode.LIGHT ? 'bg-blue-500' : 'bg-blue-600'} text-white`} onClick={onClose}>Close</button>
                    </div>
                </div>
            </div>
        );
    }

    const contentUrl = attachment.contentUrl || attachment.url;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className={`w-5/6 md:w-3/4 lg:w-2/3 h-5/6 rounded-lg shadow-lg ${appMode === AppMode.LIGHT ? 'bg-white' : dark_mode_secondary} flex flex-col`}>
                <div className="p-4 border-b flex justify-between items-center">
                    <h2 className={`font-semibold ${appMode === AppMode.LIGHT ? 'text-gray-800' : 'text-white'}`}>{attachment.name}</h2>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
                        <i className="bi bi-x-lg"></i>
                    </button>
                </div>
                <div className="flex-1 overflow-auto p-4">
                    <div className="h-full flex items-center justify-center">
                        {errorDetails ? (
                            <div className="text-center">
                                <i className="bi bi-exclamation-triangle text-red-500 text-4xl mb-4"></i>
                                <p className={`${appMode === AppMode.LIGHT ? 'text-gray-800' : 'text-white'}`}>{errorDetails}</p>
                                <div className="flex flex-col md:flex-row justify-center gap-3 mt-4">
                                    <button className={`px-4 py-2 rounded ${appMode === AppMode.LIGHT ? 'bg-blue-500' : 'bg-blue-600'} text-white`} onClick={useFallbackViewer}>Try Google Docs Viewer</button>
                                    <button className={`px-4 py-2 rounded ${appMode === AppMode.LIGHT ? 'bg-gray-500' : 'bg-gray-600'} text-white`} onClick={tryAlternateMethod}>Open in New Tab</button>
                                    <a href={contentUrl} download={attachment.name} className={`px-4 py-2 rounded text-center ${appMode === AppMode.LIGHT ? 'bg-green-500' : 'bg-green-600'} text-white`}>Download</a>
                                </div>
                            </div>
                        ) : isTextFile ? (
                            <div className={`${appMode === AppMode.LIGHT ? 'bg-gray-50' : 'bg-gray-800'} p-4 rounded w-full h-[70vh] overflow-auto`}>
                                <pre className="whitespace-pre-wrap font-mono text-sm">{textContent}</pre>
                            </div>
                        ) : previewUrl ? (
                            <iframe src={previewUrl} className="w-full h-[70vh]" title={attachment.name} onError={() => setErrorDetails("Failed to load preview. Try an alternative method.")} allow="autoplay" allowFullScreen></iframe>
                        ) : (
                            <div className="text-center">
                                <i className={`bi bi-file-earmark${getFileIcon(attachment.name)} text-5xl ${appMode === AppMode.LIGHT ? 'text-gray-400' : 'text-gray-600'}`}></i>
                                <p className={`mt-4 ${appMode === AppMode.LIGHT ? 'text-gray-800' : 'text-white'}`}>Preview not available for this file type</p>
                                <a href={contentUrl} download={attachment.name} className={`mt-4 inline-block px-4 py-2 rounded ${appMode === AppMode.LIGHT ? 'bg-blue-500' : 'bg-blue-600'} text-white`}>Download</a>
                            </div>
                        )}
                    </div>
                </div>
                <div className="p-4 border-t flex justify-end">
                    <button onClick={onClose} className={`px-4 py-2 rounded ${appMode === AppMode.LIGHT ? 'bg-blue-500 hover:bg-blue-600 text-white' : 'bg-blue-600 hover:bg-blue-700 text-white'}`}>Close</button>
                </div>
            </div>
        </div>
    );
};


export class AttachmentErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null };
    }
    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }
    componentDidCatch(error, errorInfo) { }
    render() {
        if (this.state.hasError) {
            return (
                <div className="flex flex-col items-center justify-center h-full text-red-500">
                    <i className="bi bi-exclamation-triangle text-4xl mb-3"></i>
                    <p>An error occurred while previewing this attachment</p>
                    <p className="text-sm mt-2">{this.state.error?.message}</p>
                    <button onClick={() => this.setState({ hasError: false })} className="mt-4 bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600">Try Again</button>
                </div>
            );
        }
        return this.props.children;
    }
}

export function TaskImageUpload({ appMode, onImageUpload }) {
    const fileInputRef = useRef(null);
    const [imageURL, setImageURL] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const handleImageClick = () => {
        fileInputRef.current.click();
    };

    const handleFileChange = async (event) => {
        const file = event.target.files[0];
        if (!file) return;
        try {
            setIsUploading(true);
            const previewURL = URL.createObjectURL(file);
            setImageURL(previewURL);
            setTimeout(() => {
                setIsUploading(false);
                if (onImageUpload) onImageUpload(previewURL);
            }, 1000);

        } catch (error) {
            setIsUploading(false);
        }
    };
    return (
        <div className="mt-4">
            <div className={`border-2 border-dashed ${appMode === AppMode.LIGHT ? 'border-gray-300 bg-gray-50' : dark_mode_secondary + ' border-gray-700'} rounded-lg p-6 text-center cursor-pointer`} onClick={handleImageClick}>
                {isUploading
                    ? <div className="flex flex-col items-center">
                        <div className={`w-8 h-8 border-4 ${appMode === AppMode.LIGHT ? 'border-blue-600' : 'border-blue-400'} border-t-transparent rounded-full animate-spin mb-2`}></div>
                        <p className="text-sm">Uploading...</p>
                    </div>
                    : imageURL
                        ? <div className="relative">
                            <img src={imageURL} className="max-h-48 mx-auto rounded-md" alt='' />
                            <button className={`absolute top-2 right-2 p-1 rounded-full ${appMode === AppMode.LIGHT ? 'bg-white text-gray-700' : dark_mode_secondary + ' text-white'}`} onClick={(e) => { e.stopPropagation(); setImageURL(null); }}>
                                <i className="bi bi-x"></i>
                            </button>
                        </div>
                        : <div className="flex flex-col items-center">
                            <i className={`bi bi-cloud-upload text-3xl ${appMode === AppMode.LIGHT ? 'text-blue-600' : 'text-blue-400'} mb-2`}></i>
                            <p className="text-sm font-medium">Click or drag files to upload</p>
                            <p className={`text-xs ${appMode === AppMode.LIGHT ? 'text-gray-500' : 'text-gray-400'} mt-1`}> *drag or browse from device</p>
                        </div>
                }
            </div>
            <input type="file" ref={fileInputRef} onChange={handleFileChange} accept="image/*" className="hidden" />
        </div>
    );
};

export const ToDo = ({ appMode, teamId, setSelectedTask }) => {
    const [isDragOver, setIsDragOver] = useState(false);
    const [showAddModal, setShowAddModal] = useState(false);
    const { tasks, loading } = useTasks(TaskStatus.TODO, teamId);
    const handleDragOver = (e) => {
        e.preventDefault();
        if (!isDragOver) setIsDragOver(true);
    };

    const handleDragLeave = () => {
        setIsDragOver(false);
    };

    const handleDrop = async (e) => {
        e.preventDefault();
        setIsDragOver(false);
        const taskId = e.dataTransfer.getData("taskId");
        const currentStatus = e.dataTransfer.getData("currentStatus");
        if (currentStatus !== TaskStatus.TODO && taskId) {
            try {
                await updateTaskStatus(taskId, TaskStatus.TODO, teamId);
            }
            catch (error) { }
        }
    };

    return (
        <div className={`p-3.5 w-1/3 transition-all duration-200 ${isDragOver ? 'bg-blue-50 border-2 border-dashed border-blue-300 rounded-xl' : ''}`} onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop}>
            <div className={`px-4 py-3.5 mb-3 flex items-center justify-between ${appMode === AppMode.LIGHT ? 'bg-white text-gray-800 shadow-sm border border-gray-200' : dark_mode_primary + ' text-white'} rounded-xl`}>
                <h1 className='font-medium text-base flex items-center'>To Do</h1>
                <div className="flex items-center">
                    <button className='w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-500 transition-colors'>
                        <i className="bi bi-three-dots"></i>
                    </button>
                    <button onClick={() => teamId && setShowAddModal(true)} className={`ml-1.5 w-6 h-6 ${appMode === AppMode.LIGHT ? 'bg-blue-50 text-blue-600 hover:bg-blue-100' : 'bg-blue-900/30 text-blue-400 hover:bg-blue-900/50'} rounded-lg flex items-center justify-center transition-colors ${!teamId ? 'opacity-50 cursor-not-allowed' : ''}`} disabled={!teamId} title={teamId ? "Add Task" : "Select a team first"}>
                        <i className="bi bi-plus text-lg"></i>
                    </button>
                </div>
            </div>
            <div className="space-y-3 max-h-[calc(100vh-220px)] overflow-y-auto pr-1 pb-1">
                {!teamId
                    ? <div className={`py-6 text-center rounded-xl bg-transparent ${appMode === AppMode.LIGHT ? 'text-gray-500' : 'text-gray-300'} font-medium`}>
                        <i className="bi bi-people-fill text-2xl block mb-2 text-gray-400"></i> Please select a team
                    </div>
                    : loading
                        ? <div className={`py-6 text-center rounded-xl bg-transparent ${appMode === AppMode.LIGHT ? 'text-gray-500' : 'text-gray-300'} font-medium`}>
                            <div className="inline-block w-6 h-6 border-2 border-t-blue-500 border-gray-200 rounded-full animate-spin mb-2"></div>
                            <span className="block">Loading tasks...</span>
                        </div>
                        : tasks.length === 0
                            ? <div className={`py-6 text-center rounded-xl bg-transparent ${appMode === AppMode.LIGHT ? 'text-gray-500' : 'text-gray-300'} font-medium`}>
                                <i className="bi bi-inbox text-2xl block mb-2 text-gray-400"></i> No tasks found
                            </div>
                            : tasks.map((task) => <Task key={task.id} setSelectedTask={setSelectedTask} appMode={appMode} taskId={task.id} title={task.title} deadline={task.deadline} priority={task.priority} suggestion={true} currentStatus={TaskStatus.TODO} teamId={teamId} onSelectTask={() => setSelectedTask(task)} />)
                }
            </div>
            {showAddModal && <TaskFormModal appMode={appMode} onClose={() => setShowAddModal(false)} teamId={teamId} />}
        </div>
    );
};

export const InProgress = ({ appMode, teamId, setSelectedTask }) => {
    const [isDragOver, setIsDragOver] = useState(false);
    const { tasks, loading } = useTasks(TaskStatus.IN_PROGRESS, teamId);
    const handleDragOver = (e) => {
        e.preventDefault();
        if (!isDragOver) setIsDragOver(true);
    };

    const handleDragLeave = () => {
        setIsDragOver(false);
    };

    const handleDrop = async (e) => {
        e.preventDefault();
        setIsDragOver(false);
        const taskId = e.dataTransfer.getData("taskId");
        const currentStatus = e.dataTransfer.getData("currentStatus");
        if (currentStatus !== TaskStatus.IN_PROGRESS && taskId) {
            try {
                await updateTaskStatus(taskId, TaskStatus.IN_PROGRESS, teamId);
            }
            catch (error) { }
        }
    };

    return (
        <div className={`p-3.5 w-1/3 transition-all duration-200 ${isDragOver ? 'bg-blue-50 border-2 border-dashed border-blue-300 rounded-xl' : ''}`} onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop}>
            <div className={`px-4 py-3.5 mb-3 flex items-center justify-between ${appMode === AppMode.LIGHT ? 'bg-white text-gray-800 shadow-sm border border-gray-200' : dark_mode_primary + ' text-white'} rounded-xl`}>
                <h1 className='font-medium text-base flex items-center'>In Progress</h1>
                <div className="flex items-center">
                    <button className='w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-500 transition-colors'>
                        <i className="bi bi-three-dots"></i>
                    </button>
                    <button className={`ml-1.5 w-6 h-6 ${appMode === AppMode.LIGHT ? 'bg-blue-50 text-blue-600 hover:bg-blue-100' : 'bg-blue-900/30 text-blue-400 hover:bg-blue-900/50'} rounded-lg flex items-center justify-center transition-colors ${!teamId ? 'opacity-50 cursor-not-allowed' : ''}`} disabled={!teamId}>
                        <i className="bi bi-plus text-lg"></i>
                    </button>
                </div>
            </div>
            <div className="space-y-3 max-h-[calc(100vh-220px)] overflow-y-auto pr-1 pb-1">
                {!teamId
                    ? <div className={`py-6 text-center rounded-xl bg-transparent ${appMode === AppMode.LIGHT ? 'text-gray-500' : 'text-gray-300'} font-medium`}>
                        <i className="bi bi-people-fill text-2xl block mb-2 text-gray-400"></i> Please select a team
                    </div>
                    : loading
                        ? <div className={`py-6 text-center rounded-xl bg-transparent ${appMode === AppMode.LIGHT ? 'text-gray-500' : 'text-gray-300'} font-medium`}>
                            <div className="inline-block w-6 h-6 border-2 border-t-yellow-500 border-gray-200 rounded-full animate-spin mb-2"></div>
                            <span className="block">Loading tasks...</span>
                        </div>
                        : tasks.length === 0
                            ? <div className={`py-6 text-center rounded-xl bg-transparent ${appMode === AppMode.LIGHT ? 'text-gray-500' : 'text-gray-300'} font-medium`}>
                                <i className="bi bi-hourglass-split text-2xl block mb-2 text-gray-400"></i> No tasks in progress
                            </div>
                            : tasks.map((task) => <Task key={task.id} setSelectedTask={setSelectedTask} taskId={task.id} appMode={appMode} title={task.title} deadline={task.deadline} priority={task.priority} suggestion={true} currentStatus={TaskStatus.IN_PROGRESS} teamId={teamId} onSelectTask={() => setSelectedTask(task)} />)
                }
            </div>
        </div>
    );
};

export const DoneTasks = ({ appMode, teamId, setSelectedTask }) => {
    const [isDragOver, setIsDragOver] = useState(false);
    const { tasks, loading } = useTasks(TaskStatus.DONE, teamId);
    const handleDragOver = (e) => {
        e.preventDefault();
        if (!isDragOver) setIsDragOver(true);
    };

    const handleDragLeave = () => {
        setIsDragOver(false);
    };

    const handleDrop = async (e) => {
        e.preventDefault();
        setIsDragOver(false);
        const taskId = e.dataTransfer.getData("taskId");
        const currentStatus = e.dataTransfer.getData("currentStatus");
        if (currentStatus !== TaskStatus.DONE && taskId) {
            try {
                await updateTaskStatus(taskId, TaskStatus.DONE, teamId);
            }
            catch (error) { }
        }
    };

    return (
        <div className={`p-3.5 w-1/3 transition-all duration-200 ${isDragOver ? 'bg-blue-50 border-2 border-dashed border-blue-300 rounded-xl' : ''}`} onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop}>
            <div className={`px-4 py-3.5 mb-3 flex items-center justify-between ${appMode === AppMode.LIGHT ? 'bg-white text-gray-800 shadow-sm border border-gray-200' : dark_mode_primary + ' text-white'} rounded-xl`}>
                <h1 className='font-medium text-base flex items-center'>Completed</h1>
                <div className="flex items-center">
                    <button className='w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-500 transition-colors'>
                        <i className="bi bi-three-dots"></i>
                    </button>
                    <button className={`ml-1.5 w-6 h-6 ${appMode === AppMode.LIGHT ? 'bg-blue-50 text-blue-600 hover:bg-blue-100' : 'bg-blue-900/30 text-blue-400 hover:bg-blue-900/50'} rounded-lg flex items-center justify-center transition-colors ${!teamId ? 'opacity-50 cursor-not-allowed' : ''}`} disabled={!teamId}>
                        <i className="bi bi-plus text-lg"></i>
                    </button>
                </div>
            </div>
            <div className="space-y-3 max-h-[calc(100vh-220px)] overflow-y-auto pr-1 pb-1">
                {!teamId
                    ? <div className={`py-6 text-center rounded-xl bg-transparent ${appMode === AppMode.LIGHT ? 'text-gray-500' : 'text-gray-300'} font-medium`}>
                        <i className="bi bi-people-fill text-2xl block mb-2 text-gray-400"></i> Please select a team
                    </div>
                    : loading
                        ? <div className={`py-6 text-center rounded-xl bg-transparent ${appMode === AppMode.LIGHT ? 'text-gray-500' : 'text-gray-300'} font-medium`}>
                            <div className="inline-block w-6 h-6 border-2 border-t-green-500 border-gray-200 rounded-full animate-spin mb-2"></div>
                            <span className="block">Loading tasks...</span>
                        </div>
                        : tasks.length === 0
                            ? <div className={`py-6 text-center rounded-xl bg-transparent ${appMode === AppMode.LIGHT ? 'text-gray-500' : 'text-gray-300'} font-medium`}>
                                <i className="bi bi-clipboard-check text-2xl block mb-2 text-gray-400"></i> No completed tasks
                            </div>
                            : tasks.map((task) => <Task key={task.id} setSelectedTask={setSelectedTask} taskId={task.id} appMode={appMode} title={task.title} deadline={task.deadline} priority={task.priority} suggestion={false} currentStatus={TaskStatus.DONE} teamId={teamId} onSelectTask={() => setSelectedTask(task)} />)
                }
            </div>
        </div>
    );
};

export const Task = ({ appMode, taskId, title, deadline, priority, suggestion, currentStatus, onSelectTask, teamId, setSelectedTask }) => {
    const [loading, setLoading] = useState(false);
    const [showMenu, setShowMenu] = useState(false);
    const [showPopup, setShowPopup] = useState(false);
    const [explanation, setExplanation] = useState(null);//eslint-disable-next-line
    const [taskDetails, setTaskDetails] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(false);//eslint-disable-next-line
    const [deleteSuccess, setDeleteSuccess] = useState(false);
    const [showDeletePopup, setShowDeletePopup] = useState(false);
    const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });

    useEffect(() => {
        const fetchTaskDetails = async (taskId, teamId) => {
            try {
                setLoading(true);
                const isTeamTask = teamId ? !teamId.startsWith('personal_') : false;
                if (isTeamTask) {
                    const taskRef = ref(realtime, `tasks/${taskId}`);
                    const snapshot = await get(taskRef);

                    if (snapshot.exists()) {
                        const taskData = snapshot.val();
                        let assigneeDetails = null;
                        if (taskData.assignedTo) {
                            try {
                                const userQuery = query(
                                    collection(firestore, 'users'),
                                    where('userId', '==', taskData.assignedTo)
                                );
                                const userSnapshot = await getDocs(userQuery);
                                if (!userSnapshot.empty) {
                                    assigneeDetails = {
                                        id: taskData.assignedTo,
                                        ...userSnapshot.docs[0].data()
                                    };
                                } else {
                                    const userDoc = await getDoc(doc(firestore, 'users', taskData.assignedTo));
                                    if (userDoc.exists()) {
                                        assigneeDetails = {
                                            id: taskData.assignedTo,
                                            ...userDoc.data()
                                        };
                                    }
                                }
                            }
                            catch (error) { }
                        }
                        setSelectedTask({
                            id: taskId,
                            ...taskData,
                            assigneeDetails: assigneeDetails,
                        });
                    }
                    else { }
                } else {
                    const taskDoc = await getDoc(doc(firestore, 'tasks', taskId));
                    if (taskDoc.exists()) {
                        const taskData = taskDoc.data();
                        let assigneeDetails = null;
                        if (taskData.assignedTo) {
                            const userDoc = await getDoc(doc(firestore, 'users', taskData.assignedTo));
                            if (userDoc.exists()) {
                                assigneeDetails = {
                                    id: userDoc.id,
                                    ...userDoc.data(),
                                };
                            }
                        }
                        setSelectedTask({
                            id: taskDoc.id,
                            ...taskData,
                            assigneeDetails: assigneeDetails,
                        });
                    }
                    else { }
                }
            }
            catch (error) { }
            finally {
                setLoading(false);
            }
        };
        fetchTaskDetails();
    }, [setSelectedTask]);

    const handleExplainTask = async () => {
        setShowPopup(true);
        if (!explanation) {
            setLoading(true);
            const response = await getTaskExplanation(title);
            setExplanation(response);
            setLoading(false);
        }
    };

    const handleRightClick = (e) => {
        e.preventDefault();
        const x = e.clientX;
        const y = e.clientY;
        const menuWidth = 180;
        const menuHeight = 200;
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        const adjustedX = Math.min(x, viewportWidth - menuWidth);
        const adjustedY = Math.min(y, viewportHeight - menuHeight);
        setMenuPosition({ x: adjustedX, y: adjustedY });
        setShowMenu(true);
    };

    const handleChangeStatus = async (newStatus) => {
        setLoading(true);
        await updateTaskStatus(taskId, newStatus);
        setShowMenu(false);
        setLoading(false);
    };

    const handleDeleteTask = () => {
        setShowDeletePopup(true);
        setShowMenu(false);
    };

    const confirmDelete = async () => {
        setDeleteLoading(true);
        try {
            deleteTask(taskId, teamId);
            setDeleteSuccess(true);
        }
        catch (error) { }
        finally {
            setDeleteLoading(false);
            setShowDeletePopup(false);
        }
    };

    const handleViewDetails = () => {
        if (onSelectTask && taskDetails) {
            onSelectTask({
                id: taskId,
                title,
                priority,
                ...taskDetails,
            });
        } else {
            const fetchAndSelectTask = async () => {
                try {
                    setLoading(true);
                    const isTeamTask = !teamId.startsWith('personal_');
                    if (isTeamTask) {
                        const taskRef = ref(realtime, `teams/${teamId}/tasks/${taskId}`);
                        const snapshot = await get(taskRef);
                        if (snapshot.exists()) {
                            const taskData = snapshot.val();
                            onSelectTask({
                                id: taskId,
                                ...taskData,
                            });
                        }
                    } else {
                        const taskDoc = await getDoc(doc(firestore, 'tasks', taskId));
                        if (taskDoc.exists()) {
                            onSelectTask({
                                id: taskDoc.id,
                                ...taskDoc.data()
                            });
                        }
                    }
                }
                catch (error) { }
                finally {
                    setLoading(false);
                }
            };
            fetchAndSelectTask();
        }
    };

    const handleDragStart = (e) => {
        e.dataTransfer.setData("taskId", taskId);
        e.dataTransfer.setData("currentStatus", currentStatus);
        setTimeout(() => { e.target.style.opacity = '0.4'; }, 0);
    };

    const handleDragEnd = (e) => {
        e.target.style.opacity = '1';
    };

    useEffect(() => {
        const closeMenu = () => setShowMenu(false);
        if (showMenu) {
            document.addEventListener('click', closeMenu);
            return () => document.removeEventListener('click', closeMenu);
        }
    }, [showMenu]);

    const priorityColor = {
        bg: priority === Priority.HIGH
            ? appMode === AppMode.LIGHT ? 'bg-red-50' : 'bg-red-900/20'
            : priority === Priority.MEDIUM
                ? appMode === AppMode.LIGHT ? 'bg-amber-50' : 'bg-amber-900/20'
                : appMode === AppMode.LIGHT ? 'bg-green-50' : 'bg-green-900/20',
        text: priority === Priority.HIGH
            ? 'text-red-600'
            : priority === Priority.MEDIUM
                ? 'text-amber-600'
                : 'text-green-600'
    };
    return (
        <div className='relative'>
            <div className={`px-3.5 py-4 ${appMode === AppMode.LIGHT ? 'bg-white' : dark_mode_primary} w-full h-60 justify-between rounded-xl shadow-sm hover:shadow-md transition-all duration-300 border ${appMode === AppMode.LIGHT ? 'border-gray-100 hover:border-gray-200' : 'border-gray-700 hover:border-gray-600'} cursor-pointer relative`} onContextMenu={handleRightClick} draggable={true} onDragStart={handleDragStart} onDragEnd={handleDragEnd} onClick={handleViewDetails}>
                <div className="flex justify-between items-start mb-3">
                    <h1 className='w-3/4 font-semibold text-base truncate leading-tight'>{title}</h1>
                    {suggestion
                        ? <button onClick={handleExplainTask} className={`w-6 h-6 ${appMode === AppMode.LIGHT ? 'bg-[#E8EAFF] text-[#6772FE] hover:bg-[#D8DAFF]' : dark_mode_secondary + ' text-white hover:opacity-80'} rounded-lg flex items-center justify-center transition-colors duration-200`} title="Get AI explanation">
                            <i className="bi bi-lightbulb text-sm"></i>
                        </button>
                        : <></>
                    }
                </div>
                <div className="py-3 w-2/3 flex items-center mb-3">
                    <div className={`py-1.5 px-3 ${appMode === AppMode.LIGHT ? 'bg-gray-50 border border-gray-200' : 'bg-gray-800 border border-gray-700'} rounded-lg text-xs font-medium flex items-center`}>
                        <i className="bi bi-calendar-event mr-1.5 text-blue-500"></i> {deadline}
                    </div>
                </div>
                <div className="flex justify-between items-center mb-4">
                    <div className={`text-sm flex items-center ${appMode === AppMode.LIGHT ? 'text-gray-700' : 'text-gray-300'}`}>
                        <i className="bi bi-clock mr-1.5 text-blue-500"></i> {taskDetails?.timeEstimate || "1 Hour"}</div>
                    <div className={`px-2.5 py-1 rounded-full text-xs font-medium ${priorityColor.bg} ${priorityColor.text}`}>{priority}</div>
                </div>
                <div className="mt-4">
                    <button onClick={(e) => { e.stopPropagation(); handleViewDetails(); }} className={`w-full py-2 ${appMode === AppMode.LIGHT ? 'bg-blue-50 text-blue-600 hover:bg-blue-100 border border-blue-100' : 'bg-blue-900/20 text-blue-400 hover:bg-blue-900/30 border border-blue-900/30'} rounded-lg transition-colors text-xs font-medium flex items-center justify-center`}>Click to View Details</button>
                </div>
                {loading && (
                    <div className="absolute inset-0 bg-black bg-opacity-20 backdrop-blur-sm flex items-center justify-center rounded-xl z-10">
                        <div className="w-8 h-8 border-2 border-t-blue-500 border-blue-200 rounded-full animate-spin"></div>
                    </div>
                )}
            </div>
            {showPopup && <ExplanationPopup appMode={appMode} title={'Task Explanation'} explanation={loading ? null : explanation} onClose={() => setShowPopup(false)} setShowPopup={setShowPopup} />}
            {showMenu && (
                <div className={`fixed z-50 ${appMode === AppMode.LIGHT ? 'bg-white border border-gray-200 shadow-lg' : dark_mode_primary + ' border border-gray-700 shadow-lg'} rounded-xl p-1 min-w-[180px]`} style={{ top: `${menuPosition.y}px`, left: `${menuPosition.x}px` }}>
                    <ul>
                        {currentStatus !== TaskStatus.TODO && (
                            <li className={`p-3 ${appMode === AppMode.LIGHT ? 'hover:bg-blue-50 hover:text-blue-700' : 'hover:bg-gray-800 hover:text-blue-400'} rounded-lg cursor-pointer text-sm font-medium transition-colors flex items-center`} onClick={() => handleChangeStatus(TaskStatus.TODO)}>
                                <i className="bi bi-circle mr-2 text-blue-500"></i> Move to To Do
                            </li>
                        )}
                        {currentStatus !== TaskStatus.IN_PROGRESS && (
                            <li className={`p-3 ${appMode === AppMode.LIGHT ? 'hover:bg-yellow-50 hover:text-yellow-700' : 'hover:bg-gray-800 hover:text-yellow-400'} rounded-lg cursor-pointer text-sm font-medium transition-colors flex items-center`} onClick={() => handleChangeStatus(TaskStatus.IN_PROGRESS)}>
                                <i className="bi bi-arrow-repeat mr-2 text-yellow-500"></i> Move to In Progress
                            </li>
                        )}
                        {currentStatus !== TaskStatus.DONE && (
                            <li className={`p-3 ${appMode === AppMode.LIGHT ? 'hover:bg-green-50 hover:text-green-700' : 'hover:bg-gray-800 hover:text-green-400'} rounded-lg cursor-pointer text-sm font-medium transition-colors flex items-center`} onClick={() => handleChangeStatus(TaskStatus.DONE)}>
                                <i className="bi bi-check-circle mr-2 text-green-500"></i> Move to Completed
                            </li>
                        )}
                        <li className={`p-3 ${appMode === AppMode.LIGHT ? 'hover:bg-red-50 hover:text-red-700' : 'hover:bg-red-900/20 hover:text-red-400'} rounded-lg cursor-pointer text-sm font-medium text-red-500 transition-colors flex items-center`} onClick={handleDeleteTask}>
                            <i className="bi bi-trash mr-2"></i> Delete Task
                        </li>
                    </ul>
                </div>
            )}
            {showDeletePopup && <DeletePopup appMode={appMode} title={'Task'} clickAction={confirmDelete} onCancelled={() => setShowDeletePopup(false)} deleteLoading={deleteLoading} />}
        </div>
    );
};

export const AssignmentDetails = ({ appMode, task, type = "assignee" }) => {
    const [userData, setUserData] = useState(null);
    useEffect(() => {
        const fetchUserDetails = async () => {
            const userId = type === "assignee" ? task?.assignedTo : task?.assignedBy;
            if (userId) {
                try {
                    const userDoc = await getDoc(doc(firestore, 'users', userId));
                    if (userDoc.exists()) {
                        setUserData(userDoc.data());
                    } else {
                        const userQuery = query(
                            collection(firestore, 'users'),
                            where('userId', '==', userId)
                        );
                        const userSnapshot = await getDocs(userQuery);
                        if (!userSnapshot.empty) {
                            setUserData(userSnapshot.docs[0].data());
                        } else {
                            setUserData({
                                displayName: userId === auth.currentUser.uid ? "Me" : "Unknown User",
                                email: "unknown@example.com"
                            });
                        }
                    }
                }
                catch (error) { }
            }
        };

        fetchUserDetails();
    }, [task, type]);

    return (
        <div className="px-3.5 py-2 flex justify-between items-center">
            <h1 className={appMode === AppMode.LIGHT ? 'text-[#8E92BC]' : 'text-gray-400'}>
                {type === "assignee" ? 'Assigned To' : 'Assigned By'}
            </h1>
            <h1 className={appMode === AppMode.LIGHT ? 'text-[#141522]' : 'text-white'}>
                {userData?.displayName || ((type === "assignee" ? task?.assignedTo : task?.assignedBy) === auth.currentUser?.uid
                    ? "Me"
                    : type === "assignee" ? task?.assignedTo : task?.assignedBy) || "Unknown"
                }
            </h1>
        </div>
    );
};

export const TaskFormModal = ({ appMode, onClose, initialData = {}, teamId }) => {
    const taskRef = useRef(null);
    const [loading, setLoading] = useState(false);
    const [teamMembers, setTeamMembers] = useState([]);
    const [formData, setFormData] = useState({
        ...initialData,
        teamId: teamId,
        title: initialData.title || '',
        deadline: initialData.deadline || '',
        description: initialData.description || '',
        priority: initialData.priority || Priority.MEDIUM,
        timeEstimate: initialData.timeEstimate || '1 Hour',
        assignedTo: initialData.assignedTo || auth.currentUser.uid,
        acceptanceCriteria: initialData.acceptanceCriteria || [''],
    });
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (taskRef.current && !taskRef.current.contains(event.target)) {
                onClose();
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };//eslint-disable-next-line
    }, []);
    useEffect(() => {
        const fetchTeamMembers = async () => {
            if (teamId) {
                try {
                    const isTeamTask = !teamId.startsWith('personal_');
                    if (isTeamTask) {
                        const teamRef = ref(realtime, `teams/${teamId}`);
                        const teamSnapshot = await get(teamRef);
                        if (teamSnapshot.exists()) {
                            const teamData = teamSnapshot.val();
                            const memberIds = teamData.members || [];
                            const membersData = [];
                            for (const memberId of memberIds) {
                                const userDoc = await getDoc(doc(firestore, 'users', memberId));
                                if (userDoc.exists()) {
                                    membersData.push({
                                        id: memberId,
                                        displayName: userDoc.data().displayName || userDoc.data().email || "User",
                                        email: userDoc.data().email,
                                        ...userDoc.data()
                                    });
                                } else {
                                    const userQuery = query(
                                        collection(firestore, 'users'),
                                        where('userId', '==', memberId)
                                    );
                                    const userSnapshot = await getDocs(userQuery);
                                    if (!userSnapshot.empty) {
                                        const userData = userSnapshot.docs[0].data();
                                        membersData.push({
                                            id: memberId,
                                            displayName: userData.displayName || userData.email || "User",
                                            email: userData.email,
                                            ...userData
                                        });
                                    } else {
                                        membersData.push({
                                            id: memberId,
                                            displayName: "User",
                                            email: "user@example.com",
                                        });
                                    }
                                }
                            }
                            setTeamMembers(membersData);
                        }
                    } else {
                        const teamDoc = await getDoc(doc(firestore, 'teams', teamId));
                        if (teamDoc.exists()) {
                            const memberIds = teamDoc.data().members || [];
                            const membersData = [];
                            for (const memberId of memberIds) {
                                const userDoc = await getDoc(doc(firestore, 'users', memberId));
                                if (userDoc.exists()) {
                                    membersData.push({
                                        id: userDoc.id,
                                        ...userDoc.data()
                                    });
                                }
                            }
                            setTeamMembers(membersData);
                        }
                    }
                }
                catch (error) { }
            }
        };
        fetchTeamMembers();
    }, [teamId]);

    const handleAddCriterion = () => {
        setFormData(prev => ({
            ...prev,
            acceptanceCriteria: [...prev.acceptanceCriteria, ''],
        }));
    };

    const handleCriterionChange = (index, value) => {
        const newCriteria = [...formData.acceptanceCriteria];
        newCriteria[index] = value;
        setFormData(prev => ({
            ...prev,
            acceptanceCriteria: newCriteria
        }));
    };

    const handleRemoveCriterion = (index) => {
        const newCriteria = formData.acceptanceCriteria.filter((_, i) => i !== index);
        setFormData(prev => ({
            ...prev,
            acceptanceCriteria: newCriteria
        }));
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            if (initialData.id) {
                await updateTaskDetails(initialData.id, formData, teamId);
            } else {
                await addTask({ ...formData, teamId: teamId });
            }
            onClose();
        } catch (error) {
            alert("Failed to save task. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div ref={taskRef} className={`h-[95vh] w-full max-w-md p-6 rounded-lg ${appMode === AppMode.LIGHT ? 'bg-white' : dark_mode_primary}`}>
                <div className="h-1/6 flex justify-between items-center mb-4">
                    <h2 className="text-xl font-bold">{initialData.id ? 'Edit Task' : 'New Task'}</h2>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
                        <i className="bi bi-x-lg"></i>
                    </button>
                </div>
                <form onSubmit={handleSubmit} className='h-5/6 overflow-y-auto'>
                    <div className="mb-4">
                        <label className="block mb-2">Title</label>
                        <input type="text" name="title" value={formData.title} onChange={handleChange} className={`w-full p-2 border rounded ${appMode === AppMode.LIGHT ? 'border-gray-300' : 'border-gray-700 bg-gray-800'}`} required />
                    </div>
                    <div className="mb-4">
                        <label className="block mb-2">Description</label>
                        <textarea name="description" value={formData.description} onChange={handleChange} className={`w-full p-2 border rounded ${appMode === AppMode.LIGHT ? 'border-gray-300' : 'border-gray-700 bg-gray-800'}`} rows="3"></textarea>
                    </div>
                    <div className="mb-4">
                        <label className="block mb-2">Acceptance Criteria</label>
                        {formData.acceptanceCriteria.map((criterion, index) => (
                            <div key={index} className="flex items-center mb-2">
                                <input type="text" value={criterion} onChange={(e) => handleCriterionChange(index, e.target.value)} className={`flex-1 p-2 border rounded ${appMode === AppMode.LIGHT ? 'border-gray-300' : 'border-gray-700 bg-gray-800'}`} placeholder={`Criterion ${index + 1}`} />
                                {index > 0 && (
                                    <button type="button" onClick={() => handleRemoveCriterion(index)} className="ml-2 text-red-500">
                                        <i className="bi bi-x-lg"></i>
                                    </button>
                                )}
                            </div>
                        ))}
                        <button type="button" onClick={handleAddCriterion} className={`w-full mt-2 p-3 text-sm ${appMode === AppMode.LIGHT ? 'bg-blue-100 text-blue-600' : 'bg-blue-900 text-blue-200'} rounded`}>Add Criterion</button>
                    </div>
                    <div className="mb-4">
                        <label className="block mb-2">Deadline</label>
                        <input type="date" name="deadline" value={formData.deadline} onChange={handleChange} className={`w-full p-2 border rounded ${appMode === AppMode.LIGHT ? 'border-gray-300' : 'border-gray-700 bg-gray-800'}`} />
                    </div>
                    <div className="mb-4">
                        <label className="block mb-2">Priority</label>
                        <select name="priority" value={formData.priority} onChange={handleChange} className={`w-full p-2 border rounded ${appMode === AppMode.LIGHT ? 'border-gray-300' : 'border-gray-700 bg-gray-800'}`}>
                            <option value={Priority.LOW}>Low</option>
                            <option value={Priority.MEDIUM}>Medium</option>
                            <option value={Priority.HIGH}>High</option>
                        </select>
                    </div>
                    <div className="mb-4">
                        <label className="block mb-2">Time Estimate</label>
                        <input type="text" name="timeEstimate" value={formData.timeEstimate} onChange={handleChange} className={`w-full p-2 border rounded ${appMode === AppMode.LIGHT ? 'border-gray-300' : 'border-gray-700 bg-gray-800'}`} placeholder="e.g. 1 Hour, 2 Days" />
                    </div>
                    <div className="mb-4">
                        <label className="block mb-2">Assign To</label>
                        <select name="assignedTo" value={formData.assignedTo} onChange={handleChange} className={`w-full p-2 border rounded ${appMode === AppMode.LIGHT ? 'border-gray-300' : 'border-gray-700 bg-gray-800'}`}>
                            <option value={auth.currentUser.uid}>Me</option>
                            {teamMembers.filter(member => member.id !== auth.currentUser.uid).map(member => <option key={member.id} value={member.id}>{member.displayName || member.email || member.id}</option>)}
                        </select>
                    </div>
                    <div className="flex justify-end space-x-3">
                        <button type="button" onClick={onClose} className={`px-4 py-2 border rounded ${appMode === AppMode.LIGHT ? 'hover:bg-gray-100' : 'hover:bg-gray-800'}`} disabled={loading}>Cancel</button>
                        <button type="submit" className={`px-4 py-2 bg-blue-600 text-white rounded ${loading ? 'opacity-70' : 'hover:bg-blue-700'}`} disabled={loading}>{loading ? 'Saving...' : 'Save Task'}</button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export const CreateTeamModal = ({ appMode, onClose, onTeamCreated }) => {
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [searchLoading, setSearchLoading] = useState(false);
    const [formData, setFormData] = useState({ name: '', description: '', members: [] });

    useEffect(() => {
        setSelectedUsers([{
            role: 'admin',
            id: auth.currentUser.uid,
            email: auth.currentUser.email,
            photoURL: auth.currentUser.photoURL,
            displayName: auth.currentUser.displayName || auth.currentUser.email,
        }]);
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
    };

    const handleSearch = async () => {
        if (!searchTerm.trim()) return;
        setSearchLoading(true);
        try {
            const usersRef = collection(firestore, 'users');//eslint-disable-next-line
            const simpleQuery = query(usersRef, limit(10));
            const emailQuery = query(usersRef, where('email', '>=', searchTerm), where('email', '<=', searchTerm + '\uf8ff'), limit(5));
            const nameQuery = query(usersRef, where('displayName', '>=', searchTerm), where('displayName', '<=', searchTerm + '\uf8ff'), limit(5));
            const [emailSnapshot, nameSnapshot] = await Promise.all([
                getDocs(emailQuery),
                getDocs(nameQuery)
            ]);
            const emailResults = emailSnapshot.docs.map(doc => {
                const userData = doc.data();
                return {
                    id: userData.userId,
                    ...userData
                };
            });

            const nameResults = nameSnapshot.docs.map(doc => {
                const userData = doc.data();
                return {
                    id: userData.userId,
                    ...userData
                };
            });
            const combinedResults = [...emailResults];
            nameResults.forEach(user => {
                if (!combinedResults.some(u => u.id === user.id)) {
                    combinedResults.push(user);
                }
            });

            const filteredResults = combinedResults.filter(user =>
                !selectedUsers.some(u => u.id === user.id)
            );
            setSearchResults(filteredResults);
        } catch (error) {
        } finally {
            setSearchLoading(false);
        }
    };

    const handleAddUser = (user) => {
        setSelectedUsers(prev => [...prev, { ...user, role: 'member' }]);
        setSearchResults(prev => prev.filter(u => u.id !== user.id));
        setSearchTerm('');
    };

    const handleRemoveUser = (userId) => {
        setSelectedUsers(prev => prev.filter(user => user.id !== userId));
    };

    const handleChangeRole = (userId, newRole) => {
        setSelectedUsers(prev => prev.map(user => user.id === userId ? { ...user, role: newRole } : user));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const teamsRef = ref(realtime, 'teams');
            const newTeamRef = push(teamsRef);
            const memberIds = selectedUsers.map(user => user.uid || user.id);
            const teamData = {
                name: formData.name,
                description: formData.description,
                createdBy: auth.currentUser.uid,
                createdAt: new Date().toISOString(),
                members: memberIds,
                roles: selectedUsers.reduce((acc, user) => {
                    const userId = user.uid || user.id;
                    acc[userId] = user.role;
                    return acc;
                }, {})
            };
            await set(newTeamRef, teamData);//eslint-disable-next-line
            await addDoc(collection(firestore, 'activityLogs'), {
                teamId: newTeamRef.key,
                userId: auth.currentUser.uid,
                action: 'created team',
                timestamp: new Date()
            });
            onTeamCreated({ id: newTeamRef.key, ...teamData });
            onClose();
        } catch (error) {
            alert("Failed to create team. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className={`fixed inset-0 ${appMode === AppMode.LIGHT ? 'bg-black' : 'bg-gray-700'} bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50`}>
            <div className={`w-full max-w-2xl p-6 rounded-lg ${appMode === AppMode.LIGHT ? 'bg-white' : dark_mode_primary} shadow-xl border ${appMode === AppMode.LIGHT ? 'border-gray-200' : 'border-gray-600'} transition-all duration-300`}>
                <div className={`flex justify-between items-center mb-6 border-b pb-4 ${appMode === AppMode.LIGHT ? 'border-gray-200' : 'border-gray-700'}`}>
                    <h2 className={`text-xl font-bold ${appMode === AppMode.LIGHT ? 'text-gray-800' : 'text-gray-100'}`}>Create New Team</h2>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full p-2 transition-colors duration-200">
                        <i className="bi bi-x-lg"></i>
                    </button>
                </div>
                <form onSubmit={handleSubmit}>
                    <div className="mb-5">
                        <label className={`block mb-2 font-medium ${appMode === AppMode.LIGHT ? 'text-gray-700' : 'text-gray-200'}`}>Team Name</label>
                        <input type="text" name="name" value={formData.name} onChange={handleChange} className={`w-full p-3 border rounded-lg focus:ring-2 focus:outline-none transition-colors duration-200 ${appMode === AppMode.LIGHT ? 'border-gray-300 focus:ring-blue-200 focus:border-blue-400' : 'border-gray-700 bg-gray-800 focus:ring-blue-800 focus:border-blue-600'}`} required />
                    </div>
                    <div className="mb-5">
                        <label className={`block mb-2 font-medium ${appMode === AppMode.LIGHT ? 'text-gray-700' : 'text-gray-200'}`}>Description</label>
                        <textarea name="description" value={formData.description} onChange={handleChange} className={`w-full p-3 border rounded-lg focus:ring-2 focus:outline-none transition-colors duration-200 ${appMode === AppMode.LIGHT ? 'border-gray-300 focus:ring-blue-200 focus:border-blue-400' : 'border-gray-700 bg-gray-800 focus:ring-blue-800 focus:border-blue-600'}`} rows="3"></textarea>
                    </div>
                    <div className="mb-6">
                        <label className={`block mb-2 font-medium ${appMode === AppMode.LIGHT ? 'text-gray-700' : 'text-gray-200'}`}>Team Members</label>
                        <div className="mb-4">
                            <div className="flex">
                                <input type="text" value={searchTerm} onChange={handleSearchChange} className={`flex-1 p-3 border rounded-l-lg focus:outline-none transition-colors duration-200 ${appMode === AppMode.LIGHT ? 'border-gray-300 focus:border-blue-400' : 'border-gray-700 bg-gray-800 focus:border-blue-600'}`} placeholder="Search users by email or name" />
                                <button type="button" onClick={handleSearch} className={`px-4 py-2 bg-blue-600 text-white rounded-r-lg transition-colors duration-200 ${searchLoading ? 'opacity-70' : 'hover:bg-blue-700'}`} disabled={searchLoading || !searchTerm.trim()}>{searchLoading ? <i className="bi bi-hourglass"></i> : <i className="bi bi-search"></i>}</button>
                            </div>
                            {searchResults.length > 0 && (
                                <div className={`mt-2 border rounded-lg shadow-md ${appMode === AppMode.LIGHT ? 'border-gray-200 bg-white' : 'border-gray-700 bg-gray-800'}`}>
                                    {searchResults.map(user => (
                                        <div key={user.id} className={`p-3 flex justify-between items-center ${appMode === AppMode.LIGHT ? 'hover:bg-gray-50' : 'hover:bg-gray-700'} cursor-pointer transition-colors duration-150 ${appMode === AppMode.LIGHT ? 'border-b border-gray-100' : 'border-b border-gray-700'}`} onClick={() => handleAddUser(user)}>
                                            <div className="flex items-center">
                                                {user.photoURL
                                                    ? <img src={user.photoURL} className={`w-10 h-10 rounded-full mr-3 border-2 ${appMode === AppMode.LIGHT ? 'border-gray-200' : 'border-gray-600'}`} alt='' />
                                                    : <div className={`w-10 h-10 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center mr-3 border-2 ${appMode === AppMode.LIGHT ? 'border-gray-200' : 'border-gray-600'}`}>
                                                        <i className="bi bi-person-fill"></i>
                                                    </div>
                                                }
                                                <div>
                                                    <div className="font-medium">{user.displayName || 'User'}</div>
                                                    <div className={`text-sm ${appMode === AppMode.LIGHT ? 'text-gray-500' : 'text-gray-400'}`}>{user.email}</div>
                                                </div>
                                            </div>
                                            <button type="button" className="text-blue-600 hover:text-blue-800 hover:bg-blue-50 p-1.5 rounded-full transition-colors duration-150">
                                                <i className="bi bi-plus-circle-fill"></i>
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div className={`border rounded-lg shadow-sm ${appMode === AppMode.LIGHT ? 'border-gray-200' : 'border-gray-700'}`}>
                            {selectedUsers.map(user => (
                                <div key={user.id} className={`p-3 flex justify-between items-center ${appMode === AppMode.LIGHT ? 'border-b border-gray-200' : 'border-b border-gray-700'}`}>
                                    <div className="flex items-center">
                                        {user.photoURL
                                            ? <img src={user.photoURL} className={`w-10 h-10 rounded-full mr-3 border-2 ${appMode === AppMode.LIGHT ? 'border-gray-200' : 'border-gray-600'}`} alt='' />
                                            : <div className={`w-10 h-10 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center mr-3 border-2 ${appMode === AppMode.LIGHT ? 'border-gray-200' : 'border-gray-600'}`}>
                                                <i className="bi bi-person-fill"></i>
                                            </div>
                                        }
                                        <div>
                                            <div className="font-medium">{user.displayName || 'User'}</div>
                                            <div className={`text-sm ${appMode === AppMode.LIGHT ? 'text-gray-500' : 'text-gray-400'}`}>{user.email}</div>
                                        </div>
                                    </div>
                                    <div className="flex items-center">
                                        <select value={user.role} onChange={(e) => handleChangeRole(user.id, e.target.value)} className={`mr-3 p-2 border rounded-lg transition-colors duration-200 ${appMode === AppMode.LIGHT ? 'border-gray-300 bg-gray-50 focus:border-blue-400' : 'border-gray-700 bg-gray-800 focus:border-blue-600'}`} disabled={user.id === auth.currentUser.uid}>
                                            <option value="admin">Admin</option>
                                            <option value="manager">Manager</option>
                                            <option value="member">Member</option>
                                        </select>
                                        {user.id !== auth.currentUser.uid && (
                                            <button type="button" onClick={() => handleRemoveUser(user.id)} className="text-red-500 hover:text-red-700 hover:bg-red-50 p-1.5 rounded-full transition-colors duration-150">
                                                <i className="bi bi-trash-fill"></i>
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className={`flex justify-end space-x-3 pt-2 border-t ${appMode === AppMode.LIGHT ? 'border-gray-200' : 'border-gray-700'}`}>
                        <button type="button" onClick={onClose} className={`px-5 py-2.5 border rounded-lg font-medium transition-colors duration-200 ${appMode === AppMode.LIGHT ? 'border-gray-300 hover:bg-gray-50 text-gray-700' : 'border-gray-600 hover:bg-gray-800 text-gray-300'}`} disabled={loading}>Cancel</button>
                        <button type="submit" className={`px-5 py-2.5 bg-blue-600 text-white rounded-lg font-medium transition-colors duration-200 ${loading ? 'opacity-70' : 'hover:bg-blue-700 shadow-sm hover:shadow'}`} disabled={loading || !formData.name.trim() || selectedUsers.length === 0}>{loading ? 'Creating...' : 'Create Team'}</button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export const AddMemberModal = ({ appMode, onClose, teamId, onMemberAdded }) => {
    const modalRef = useRef(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [searchLoading, setSearchLoading] = useState(false);
    const [selectedRole, setSelectedRole] = useState('member');

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (modalRef.current && !modalRef.current.contains(event.target)) {
                onClose();
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };//eslint-disable-next-line
    }, []);

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
    };

    const handleSearch = async () => {
        if (!searchTerm.trim()) return;
        setSearchLoading(true);
        try {
            const usersRef = collection(firestore, 'users');//eslint-disable-next-line
            const simpleQuery = query(usersRef, limit(10));
            const emailQuery = query(usersRef, where('email', '>=', searchTerm), where('email', '<=', searchTerm + '\uf8ff'), limit(5));
            const nameQuery = query(usersRef, where('displayName', '>=', searchTerm), where('displayName', '<=', searchTerm + '\uf8ff'), limit(5));
            const [emailSnapshot, nameSnapshot] = await Promise.all([
                getDocs(emailQuery),
                getDocs(nameQuery)
            ]);
            const emailResults = emailSnapshot.docs.map(doc => {
                const userData = doc.data();
                return {
                    id: userData.userId,
                    ...userData
                };
            });

            const nameResults = nameSnapshot.docs.map(doc => {
                const userData = doc.data();
                return {
                    id: userData.userId,
                    ...userData
                };
            });

            const combinedResults = [...emailResults];
            nameResults.forEach(user => {
                if (!combinedResults.some(u => u.id === user.id)) {
                    combinedResults.push(user);
                }
            });

            const teamRef = ref(realtime, `teams/${teamId}`);
            const teamSnapshot = await get(teamRef);
            let filteredResults = combinedResults;
            if (teamSnapshot.exists()) {
                const teamData = teamSnapshot.val();
                const currentMembers = teamData.members || [];
                filteredResults = combinedResults.filter(user =>
                    !currentMembers.includes(user.id)
                );
            }
            setSearchResults(filteredResults);
        } catch (error) {
        } finally {
            setSearchLoading(false);
        }
    };

    const handleAddMember = async (user) => {
        try {
            const teamRef = ref(realtime, `teams/${teamId}`);
            const teamSnapshot = await get(teamRef);
            if (teamSnapshot.exists()) {
                const teamData = teamSnapshot.val();
                const currentMembers = teamData.members || [];
                const currentRoles = teamData.roles || {};
                const authUid = user.uid || user.id;
                if (!currentMembers.includes(authUid)) {
                    await update(teamRef, {
                        members: [...currentMembers, authUid],
                        roles: { ...currentRoles, [authUid]: selectedRole }
                    });
                    await addDoc(collection(firestore, 'activityLogs'), {
                        teamId: teamId,
                        userId: auth.currentUser.uid,
                        action: `added ${user.displayName || user.email} to team`,
                        timestamp: new Date()
                    });

                    await addDoc(collection(firestore, 'notifications'), {
                        userId: authUid,
                        type: 'team_invite',
                        teamId: teamId,
                        teamName: teamData.name,
                        invitedBy: auth.currentUser.uid,
                        read: false,
                        timestamp: new Date()
                    });
                    if (onMemberAdded) {
                        onMemberAdded({ ...user, role: selectedRole });
                    }
                    setSearchResults(prev => prev.filter(u => u.id !== user.id));
                    setSearchTerm('');
                }
            }
        } catch (error) {
        }
    };

    return (
        <div className={`fixed inset-0 ${appMode === AppMode.LIGHT ? 'bg-black' : 'bg-gray-700'} bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50`}>
            <div ref={modalRef} className={`w-full max-w-md p-6 rounded-lg ${appMode === AppMode.LIGHT ? 'bg-white' : dark_mode_primary} shadow-xl border ${appMode === AppMode.LIGHT ? 'border-gray-200' : 'border-gray-600'} transition-all duration-300`}>
                <div className={`flex justify-between items-center mb-5 border-b pb-4 ${appMode === AppMode.LIGHT ? 'border-gray-200' : 'border-gray-700'}`}>
                    <h2 className={`text-xl font-bold ${appMode === AppMode.LIGHT ? 'text-gray-800' : 'text-gray-100'}`}>Add Team Member</h2>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full p-2 transition-colors duration-200">
                        <i className="bi bi-x-lg"></i>
                    </button>
                </div>
                <div className="mb-6">
                    <div className="flex mb-4">
                        <input type="text" value={searchTerm} onChange={handleSearchChange} className={`flex-1 p-3 border rounded-l-lg focus:ring-2 focus:outline-none transition-colors duration-200 ${appMode === AppMode.LIGHT ? 'border-gray-300 focus:ring-blue-200 focus:border-blue-400' : 'border-gray-700 bg-gray-800 focus:ring-blue-800 focus:border-blue-600'}`} placeholder="Search users by email or name" />
                        <select value={selectedRole} onChange={(e) => setSelectedRole(e.target.value)} className={`p-3 border-t border-b focus:outline-none transition-colors duration-200 ${appMode === AppMode.LIGHT ? 'border-gray-300 bg-gray-50' : 'border-gray-700 bg-gray-800'}`}>
                            <option value="admin">Admin</option>
                            <option value="manager">Manager</option>
                            <option value="member">Member</option>
                        </select>
                        <button onClick={handleSearch} className={`px-4 py-2 bg-blue-600 text-white rounded-r-lg transition-colors duration-200 ${searchLoading ? 'opacity-70' : 'hover:bg-blue-700'}`} disabled={searchLoading || !searchTerm.trim()}>{searchLoading ? <i className="bi bi-hourglass"></i> : <i className="bi bi-search"></i>}</button>
                    </div>
                    {searchResults.length > 0 && (
                        <div className={`mb-4 border rounded-lg shadow-sm ${appMode === AppMode.LIGHT ? 'border-gray-200 bg-white' : 'border-gray-700 bg-gray-800'}`}>
                            <h3 className={`p-3 border-b font-medium ${appMode === AppMode.LIGHT ? 'border-gray-200 bg-gray-50' : 'border-gray-700 bg-gray-800'}`}>Search Results</h3>
                            {searchResults.map(user => (
                                <div key={user.id} className={`p-3 flex justify-between items-center ${appMode === AppMode.LIGHT ? 'hover:bg-gray-50 border-b border-gray-200' : 'hover:bg-gray-700 border-b border-gray-700'} transition-colors duration-150`}>
                                    <div className="flex items-center">
                                        <div className={`w-10 h-10 rounded-full overflow-hidden mr-3 border-2 ${appMode === AppMode.LIGHT ? 'border-gray-200' : 'border-gray-600'}`}>
                                            {user.photoURL
                                                ? <img src={user.photoURL} className="w-full h-full object-cover" alt='' />
                                                : <div className={`w-full h-full flex items-center justify-center ${appMode === AppMode.LIGHT ? 'bg-blue-100 text-blue-600' : 'bg-blue-900 text-blue-200'}`}>{(user.displayName || user.email || 'U').charAt(0).toUpperCase()}</div>
                                            }
                                        </div>
                                        <div>
                                            <div className="font-medium">{user.displayName || 'User'}</div>
                                            <div className={`text-sm ${appMode === AppMode.LIGHT ? 'text-gray-500' : 'text-gray-400'}`}>{user.email}</div>
                                        </div>
                                    </div>
                                    <button onClick={() => handleAddMember(user)} className={`px-3 py-1.5 rounded-lg font-medium transition-colors duration-200 ${appMode === AppMode.LIGHT ? 'bg-blue-100 text-blue-600 hover:bg-blue-200' : 'bg-blue-900 text-blue-200 hover:bg-blue-800'}`}>Add</button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
                <div className={`flex justify-end pt-2 border-t ${appMode === AppMode.LIGHT ? 'border-gray-200' : 'border-gray-700'}`}>
                    <button onClick={onClose} className={`px-5 py-2.5 border rounded-lg font-medium transition-colors duration-200 ${appMode === AppMode.LIGHT ? 'border-gray-300 hover:bg-gray-50 text-gray-700' : 'border-gray-600 hover:bg-gray-800 text-gray-300'}`}>Close</button>
                </div>
            </div>
        </div>
    );
};

export function SessionExpiredAlert({ appMode, expiredProvider, setShowSessionExpiredAlert, handleSessionExpired }) {
    <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50">
        <div className={`w-96 rounded-lg shadow-xl ${appMode === AppMode.LIGHT ? 'bg-white' : 'bg-gray-800'} border ${appMode === AppMode.LIGHT ? 'border-gray-200' : 'border-gray-700'} transition-all duration-300`}>
            <div className="p-4 border-b flex justify-between items-center">
                <h2 className={`font-semibold ${appMode === AppMode.LIGHT ? 'text-gray-800' : 'text-white'}`}>Session Expired</h2>
                <button onClick={() => setShowSessionExpiredAlert(false)} className="text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full p-2 transition-colors">
                    <i className="bi bi-x-lg"></i>
                </button>
            </div>
            <div className="p-6">
                <div className="text-center mb-4">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-yellow-100 text-yellow-500 mb-4">
                        <i className="bi bi-exclamation-triangle-fill text-2xl"></i>
                    </div>
                    <h3 className={`text-lg font-medium ${appMode === AppMode.LIGHT ? 'text-gray-800' : 'text-white'}`}>Authentication Session Expired</h3>
                    <p className={`mt-2 ${appMode === AppMode.LIGHT ? 'text-gray-600' : 'text-gray-300'}`}>
                        {expiredProvider === AuthProvider.MICROSOFT ?
                            "Your Microsoft account session has expired. You need to log out and log in again to continue using Microsoft services." :
                            "Your account session has expired. You need to log out and log in again to continue."}
                    </p>
                </div>
                <div className="flex justify-center space-x-3 mt-6">
                    <button onClick={() => setShowSessionExpiredAlert(false)} className={`px-4 py-2.5 rounded-lg font-medium transition-colors duration-200 ${appMode === AppMode.LIGHT ? 'bg-gray-100 hover:bg-gray-200 text-gray-700' : 'bg-gray-700 hover:bg-gray-600 text-white'}`}>Cancel</button>
                    <button onClick={handleSessionExpired} className={`px-4 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-medium transition-colors duration-200 shadow-sm hover:shadow`}>Log Out & Refresh</button>
                </div>
            </div>
        </div>
    </div>
};

export function MessageContextMenu({ appMode, position, onClose, onEdit, onDelete, onReply, onReplySuggestion }) {
    const menuRef = useRef(null);
    useEffect(() => {
        function adjustPosition() {
            const menu = menuRef.current;
            if (!menu) return;
            const { innerWidth, innerHeight } = window;
            const rect = menu.getBoundingClientRect();
            let x = position.x;
            let y = position.y;
            if (x + rect.width > innerWidth) x = innerWidth - rect.width - 10;
            if (y + rect.height > innerHeight) y = innerHeight - rect.height - 10;
            menu.style.left = `${x}px`;
            menu.style.top = `${y}px`;
        }
        adjustPosition();
    }, [position.x, position.y]);

    useEffect(() => {
        function handle(e) {
            if (menuRef.current && !menuRef.current.contains(e.target)) onClose();
        }
        function handleEsc(e) {
            if (e.key === "Escape") onClose();
        }
        document.addEventListener("mousedown", handle);
        document.addEventListener("keydown", handleEsc);
        return () => {
            document.removeEventListener("mousedown", handle);
            document.removeEventListener("keydown", handleEsc);
        };
    }, [onClose]);

    return (
        <div ref={menuRef} className={`fixed z-50 min-w-[180px] rounded-xl shadow-lg border transition-all
          ${appMode === AppMode.LIGHT ? "bg-white border-gray-200" : "bg-gray-800 border-gray-700"}`}
            style={{
                left: position.x, top: position.y, boxShadow:
                    "0 8px 32px 0 rgba(60,60,90,0.18), 0 1.5px 4px 0 rgba(60,60,90,0.08)",
                animation: "fadeIn 0.15s ease-out"
            }} role="menu" aria-label="Message options"
        >
            <div className={`py-2 ${appMode === AppMode.LIGHT ? "" : ""}`}>
                <button className={`flex items-center w-full px-5 py-3 gap-3 text-left rounded-none text-base font-medium transition-all duration-150
              ${appMode === AppMode.LIGHT
                        ? "text-gray-700 hover:bg-blue-50 hover:text-blue-700"
                        : "text-gray-200 hover:bg-blue-900 hover:text-blue-100"}`} onClick={onEdit} role="menuitem"
                >
                    <i className="bi bi-pencil-square text-lg" /> Edit Message
                </button>
                <div className={`mx-3 ${appMode === AppMode.LIGHT ? "border-t border-gray-100" : "border-t border-gray-700"}`}></div>
                <button className={`flex items-center w-full px-5 py-3 gap-3 text-left rounded-none text-base font-medium transition-all duration-150
              ${appMode === AppMode.LIGHT
                        ? "text-gray-700 hover:bg-blue-50 hover:text-blue-700"
                        : "text-gray-200 hover:bg-blue-900 hover:text-blue-100"}`} onClick={onReply} role="menuitem"
                >
                    <i className="bi bi-reply text-lg" /> Reply
                </button>
                <div className={`mx-3 ${appMode === AppMode.LIGHT ? "border-t border-gray-100" : "border-t border-gray-700"}`}></div>
                <button className={`flex items-center w-full px-5 py-3 gap-3 text-left rounded-none text-base font-medium transition-all duration-150
              ${appMode === AppMode.LIGHT
                        ? "text-gray-700 hover:bg-blue-50 hover:text-blue-700"
                        : "text-gray-200 hover:bg-blue-900 hover:text-blue-100"}`} onClick={onReplySuggestion} role="menuitem"
                >
                    <i className="bi bi-lightbulb text-lg" /> Get Reply Suggestion
                </button>
                <div className={`mx-3 ${appMode === AppMode.LIGHT ? "border-t border-gray-100" : "border-t border-gray-700"}`}></div>
                <button className={`flex items-center w-full px-5 py-3 gap-3 text-left rounded-none text-base font-medium transition-all duration-150
              ${appMode === AppMode.LIGHT
                        ? "text-red-600 hover:bg-red-50 hover:text-red-700"
                        : "text-red-400 hover:bg-red-900/50 hover:text-red-300"}`} onClick={onDelete} role="menuitem"
                >
                    <i className="bi bi-trash text-lg" /> Delete Message
                </button>
            </div>
            <style jsx>{`
          @keyframes fadeIn {
            from { opacity: 0; transform: scale(0.95); }
            to { opacity: 1; transform: scale(1); }
          }
        `}</style>
        </div>
    );
}

export function ConfirmDialog({ open, title, message, onConfirm, onCancel, appMode, setShowDeleteConfirm }) {
    const popupRef = useRef(null);
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (popupRef.current && !popupRef.current.contains(event.target)) {
                setShowDeleteConfirm(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };//eslint-disable-next-line
    }, []);
    if (!open) return null;
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm animate-fadeIn">
            <div ref={popupRef} className={`rounded-xl shadow-2xl p-6 w-[340px] max-w-[90vw] animate-scaleIn ${appMode === AppMode.LIGHT
                ? "bg-white text-gray-900 border border-gray-200"
                : "bg-gray-900 text-white border border-gray-700"
                }`} role="dialog" aria-modal="true" aria-labelledby="confirm-dialog-title">
                <div className="flex items-center mb-4">
                    <i className="bi bi-exclamation-triangle-fill text-2xl text-red-500 mr-3" />
                    <h2 className="text-lg font-semibold" id="confirm-dialog-title">{title}</h2>
                </div>
                <div className="mb-6 text-sm">{message}</div>
                <div className="flex justify-end gap-3">
                    <button className={`px-4 py-2 rounded-lg font-medium ${appMode === AppMode.LIGHT
                        ? "bg-gray-100 text-gray-700 hover:bg-gray-200"
                        : "bg-gray-800 text-gray-200 hover:bg-gray-700"
                        }`} onClick={onCancel}
                    > Cancel
                    </button>
                    <button className={`px-4 py-2 rounded-lg font-medium bg-red-600 text-white hover:bg-red-700 shadow`} onClick={onConfirm} autoFocus>Delete</button>
                </div>
            </div>
            <style jsx>{`
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes scaleIn {
            from { transform: scale(0.95); opacity: 0; }
            to { transform: scale(1); opacity: 1; }
          }
          .animate-fadeIn {
            animation: fadeIn 0.15s ease-out;
          }
          .animate-scaleIn {
            animation: scaleIn 0.15s ease-out;
          }
        `}</style>
        </div>
    );
}